import {
  type CSSProperties,
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { api, ApiError, deviceApi, setCsrfToken } from "./api";
import { kioskCapacityChips } from "./kiosk-capacity";
import type {
  ClassPeriod,
  CurrentUser,
  Notification,
  Pass,
  PassCategory,
  RoleGrant,
  RoleName,
  Student,
} from "./types";

const GLOBAL_SCOPE_ID = "00000000-0000-0000-0000-000000000000";
const DEFAULT_TIME_ZONE = "America/Los_Angeles";
const DEFAULT_TIMER_NORMAL_COLOR = "#16834F";
const DEFAULT_TIMER_WARNING_COLOR = "#A45B00";
const DEFAULT_TIMER_ALERT_COLOR = "#C73434";
const TIME_ZONE_OPTIONS = [
  ["America/Los_Angeles", "Pacific — Los Angeles"],
  ["America/Denver", "Mountain — Denver"],
  ["America/Phoenix", "Arizona — Phoenix"],
  ["America/Chicago", "Central — Chicago"],
  ["America/New_York", "Eastern — New York"],
  ["America/Anchorage", "Alaska — Anchorage"],
  ["Pacific/Honolulu", "Hawaii — Honolulu"],
] as const;
const reasonLabels: Record<string, string> = {
  RESTROOM: "Restroom",
  WATER: "Water",
  RESTROOM_WATER: "Restroom / Water",
  FRONT_OFFICE: "Front office",
  COUNSELOR: "Counselor",
  NURSE: "Nurse",
  LIBRARY: "Library",
  OTHER_CLASSROOM: "Other classroom",
  TARDY: "Tardy",
};
const PASS_SYMBOL_CHOICES = [
  ["\u{1F6BD}", "Toilet"], ["\u{1F4A7}", "Water drop"], ["\u{23F1}\u{FE0F}", "Stopwatch"], ["\u{1F3E2}", "Office building"],
  ["\u{1F4AC}", "Conversation"], ["✚", "Medical cross"], ["\u{1F4DA}", "Books"], ["\u{1F3EB}", "School"],
  ["\u{1F6AA}", "Door"], ["\u{1F6B0}", "Drinking water"], ["\u{1F4CD}", "Location pin"], ["\u{1F3E5}", "Health office"],
  ["\u{1FA79}", "Bandage"], ["\u{1FA7A}", "Stethoscope"], ["\u{1F91D}", "Support"], ["\u{1F465}", "People"],
  ["\u{1F4DD}", "Notes"], ["\u{270F}\u{FE0F}", "Pencil"], ["\u{1F4CC}", "Pushpin"], ["\u{1F514}", "Bell"],
  ["\u{23F0}", "Alarm clock"], ["\u{231B}", "Hourglass"], ["\u{1F4C5}", "Calendar"], ["\u{1F4CB}", "Clipboard"],
  ["\u{1F4BB}", "Computer"], ["\u{1F511}", "Key"], ["\u{1F6E1}\u{FE0F}", "Shield"], ["\u{26A0}\u{FE0F}", "Caution"],
  ["\u{2B50}", "Star"], ["\u{2705}", "Check mark"], ["\u{1F393}", "Graduation cap"], ["\u{1F4A1}", "Idea"],
] as const;

function categoryLabel(
  reasonCode: string,
  categories: PassCategory[] = [],
  snapshot?: string | null,
) {
  // Older pass rows can carry an empty-string snapshot. Treat it as absent so
  // operational screens never render a blank reason column.
  const savedLabel = snapshot?.trim();
  return savedLabel || categories.find((category) => category.code === reasonCode)?.label || reasonLabels[reasonCode] || reasonCode;
}

/**
 * Operational screens use the name a student asks staff to use. The database
 * still retains first_name/last_name as the official school-record identity
 * for reports, corrections, and student-number matching.
 */
function studentDisplayName(student: {
  first_name: string;
  last_name?: string | null;
  preferred_first_name?: string | null;
  preferred_last_name?: string | null;
}) {
  const firstName = student.preferred_first_name?.trim() || student.first_name;
  const lastName = student.preferred_last_name?.trim() || student.last_name || "";
  return `${firstName} ${lastName}`.trim();
}

function studentDisplayInitials(student: {
  first_name: string;
  last_name?: string | null;
  preferred_first_name?: string | null;
  preferred_last_name?: string | null;
}) {
  const firstName = student.preferred_first_name?.trim() || student.first_name;
  const lastName = student.preferred_last_name?.trim() || student.last_name || "";
  return `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`;
}

function cx(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

function useClock(interval = 1_000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), interval);
    return () => window.clearInterval(timer);
  }, [interval]);
  return now;
}

function useFreshClientBundle() {
  useEffect(() => {
    let disposed = false;
    const currentScript = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src;
    if (!currentScript) return undefined;
    const checkForDeployment = async () => {
      try {
        const response = await fetch("/", { cache: "no-store", credentials: "same-origin" });
        const documentText = await response.text();
        const match = documentText.match(/<script\b[^>]*type=["']module["'][^>]*src=["']([^"']+)/i);
        if (!match?.[1]) return;
        const latestScript = new URL(match[1], window.location.origin).href;
        if (!disposed && latestScript !== currentScript) window.location.reload();
      } catch {
        // A temporary network failure should never interrupt a kiosk or display.
      }
    };
    const timer = window.setInterval(() => void checkForDeployment(), 60_000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, []);
}

function timestampMs(value: string | null) {
  if (!value) return null;
  const text = value.trim();
  // Be defensive with older API responses that still contain a bare MariaDB
  // DATETIME. Treat it as UTC, just like the current API serialization does.
  const withTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(text)
    ? text
    : `${text.replace(" ", "T")}Z`;
  const parsed = Date.parse(withTimezone);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatElapsedSeconds(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function elapsedSeconds(
  start: string | null,
  now: number,
  serverElapsedSeconds?: number | null,
  receivedAt?: number,
) {
  // State endpoints calculate elapsed time in MariaDB. Anchor that value to
  // the moment the browser received it, so the clock does not depend on the
  // browser parsing either database timestamps or a server clock string.
  const elapsedFromServer =
    serverElapsedSeconds === null || serverElapsedSeconds === undefined
      ? Number.NaN
      : Number(serverElapsedSeconds);
  if (Number.isFinite(elapsedFromServer) && receivedAt && receivedAt > 0) {
    return Math.max(0, elapsedFromServer + Math.floor((now - receivedAt) / 1_000));
  }
  const startMs = timestampMs(start);
  if (startMs === null) return 0;
  return Math.max(0, Math.floor((now - startMs) / 1_000));
}

function elapsed(
  start: string | null,
  now: number,
  serverElapsedSeconds?: number | null,
  receivedAt?: number,
) {
  return formatElapsedSeconds(elapsedSeconds(start, now, serverElapsedSeconds, receivedAt));
}

type RestroomTimerTone = "warning" | "limit" | null;

interface TimerColorRules {
  timer_colors_enabled?: number | boolean | null;
  timer_normal_color?: string | null;
  timer_warning_color?: string | null;
  timer_alert_color?: string | null;
}

function safeTimerColor(value: string | null | undefined, fallback: string): string {
  return value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
}

function timerPresentation(
  tone: RestroomTimerTone,
  rules: TimerColorRules,
): { className?: string; style: CSSProperties } {
  const enabled = rules.timer_colors_enabled !== false && rules.timer_colors_enabled !== 0;
  if (!enabled) {
    // Avoid flashing in no-color mode too; motion would still reveal the
    // warning state even though the timer is black.
    return { className: "timer-color-disabled", style: { color: "#111111", animation: "none" } };
  }
  const color = tone === "limit"
    ? safeTimerColor(rules.timer_alert_color, DEFAULT_TIMER_ALERT_COLOR)
    : tone === "warning"
      ? safeTimerColor(rules.timer_warning_color, DEFAULT_TIMER_WARNING_COLOR)
      : safeTimerColor(rules.timer_normal_color, DEFAULT_TIMER_NORMAL_COLOR);
  return { style: { color } };
}

function passTimerRules(pass: Pass, fallback: TimerColorRules): TimerColorRules {
  return {
    timer_colors_enabled: pass.restroom_timer_colors_enabled ?? fallback.timer_colors_enabled,
    timer_normal_color: pass.restroom_timer_normal_color ?? fallback.timer_normal_color,
    timer_warning_color: pass.restroom_timer_warning_color ?? fallback.timer_warning_color,
    timer_alert_color: pass.restroom_timer_alert_color ?? fallback.timer_alert_color,
  };
}

function restroomTimerTone(
  pass: {
    uses_restroom_capacity?: number;
    approved_at: string | null;
    elapsed_seconds?: number | null;
  },
  now: number,
  warningMinutes: number,
  fallbackLimitMinutes: number,
  receivedAt?: number,
): RestroomTimerTone {
  if (!pass.uses_restroom_capacity || !pass.approved_at) return null;
  // A pass's stored limit is the hard 45-minute no-return deadline. The
  // teacher's period setting is intentionally used here instead: it controls
  // the orange warning and red restroom-time alert while the student remains
  // visible until the independent 45-minute timeout.
  const limitMinutes = Math.max(1, Number(fallbackLimitMinutes));
  const warningAtMinutes = Math.max(0, Math.min(limitMinutes, Number(warningMinutes)));
  const secondsOut = elapsedSeconds(pass.approved_at, now, pass.elapsed_seconds, receivedAt);
  if (secondsOut >= limitMinutes * 60) return "limit";
  return secondsOut >= warningAtMinutes * 60 ? "warning" : null;
}

function formatDateAtTimeZone(value: string | null, timeZone = DEFAULT_TIME_ZONE) {
  const milliseconds = timestampMs(value);
  return milliseconds === null
    ? "—"
    : new Date(milliseconds).toLocaleString("en-US", { timeZone });
}

function formatDateOnlyAtTimeZone(value: string | null, timeZone = DEFAULT_TIME_ZONE) {
  const milliseconds = timestampMs(value);
  return milliseconds === null
    ? "—"
    : new Date(milliseconds).toLocaleDateString("en-US", { timeZone });
}

function formatTimestampTimeAtTimeZone(value: string | null, timeZone = DEFAULT_TIME_ZONE) {
  const milliseconds = timestampMs(value);
  return milliseconds === null ? "—" : formatTimeAtTimeZone(milliseconds, timeZone);
}

function formatTimeAtTimeZone(milliseconds: number, timeZone = DEFAULT_TIME_ZONE) {
  return new Date(milliseconds).toLocaleTimeString("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  });
}

function dateInputAtTimeZone(timeZone = DEFAULT_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function formatDateInputTitle(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[2]}-${match[3]}-${match[1]}` : value;
}

function dateTimeInputAtTimeZone(timeZone = DEFAULT_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
}

function dateTimeValueAtTimeZone(value: string, timeZone = DEFAULT_TIME_ZONE) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return dateTimeInputAtTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
}

interface TermsDocument {
  id: string;
  version: string;
  title: string;
  privacySummary: string;
  termsBody: string;
  isCurrent: boolean;
  publishedAt: string;
  publisherName: string | null;
}

function TermsContent({ document }: { document: TermsDocument }) {
  return <article className="terms-content">
    <p className="eyebrow ink">Version {document.version}</p>
    <h1>{document.title}</h1>
    <section className="terms-privacy-summary"><h2>Student privacy at a glance</h2><p>{document.privacySummary}</p></section>
    {document.termsBody.split(/\n\s*\n/).filter(Boolean).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
    <small>Published {new Date(document.publishedAt).toLocaleDateString()}</small>
  </article>;
}

function TermsAndPrivacyPage() {
  const [document, setDocument] = useState<TermsDocument | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { void api<{ document: TermsDocument }>("/api/terms/current").then((result) => setDocument(result.document)).catch((caught) => setError(caught instanceof Error ? caught.message : "Terms & Privacy could not be loaded")); }, []);
  return <main className="public-policy-page"><Link className="brand-lockup" to="/login"><span className="brand-mark">V</span><span>Virtual Hall Pass</span></Link>{error && <div className="alert error">{error}</div>}{document ? <TermsContent document={document} /> : !error && <LoadingScreen />}</main>;
}

function RequiredTermsAcceptance({ user, onAccepted }: { user: CurrentUser; onAccepted: () => Promise<void> }) {
  const [document, setDocument] = useState<TermsDocument | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { void api<{ document: TermsDocument }>("/api/terms/current").then((result) => setDocument(result.document)).catch((caught) => setError(caught instanceof Error ? caught.message : "Terms & Privacy could not be loaded")); }, []);
  async function accept() { setSaving(true); setError(""); try { await api("/api/auth/terms/accept", { method: "POST" }); await onAccepted(); } catch (caught) { setError(caught instanceof Error ? caught.message : "Terms could not be accepted"); } finally { setSaving(false); } }
  return <main className="required-terms-page"><div className="required-terms-card"><p className="eyebrow ink">One more step</p><h1>Review Terms & Privacy</h1><p className="form-note">Hi {user.displayName}. A current agreement is required before you can continue using Virtual Hall Pass.</p>{error && <div className="alert error">{error}</div>}{document && <><TermsContent document={document} /><label className="check-row terms-agreement"><input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />I have read and agree to version {document.version} of the Terms & Privacy.</label><div className="dialog-actions"><button className="button primary" disabled={!agreed || saving} onClick={() => void accept()}>{saving ? "Saving…" : "Agree and continue"}</button></div></>}</div></main>;
}

function App() {
  useFreshClientBundle();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api<{ user: CurrentUser }>("/api/auth/me");
      setCsrfToken(response.user.csrfToken);
      setUser(response.user);
      setNeedsSetup(false);
    } catch {
      setCsrfToken(undefined);
      setUser(null);
      try {
        const status = await api<{ needsSetup: boolean }>("/api/setup/status");
        setNeedsSetup(status.needsSetup);
      } catch {
        setNeedsSetup(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
    const refreshAfterHistoryNavigation = () => { void refreshUser(); };
    window.addEventListener("pageshow", refreshAfterHistoryNavigation);
    return () => window.removeEventListener("pageshow", refreshAfterHistoryNavigation);
  }, [refreshUser]);

  if (loading) return <LoadingScreen />;
  if (user?.passwordResetRequired) {
    return <RequiredPasswordChange user={user} onChanged={refreshUser} />;
  }
  if (user?.termsAcceptanceRequired) {
    return <RequiredTermsAcceptance user={user} onAccepted={refreshUser} />;
  }
  const deviceRoute = window.location.pathname === "/kiosk" || window.location.pathname === "/display";
  if (user?.reauthenticationRequired && !deviceRoute) {
    return <RequiredReauthentication user={user} onConfirmed={refreshUser} />;
  }
  return (
    <Routes>
      <Route
        path="/setup"
        element={
          needsSetup ? (
            <InitialSetup onSuccess={refreshUser} />
          ) : (
            <Navigate to={user ? "/" : "/login"} replace />
          )
        }
      />
      <Route
        path="/login"
        element={
          user ? (
            <Navigate to="/" replace />
          ) : needsSetup ? (
            <Navigate to="/setup" replace />
          ) : (
            <Login onSuccess={refreshUser} />
          )
        }
      />
      <Route path="/terms" element={<TermsAndPrivacyPage />} />
      <Route path="/kiosk" element={<DeviceScreen type="KIOSK" />} />
      <Route path="/display" element={<DeviceScreen type="DISPLAY" />} />
      <Route
        path="/admin"
        element={
          user ? (
            <AppShell user={user} onUserChange={refreshUser}>
              <AdminPage user={user} onUserChange={refreshUser} />
            </AppShell>
          ) : (
            <Navigate to={needsSetup ? "/setup" : "/login"} replace />
          )
        }
      />
      <Route
        path="/admin/users"
        element={
          user ? (
            <Navigate to="/admin" replace />
          ) : (
            <Navigate to={needsSetup ? "/setup" : "/login"} replace />
          )
        }
      />
      <Route
        path="/account"
        element={
          user ? (
            <AppShell user={user} onUserChange={refreshUser}>
              <UserProfilePage user={user} onUserChange={refreshUser} />
            </AppShell>
          ) : (
            <Navigate to={needsSetup ? "/setup" : "/login"} replace />
          )
        }
      />
      <Route
        path="/feedback"
        element={
          user ? (
            <AppShell user={user} onUserChange={refreshUser}>
              <FeedbackPage />
            </AppShell>
          ) : (
            <Navigate to={needsSetup ? "/setup" : "/login"} replace />
          )
        }
      />
      <Route
        path="/"
        element={
          user ? (
            <AppShell user={user} onUserChange={refreshUser}>
              <TeacherDashboard user={user} />
            </AppShell>
          ) : (
            <Navigate to={needsSetup ? "/setup" : "/login"} replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RequiredReauthentication({ user, onConfirmed }: { user: CurrentUser; onConfirmed: () => Promise<void> }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent) { event.preventDefault(); setSaving(true); setError(""); try { await api("/api/auth/reauthenticate", { method: "POST", body: JSON.stringify({ password }) }); await onConfirmed(); } catch (caught) { setError(caught instanceof Error ? caught.message : "Password could not be verified"); } finally { setSaving(false); } }
  return <main className="required-terms-page"><form className="required-terms-card reauthentication-card" onSubmit={submit}><p className="eyebrow ink">Account protected</p><h1>Enter your password to continue</h1><p className="form-note">Kiosk or Live Display was opened from this browser. Enter the password for {user.email} before returning to staff information.</p>{error && <div className="alert error">{error}</div>}<label>Password<input type="password" autoComplete="current-password" autoFocus value={password} onChange={(event) => setPassword(event.target.value)} required /></label><div className="dialog-actions"><button className="button primary" disabled={saving}>{saving ? "Verifying…" : "Continue to account"}</button></div></form></main>;
}

function RequiredPasswordChange({
  user,
  onChanged,
}: {
  user: CurrentUser;
  onChanged: () => Promise<void>;
}) {
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/password", {
        method: "PATCH",
        body: JSON.stringify({
          currentPassword: temporaryPassword,
          newPassword,
        }),
      });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Password could not be changed");
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    await api("/api/auth/logout", { method: "POST" });
    setCsrfToken(undefined);
    window.location.assign("/login");
  }

  return (
    <main className="auth-layout setup-layout">
      <section className="auth-story">
        <div className="brand-lockup light"><span className="brand-mark">V</span><span>Virtual Hall Pass</span></div>
        <div><p className="eyebrow">Account recovery</p><h1>Choose your private password.</h1><p>The temporary password only gets you to this screen. It cannot be used to access classroom or administrative data until you replace it.</p></div>
        <div className="auth-proof"><span>Required change</span><span>Sessions revoked</span><span>Temporary access</span></div>
      </section>
      <section className="auth-panel">
        <form className="auth-card" onSubmit={submit}>
          <div><p className="eyebrow ink">Signed in as {user.email}</p><h2>Set a new password</h2><p className="muted">Enter the temporary password your Global Admin sent, then choose a password only you know.</p></div>
          {error && <div className="alert error">{error}</div>}
          <label>Temporary password<input autoFocus type="password" autoComplete="current-password" value={temporaryPassword} onChange={(event) => setTemporaryPassword(event.target.value)} required /></label>
          <label>New password<input type="password" minLength={12} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required /><small>At least 12 characters</small></label>
          <label>Confirm new password<input type="password" minLength={12} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required /></label>
          <button className="button primary wide" disabled={busy}>{busy ? "Changing password…" : "Change password and continue"}</button>
          <button type="button" className="text-button" onClick={() => void signOut()}>Sign out</button>
        </form>
      </section>
    </main>
  );
}

function InitialSetup({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    setError("");
    try {
      const response = await api<{ csrfToken: string }>("/api/setup", {
        method: "POST",
        body: JSON.stringify({
          displayName,
          email,
          password,
        }),
      });
      setCsrfToken(response.csrfToken);
      await onSuccess();
      navigate("/");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Setup could not be completed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-layout setup-layout">
      <section className="auth-story">
        <div className="brand-lockup light"><span className="brand-mark">V</span><span>Virtual Hall Pass</span></div>
        <div><p className="eyebrow">First-run setup</p><h1>Create the system owner.</h1><p>This account starts with Global Admin access. Districts, schools, teachers, and additional administrators are added after sign in.</p></div>
        <div className="auth-proof"><span>One-time setup</span><span>Local account</span><span>No preset password</span></div>
      </section>
      <section className="auth-panel">
        <form className="auth-card" onSubmit={submit}>
          <div><p className="eyebrow ink">Initial installation</p><h2>Create Global Admin</h2><p className="muted">This page permanently closes as soon as the first account is created.</p></div>
          {error && <div className="alert error">{error}</div>}
          <label>Full name<input autoFocus value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></label>
          <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label>Password<input type="password" minLength={12} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required /><small>At least 12 characters</small></label>
          <label>Confirm password<input type="password" minLength={12} autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} required /></label>
          <button className="button primary wide" disabled={busy}>{busy ? "Creating account…" : "Create Global Admin"}</button>
        </form>
      </section>
    </main>
  );
}

function LoadingScreen() {
  return (
    <main className="loading-screen">
      <div className="brand-mark">V</div>
      <p>Opening Virtual Hall Pass…</p>
    </main>
  );
}

function Login({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [showAccessRequest, setShowAccessRequest] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await api<{ csrfToken: string }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      setCsrfToken(response.csrfToken);
      await onSuccess();
      navigate("/");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign in failed");
    } finally {
      setBusy(false);
    }
  }

  async function requestPasswordReset(event: FormEvent) {
    event.preventDefault();
    setRecoveryBusy(true);
    setRecoveryError("");
    setRecoveryMessage("");
    try {
      const response = await api<{ message: string }>(
        "/api/auth/password-reset-requests",
        {
          method: "POST",
          body: JSON.stringify({ email: recoveryEmail }),
        },
      );
      setRecoveryMessage(response.message);
    } catch (caught) {
      setRecoveryError(caught instanceof Error ? caught.message : "Reset request could not be submitted");
    } finally {
      setRecoveryBusy(false);
    }
  }

  return (
    <main className="auth-layout">
      <section className="auth-story">
        <div className="brand-lockup light">
          <span className="brand-mark">V</span>
          <span>Virtual Hall Pass</span>
        </div>
        <div>
          <p className="eyebrow">Clear halls. Clear accountability.</p>
          <h1>Every student out, accounted for.</h1>
          <p>
            A calm, secure hall pass system built around the way teachers
            actually run a classroom.
          </p>
        </div>
        <div className="auth-proof">
          <span>Multi-school</span>
          <span>Live capacity</span>
          <span>Auditable</span>
        </div>
      </section>
      <section className="auth-panel">
        <form className="auth-card" onSubmit={submit}>
          <div>
            <p className="eyebrow ink">Staff sign in</p>
            <h2>Welcome back</h2>
            <p className="muted">Use the account provided by your administrator.</p>
          </div>
          {error && <div className="alert error">{error}</div>}
          <label>
            Email
            <input
              autoFocus
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          <button className="button primary wide" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="device-links">
            <button type="button" className="text-button" onClick={() => { setRecoveryEmail(email); setShowRecovery(true); }}>Forgot password?</button>
            <button type="button" className="text-button" onClick={() => setShowAccessRequest(true)}>Request access</button>
            <Link to="/terms">Terms &amp; Privacy</Link>
            <Link to="/kiosk">Pair a kiosk</Link>
            <Link to="/display">Pair a display</Link>
          </div>
        </form>
      </section>
      {showRecovery && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={() => setShowRecovery(false)}>
          <form className="dialog" onSubmit={requestPasswordReset} onMouseDown={(event) => event.stopPropagation()}>
            <div className="panel-heading">
              <div><p className="eyebrow ink">Account recovery</p><h2>Request a password reset</h2></div>
              <button type="button" className="icon-button" onClick={() => setShowRecovery(false)} aria-label="Close">×</button>
            </div>
            <p className="form-note">Your Global Admin will receive the request in Virtual Hall Pass. They can generate a temporary password and send it to your registered email using their normal email account.</p>
            {recoveryError && <div className="alert error">{recoveryError}</div>}
            {recoveryMessage && <div className="alert success">{recoveryMessage}</div>}
            {!recoveryMessage && <label>Email<input autoFocus type="email" autoComplete="email" value={recoveryEmail} onChange={(event) => setRecoveryEmail(event.target.value)} required /></label>}
            <div className="dialog-actions">
              <button type="button" className="button quiet" onClick={() => setShowRecovery(false)}>{recoveryMessage ? "Close" : "Cancel"}</button>
              {!recoveryMessage && <button className="button primary" disabled={recoveryBusy}>{recoveryBusy ? "Submitting…" : "Submit request"}</button>}
            </div>
          </form>
        </div>
      )}
      {showAccessRequest && <AccessRequestDialog onClose={() => setShowAccessRequest(false)} />}
    </main>
  );
}

function AccessRequestDialog({ onClose }: { onClose: () => void }) {
  const [options, setOptions] = useState<{ districts: Array<{ id: string; name: string }>; schools: Array<{ id: string; district_id: string; name: string }> }>({ districts: [], schools: [] });
  const [districtId, setDistrictId] = useState(""); const [schoolId, setSchoolId] = useState("");
  const [newDistrict, setNewDistrict] = useState(false); const [newSchool, setNewSchool] = useState(false);
  const [displayName, setDisplayName] = useState(""); const [title, setTitle] = useState(""); const [email, setEmail] = useState(""); const [proposedDistrict, setProposedDistrict] = useState(""); const [proposedSchool, setProposedSchool] = useState(""); const [introduction, setIntroduction] = useState(""); const [message, setMessage] = useState(""); const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { void api<typeof options>("/api/access-requests/options").then(setOptions).catch(() => setError("Request options could not be loaded.")); }, []);
  const schools = options.schools.filter((school) => school.district_id === districtId);
  const needsFollowUp = newDistrict || newSchool;
  async function submit(event: FormEvent) { event.preventDefault(); setBusy(true); setError(""); try { const result = await api<{ message: string }>("/api/access-requests", { method: "POST", body: JSON.stringify({ displayName, title, email, districtId: newDistrict ? null : districtId || null, schoolId: newDistrict || newSchool ? null : schoolId || null, proposedDistrictName: newDistrict ? proposedDistrict : undefined, proposedSchoolName: newSchool ? proposedSchool : undefined, introduction }) }); setMessage(result.message); } catch (caught) { setError(caught instanceof Error ? caught.message : "Access request could not be submitted"); } finally { setBusy(false); } }
  return <div className="dialog-backdrop" onMouseDown={onClose}><form className="dialog" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Virtual Hall Pass</p><h2>Request access</h2></div><button type="button" className="icon-button" onClick={onClose}>×</button></div>{error && <div className="alert error">{error}</div>}{message ? <div className="alert success">{message}</div> : <><label>Full name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></label><label>Title<input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Optional" /></label><label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label><label>District<select value={newDistrict ? "__NEW__" : districtId} onChange={(event) => { setNewDistrict(event.target.value === "__NEW__"); setDistrictId(event.target.value === "__NEW__" ? "" : event.target.value); setSchoolId(""); }} required><option value="" disabled>Select district</option>{options.districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}<option value="__NEW__">My district is not listed</option></select></label>{newDistrict ? <label>District name<input value={proposedDistrict} onChange={(event) => setProposedDistrict(event.target.value)} required /></label> : <label>School<select value={newSchool ? "__NEW__" : schoolId} onChange={(event) => { setNewSchool(event.target.value === "__NEW__"); setSchoolId(event.target.value === "__NEW__" ? "" : event.target.value); }} required disabled={!districtId}><option value="" disabled>Select school</option>{schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}<option value="__NEW__">My school is not listed</option></select></label>}{(newDistrict || newSchool) && <>{newDistrict && <label>School name<input value={proposedSchool} onChange={(event) => setProposedSchool(event.target.value)} required /></label>}{newSchool && <label>School name<input value={proposedSchool} onChange={(event) => setProposedSchool(event.target.value)} required /></label>}<p className="form-note">Virtual Hall Pass was created by a teacher and is still growing. To protect student privacy and support each school well, requests from new schools or districts may require follow-up or may be declined. Tell us about your school and intended use; additional setup details may be needed for academic terms and bell schedules.</p></>}<label>Introduction <span className="muted">(optional)</span><textarea value={introduction} onChange={(event) => setIntroduction(event.target.value)} maxLength={2000} placeholder="Who you are and what you hope to use the app for" /></label><div className="dialog-actions"><button type="button" className="button quiet" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Submitting…" : "Submit request"}</button></div></>}</form></div>;
}

function AppShell({
  user,
  onUserChange,
  children,
}: {
  user: CurrentUser;
  onUserChange: () => Promise<void>;
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user.roles.some((role) => role.role.includes("ADMIN"));
  const [launching, setLaunching] = useState<"KIOSK" | "DISPLAY" | "">("");
  const [launchError, setLaunchError] = useState("");

  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    setCsrfToken(undefined);
    navigate("/login");
    window.location.reload();
  }

  async function launchDevice(deviceType: "KIOSK" | "DISPLAY") {
    setLaunching(deviceType); setLaunchError("");
    try {
      const result = await api<{ token: string; deviceType: "KIOSK" | "DISPLAY" }>("/api/devices/launch", { method: "POST", body: JSON.stringify({ deviceType }) });
      await onUserChange();
      window.location.assign(`${result.deviceType === "KIOSK" ? "/kiosk" : "/display"}#launch=${encodeURIComponent(result.token)}`);
    } catch (caught) {
      setLaunchError(caught instanceof Error ? caught.message : "Device could not be launched");
      setLaunching("");
    }
  }

  return (
    <div className="app-frame">
      <aside className="sidebar">
        <Link className="brand-lockup" to="/">
          <span className="brand-mark">V</span>
          <span className="brand-name">Virtual <span>Hall Pass</span></span>
        </Link>
        <nav>
          <Link className={location.pathname === "/" ? "active" : ""} to="/">
            <span>{"\u{1F3EB}"}</span> Classroom
          </Link>
          <Link className={location.pathname === "/account" ? "active" : ""} to="/account">
            <span>{"\u{1F464}"}</span> My profile
          </Link>
          <Link className={location.pathname === "/feedback" ? "active" : ""} to="/feedback">
            <span>{"\u{1F4AC}"}</span> Feedback
          </Link>
          {isAdmin && (
            <>
            <Link
              className={location.pathname === "/admin" ? "active" : ""}
              to="/admin"
            >
              <span>{"\u{1F6E0}\u{FE0F}"}</span> Administration
            </Link>
            </>
          )}
          <button type="button" onClick={() => void launchDevice("KIOSK")} disabled={Boolean(launching)}>
            <span>{"\u{1F4F1}"}</span> {launching === "KIOSK" ? "Opening kiosk…" : "Kiosk"}
          </button>
          <button type="button" onClick={() => void launchDevice("DISPLAY")} disabled={Boolean(launching)}>
            <span>{"\u{1F5A5}\u{FE0F}"}</span> {launching === "DISPLAY" ? "Opening display…" : "Live display"}
          </button>
        </nav>
        <div className="sidebar-account">
          <div className="avatar">{user.displayName.slice(0, 1).toUpperCase()}</div>
          <div>
            <strong>{user.displayName}</strong>
            <span>{user.email}</span>
          </div>
          <button className="icon-button" onClick={logout} title="Sign out">
            ↗
          </button>
        </div>
      </aside>
      <main className="main-content">{children}</main>
      {launchError && <div className="dialog-backdrop" role="presentation" onMouseDown={() => setLaunchError("")}><section className="dialog" onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Classroom device</p><h2>Unable to launch</h2></div><button type="button" className="icon-button" onClick={() => setLaunchError("")}>×</button></div><p>{launchError}</p><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setLaunchError("")}>Close</button>{isAdmin && <button type="button" className="button primary" onClick={() => { setLaunchError(""); navigate("/admin"); }}>Go to Administration</button>}</div></section></div>}
    </div>
  );
}

const feedbackTypeLabels: Record<string, string> = { BUG: "Bug", GLITCH: "Glitch", APPRECIATION: "Appreciation", FEATURE: "Feature idea", OTHER: "Other" };

function FeedbackPage() {
  const [type, setType] = useState("BUG"); const [comment, setComment] = useState(""); const [message, setMessage] = useState(""); const [error, setError] = useState(""); const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent) { event.preventDefault(); setSaving(true); setError(""); setMessage(""); try { const result = await api<{ message: string }>("/api/feedback", { method: "POST", body: JSON.stringify({ type, comment }) }); setComment(""); setMessage(result.message); } catch (caught) { setError(caught instanceof Error ? caught.message : "Feedback could not be sent"); } finally { setSaving(false); } }
  return <section className="panel feedback-page"><div className="panel-heading"><div><p className="eyebrow ink">Help improve Virtual Hall Pass</p><h1>Send feedback</h1><p className="form-note">Report a bug or glitch, suggest a feature, or leave an appreciation. Your name, school, district, and assigned role are included for the Global Admin.</p></div></div>{error && <div className="alert error">{error}</div>}{message && <div className="alert success">{message}</div>}<form className="feedback-form" onSubmit={submit}><label>Feedback type<select value={type} onChange={(event) => setType(event.target.value)}>{Object.entries(feedbackTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label>Your message<textarea value={comment} onChange={(event) => setComment(event.target.value)} maxLength={5000} placeholder="Please include what happened, what you expected, and any helpful details." required /><small>{comment.length.toLocaleString()} / 5,000</small></label><div className="dialog-actions"><button className="button primary" disabled={saving}>{saving ? "Sending…" : "Send feedback"}</button></div></form></section>;
}

interface ProfileSchool {
  id: string;
  name: string;
  district_id: string;
  district_name: string;
}

interface ProfileDistrict {
  id: string;
  name: string;
}

interface PendingHomeLocationRequest {
  id: string;
  requested_district_id: string;
  requested_district_name: string;
  requested_school_id: string | null;
  requested_school_name: string | null;
  requested_at: string;
}

function UserProfilePage({
  user,
  onUserChange,
}: {
  user: CurrentUser;
  onUserChange: () => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [title, setTitle] = useState(user.title ?? "");
  const [email, setEmail] = useState(user.email);
  const [homeDistrictId, setHomeDistrictId] = useState(user.homeDistrictId ?? "");
  const [homeSchoolId, setHomeSchoolId] = useState(user.homeSchoolId ?? "");
  const [districts, setDistricts] = useState<ProfileDistrict[]>([]);
  const [schools, setSchools] = useState<ProfileSchool[]>([]);
  const [pendingHomeLocationRequest, setPendingHomeLocationRequest] = useState<PendingHomeLocationRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    let cancelled = false;
    void api<{ districts: ProfileDistrict[]; schools: ProfileSchool[]; pendingHomeLocationRequest: PendingHomeLocationRequest | null }>("/api/auth/profile-options")
      .then((response) => {
        if (!cancelled) {
          setDistricts(response.districts);
          setSchools(response.schools);
          setPendingHomeLocationRequest(response.pendingHomeLocationRequest);
        }
      })
      .catch((caught) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : "Profile options could not be loaded");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSaved("");
    try {
      const locationChanged = homeDistrictId !== (user.homeDistrictId ?? "")
        || homeSchoolId !== (user.homeSchoolId ?? "");
      if (locationChanged && !pendingHomeLocationRequest) {
        if (!homeDistrictId) throw new Error("Select a home district.");
        if (user.roles.some((role) => role.role === "TEACHER") && !homeSchoolId) {
          throw new Error("A teacher account needs a home school.");
        }
      }
      await api("/api/auth/profile", {
        method: "PATCH",
        body: JSON.stringify({
          displayName,
          title: title || null,
          email,
        }),
      });
      let locationUpdated = false;
      if (locationChanged && !pendingHomeLocationRequest) {
        const response = await api<{ id?: string; approved?: boolean }>("/api/auth/home-location-requests", {
          method: "POST",
          body: JSON.stringify({ districtId: homeDistrictId, schoolId: homeSchoolId || null }),
        });
        locationUpdated = Boolean(response.approved);
        if (!locationUpdated && response.id) {
          const requestedDistrict = districts.find((district) => district.id === homeDistrictId);
          const requestedSchool = schools.find((school) => school.id === homeSchoolId);
          setPendingHomeLocationRequest({
            id: response.id,
            requested_district_id: homeDistrictId,
            requested_district_name: requestedDistrict?.name ?? "Selected district",
            requested_school_id: homeSchoolId || null,
            requested_school_name: requestedSchool?.name ?? null,
            requested_at: new Date().toISOString(),
          });
        }
      }
      await onUserChange();
      setSaved(locationChanged && !pendingHomeLocationRequest
        ? locationUpdated ? "Profile and home location updated." : "Profile updated. Your home location request was sent for approval."
        : "Profile updated.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Profile could not be updated");
    } finally {
      setSaving(false);
    }
  }

  async function cancelHomeLocationRequest() {
    if (!pendingHomeLocationRequest) return;
    setSaving(true);
    setError("");
    try {
      await api(`/api/auth/home-location-requests/${pendingHomeLocationRequest.id}`, { method: "DELETE" });
      setPendingHomeLocationRequest(null);
      setSaved("Home location request cancelled.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Home location request could not be cancelled");
    } finally {
      setSaving(false);
    }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }
    setChangingPassword(true);
    setPasswordError("");
    try {
      await api("/api/auth/password", {
        method: "PATCH",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordDialogOpen(false);
      setSaved("Password changed. Other signed-in devices were logged out.");
      await onUserChange();
    } catch (caught) {
      setPasswordError(caught instanceof Error ? caught.message : "Password could not be changed");
    } finally {
      setChangingPassword(false);
    }
  }

  return (
    <>
      <header className="page-header">
        <div>
          <p className="eyebrow ink">User account</p>
          <h1>My profile</h1>
          <p>Keep your name, title, login email, and home location current.</p>
        </div>
      </header>
      {error && <Dismissible message={error} kind="error" onClose={() => setError("")} />}
      {saved && <Dismissible message={saved} onClose={() => setSaved("")} />}
      <section className="panel profile-panel">
        <div className="panel-heading">
          <div><p className="eyebrow ink">Personal information</p><h2>Profile details</h2></div>
        </div>
        <form className="stack-form" onSubmit={save}>
          <label>Full name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} minLength={2} maxLength={160} required /></label>
          <label>Title <span className="muted">(optional)</span><input placeholder="Mr. / Ms. / Dr." value={title} onChange={(event) => setTitle(event.target.value)} maxLength={40} /></label>
          <label>Login email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /><small>Your email is also your username.</small></label>
          <label>Home district<select value={homeDistrictId} onChange={(event) => { setHomeDistrictId(event.target.value); if (homeSchoolId && !schools.some((school) => school.id === homeSchoolId && school.district_id === event.target.value)) setHomeSchoolId(""); }} disabled={loading || Boolean(pendingHomeLocationRequest)} required><option value="" disabled>Select home district</option>{districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label>
          <label>Home school<select value={homeSchoolId} onChange={(event) => setHomeSchoolId(event.target.value)} disabled={loading || !homeDistrictId || Boolean(pendingHomeLocationRequest)} required={user.roles.some((role) => role.role === "TEACHER")}><option value="">{user.roles.some((role) => role.role === "TEACHER") ? "Select home school" : "No home school"}</option>{schools.filter((school) => school.district_id === homeDistrictId).map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select><small>Home district and school changes require approval by an administrator in the requested location.</small></label>
          {pendingHomeLocationRequest && <div className="form-note"><strong>Approval pending:</strong> {pendingHomeLocationRequest.requested_district_name}{pendingHomeLocationRequest.requested_school_name ? ` · ${pendingHomeLocationRequest.requested_school_name}` : ""}. <button type="button" className="text-button" onClick={() => void cancelHomeLocationRequest()} disabled={saving}>Cancel request</button></div>}
          <div className="dialog-actions profile-actions"><button type="button" className="button quiet" onClick={() => { setPasswordError(""); setPasswordDialogOpen(true); }}>Change password</button><button className="button primary" disabled={saving || loading}>{saving ? "Saving…" : "Save profile"}</button></div>
        </form>
      </section>
      {passwordDialogOpen && <div className="dialog-backdrop" role="presentation" onMouseDown={() => { if (!changingPassword) setPasswordDialogOpen(false); }}><form className="dialog" onSubmit={changePassword} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Account security</p><h2>Change password</h2></div><button type="button" className="icon-button" onClick={() => setPasswordDialogOpen(false)} disabled={changingPassword} aria-label="Close change password">×</button></div>{passwordError && <div className="alert error">{passwordError}</div>}<label>Current password<input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required autoFocus /></label><label>New password<input type="password" autoComplete="new-password" minLength={12} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required /><small>At least 12 characters</small></label><label>Confirm new password<input type="password" autoComplete="new-password" minLength={12} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required /></label><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setPasswordDialogOpen(false)} disabled={changingPassword}>Cancel</button><button className="button primary" disabled={changingPassword}>{changingPassword ? "Changing…" : "Change password"}</button></div></form></div>}
    </>
  );
}

interface ClassState {
  class: {
    id: string;
    name: string;
    period_label: string;
    school_name: string;
    timezone: string;
    max_restroom_out: number;
    pass_limit_minutes: number;
    warning_minutes: number;
    timer_colors_enabled: number;
    timer_normal_color: string;
    timer_warning_color: string;
    timer_alert_color: string;
    semester_pass_limit: number | null;
    emergency_pass_limit: number | null;
    schedule_rules_enabled: number;
    schedule_hold_minutes: number;
    schedule_end_warning_minutes: number;
    schedule_not_returned_minutes: number;
    substitute_mode: number;
  };
  roster: Student[];
  categories: PassCategory[];
  passes: Pass[];
  carryover: Array<{
    id: string;
    class_name: string;
    period_label: string;
    first_name: string;
    last_name: string;
    reason_code: string;
    uses_restroom_capacity?: number;
    approved_at: string;
    due_at: string;
    limit_minutes: number | null;
    warning_minutes: number;
    restroom_alert_limit_minutes: number;
    timer_colors_enabled: number;
    timer_normal_color: string;
    timer_warning_color: string;
    timer_alert_color: string;
    elapsed_seconds?: number | null;
  }>;
  notifications: Notification[];
  schedule: {
    calendarAvailable: boolean;
    noSchool: boolean;
    scheduleId: string | null;
    scheduleName: string | null;
    phase: "PERIOD" | "BREAK" | "BEFORE_SCHOOL" | "AFTER_SCHOOL" | "NO_SCHOOL" | "UNSCHEDULED";
    source: "SCHOOL" | "TEACHER_OVERRIDE" | "TEACHER_NONE";
    slot: { id: string; label: string; startsAt: string; endsAt: string } | null;
  } | null;
  schoolSchedule: {
    calendarAvailable: boolean;
    noSchool: boolean;
    scheduleName: string | null;
  } | null;
  bellScheduleOptions: Array<{ id: string; name: string }>;
}

type TardyDetailScope = "FILTER" | "SEMESTER";

interface TardyFocus {
  studentId: string;
  scope: TardyDetailScope;
}

interface RolloverSourceClass {
  id: string;
  name: string;
  period_label: string;
  term_id: string;
  term_name: string;
  term_starts_on: string | null;
  term_ends_on: string | null;
  archived_at: string | null;
  roster_count: number;
}

interface RolloverResponse {
  currentTerm: { id: string; name: string; starts_on: string | null; ends_on: string | null } | null;
  status: "PENDING" | "SKIPPED" | "COMPLETED" | "NOT_AVAILABLE";
  sourceClasses: RolloverSourceClass[];
}

interface ArchivedClass {
  id: string;
  school_id: string;
  school_name: string;
  timezone: string;
  name: string;
  period_label: string;
  term_id: string | null;
  term_name: string | null;
  term_starts_on: string | null;
  term_ends_on: string | null;
  archived_at: string | null;
  roster_count: number;
  pass_count: number;
  tardy_count: number;
}

type ClassroomTab = "live" | "roster" | "history" | "tardies" | "alerts" | "settings" | "devices";

const ALL_CLASSES_PERIOD_VALUE = "__ALL_CLASSES__";
const NEW_PERIOD_VALUE = "__NEW_PERIOD__";
const periodSelectorTabs: ClassroomTab[] = ["roster", "history", "tardies", "settings"];
const allPeriodsTabs: ClassroomTab[] = ["roster", "history", "tardies", "settings"];
const workspaceTitles: Partial<Record<ClassroomTab, string>> = {
  live: "Live Room",
  alerts: "Alerts",
  devices: "Devices",
};

function TeacherDashboard({ user }: { user: CurrentUser }) {
  const [classes, setClasses] = useState<ClassPeriod[]>([]);
  const [state, setState] = useState<ClassState | null>(null);
  const [stateReceivedAt, setStateReceivedAt] = useState(0);
  const [alertCount, setAlertCount] = useState(0);
  const [tab, setTab] = useState<ClassroomTab>("live");
  const [viewPeriodId, setViewPeriodId] = useState(() => {
    try {
      return localStorage.getItem(`hallway:teacher-view-period:${user.id}`) ?? "";
    } catch {
      return "";
    }
  });
  const [allPeriods, setAllPeriods] = useState(false);
  const [historyFocus, setHistoryFocus] = useState<string | null>(null);
  const [tardyFocus, setTardyFocus] = useState<TardyFocus | null>(null);
  const [error, setError] = useState("");
  const [showNewClass, setShowNewClass] = useState(false);
  const [recordVersion, setRecordVersion] = useState(0);
  const [rollover, setRollover] = useState<RolloverResponse | null>(null);
  const selected = classes.find((item) => item.id === viewPeriodId) ?? classes[0];
  const selectedState = state?.class.id === selected?.id ? state : null;
  const viewingAllPeriods = Boolean(selected && allPeriods);
  const visibleTabs: ClassroomTab[] = ["live", "roster", "history", "tardies", "alerts", "settings", "devices"];

  const loadClasses = useCallback(async () => {
    const response = await api<{ classes: ClassPeriod[] }>("/api/classes");
    setClasses(response.classes);
  }, []);

  const loadRollover = useCallback(async () => {
    if (!user.homeSchoolId) {
      setRollover(null);
      return;
    }
    try {
      const response = await api<RolloverResponse>(`/api/classes/rollover?schoolId=${encodeURIComponent(user.homeSchoolId)}`);
      setRollover(response);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Semester rollover could not be loaded");
    }
  }, [user.homeSchoolId]);

  const loadState = useCallback(async () => {
    if (!selected) {
      setState(null);
      return;
    }
    try {
      const response = await api<ClassState>(`/api/classes/${selected.id}/state`);
      setState(response);
      setStateReceivedAt(Date.now());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to refresh");
    }
  }, [selected?.id]);

  const loadAlertCount = useCallback(async () => {
    if (!selected) {
      setAlertCount(0);
      return;
    }
    try {
      const alerts = await api<AlertsResponse>(`/api/classes/${selected.id}/alerts`);
      setAlertCount(totalAlerts(alerts));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to refresh alerts");
    }
  }, [selected?.id]);

  useEffect(() => {
    void loadClasses();
  }, [loadClasses]);
  useEffect(() => {
    void loadRollover();
  }, [loadRollover]);
  useEffect(() => {
    if (!classes.length) {
      setViewPeriodId("");
      setAllPeriods(false);
      return;
    }
    if (!classes.some((item) => item.id === viewPeriodId)) {
      setViewPeriodId(classes[0].id);
    }
  }, [classes, viewPeriodId]);
  useEffect(() => {
    if (!selected) return;
    try {
      localStorage.setItem(`hallway:teacher-view-period:${user.id}`, selected.id);
    } catch {
      // Viewing-period persistence is a convenience only; privacy settings
      // that disable storage must not block the classroom workspace.
    }
  }, [selected?.id, user.id]);
  useEffect(() => {
    void loadState();
    const timer = window.setInterval(() => void loadState(), 5_000);
    return () => window.clearInterval(timer);
  }, [loadState]);
  useEffect(() => {
    void loadAlertCount();
    const timer = window.setInterval(() => void loadAlertCount(), 5_000);
    return () => window.clearInterval(timer);
  }, [loadAlertCount]);

  function chooseViewingPeriod(value: string) {
    if (value === ALL_CLASSES_PERIOD_VALUE) {
      setAllPeriods(true);
      setHistoryFocus(null);
      setTardyFocus(null);
      return;
    }
    if (value === NEW_PERIOD_VALUE) {
      setShowNewClass(true);
      return;
    }
    setAllPeriods(false);
    setViewPeriodId(value);
    setHistoryFocus(null);
    setTardyFocus(null);
  }

  function openStudentInPeriod(
    classId: string,
    studentId: string,
    targetTab: "history" | "tardies" = "history",
    tardyDetailScope: TardyDetailScope = "FILTER",
  ) {
    setViewPeriodId(classId);
    setAllPeriods(false);
    if (targetTab === "history") {
      setHistoryFocus(studentId);
      setTardyFocus(null);
    } else {
      setTardyFocus({ studentId, scope: tardyDetailScope });
      setHistoryFocus(null);
    }
    setTab(targetTab);
  }

  function openStudentHistory(classId: string, studentId: string) {
    setViewPeriodId(classId);
    setAllPeriods(false);
    setHistoryFocus(studentId);
    setTardyFocus(null);
    setTab("history");
  }

  if (!user.roles.some((role) => role.role === "TEACHER")) {
    return (
      <section className="empty-state large">
        <span className="empty-icon">◇</span>
        <h1>Administration account</h1>
        <p>This account has no teacher role. Use Administration to manage the system.</p>
        <Link className="button primary" to="/admin">
          Open administration
        </Link>
      </section>
    );
  }

  return (
    <>
      <header className="page-header classroom-header">
        <div>
          <p className="eyebrow ink">Teacher workspace</p>
          <h1>{workspaceTitles[tab] ?? (selected ? (viewingAllPeriods ? "All classes" : `${selected.period_label} · ${selected.name}`) : "Your classroom")}</h1>
          <p>{selected?.school_name ?? "Create or select a class period to begin."}</p>
        </div>
      </header>
      {error && <Dismissible message={error} kind="error" onClose={() => setError("")} />}
      <nav className="tabs">
        {visibleTabs.map((item) => (
          <button
            key={item}
            className={tab === item ? "active" : ""}
            onClick={() => {
              setTab(item);
              if (!allPeriodsTabs.includes(item)) setAllPeriods(false);
            }}
            aria-label={item === "alerts" && alertCount ? `Alerts: ${alertCount} need attention` : undefined}
          >
            {item === "live"
              ? "Live room"
              : item === "tardies"
                ? "Tardies"
                : item === "alerts"
                  ? <><span>Alerts</span>{alertCount > 0 && <span className="tab-alert-count">{alertCount}</span>}</>
                  : item === "settings"
                      ? "Settings"
                      : item.charAt(0).toUpperCase() + item.slice(1)}
          </button>
        ))}
      </nav>
      {selected && periodSelectorTabs.includes(tab) && (
        <div className="viewing-period-selector">
          <label>
            Viewing period
            <select
              value={viewingAllPeriods ? ALL_CLASSES_PERIOD_VALUE : selected.id}
              onChange={(event) => chooseViewingPeriod(event.target.value)}
            >
              {allPeriodsTabs.includes(tab) && <option value={ALL_CLASSES_PERIOD_VALUE}>All classes</option>}
              {classes.map((item) => (
                <option key={item.id} value={item.id}>{item.period_label} — {item.name}</option>
              ))}
              <option value={NEW_PERIOD_VALUE}>+ Add new period</option>
            </select>
          </label>
        </div>
      )}
      {selected ? (
        <>
          {tab === "live" && selectedState && (
            <LiveRoom
              state={selectedState}
              receivedAt={stateReceivedAt}
              refresh={loadState}
              onError={setError}
            />
          )}
          {tab === "roster" && (viewingAllPeriods
            ? <AllPeriodsRosterPanel schoolId={selected.school_id} onError={setError} />
            : selectedState && <RosterPanel classId={selected.id} roster={selectedState.roster} refresh={loadState} onError={setError} onViewHistory={(studentId) => openStudentHistory(selected.id, studentId)} />
          )}
          {tab === "history" && (
            <HistoryPanel key={`history-${recordVersion}`} classId={viewingAllPeriods ? "all" : selected.id} schoolId={selected.school_id} allPeriods={viewingAllPeriods} timeZone={selectedState?.class.timezone ?? selected.timezone} defaultTermId={selected.term_id} focusStudentId={historyFocus} onFocusHandled={() => setHistoryFocus(null)} onOpenPeriod={openStudentInPeriod} onRecordsAdded={() => setRecordVersion((value) => value + 1)} onError={setError} />
          )}
          {tab === "tardies" && (
            <TardyPanel key={`tardies-${recordVersion}`} classId={viewingAllPeriods ? "all" : selected.id} schoolId={selected.school_id} allPeriods={viewingAllPeriods} timeZone={selectedState?.class.timezone ?? selected.timezone} defaultTermId={selected.term_id} focusStudent={tardyFocus} onFocusHandled={() => setTardyFocus(null)} onOpenPeriod={(classId, studentId, scope) => openStudentInPeriod(classId, studentId, "tardies", scope)} onRecordsAdded={() => setRecordVersion((value) => value + 1)} onError={setError} />
          )}
          {tab === "alerts" && (
            <AlertsPanel classId={selected.id} timeZone={selectedState?.class.timezone ?? selected.timezone} onError={setError} onCountChanged={setAlertCount} onOpenStudent={openStudentInPeriod} />
          )}
          {tab === "settings" && viewingAllPeriods && (
            <section className="panel">
              <p className="eyebrow ink">All classes</p>
              <h2>Select a period to edit its settings</h2>
            </section>
          )}
          {!viewingAllPeriods && tab === "settings" && selectedState && (
            <TeacherSettingsPanel
              classId={selected.id}
              settings={selectedState.class}
              schedule={selectedState.schedule}
              refresh={loadState}
              onError={setError}
              onSettingsSaved={async () => { await loadClasses(); await loadState(); }}
            />
          )}
          {tab === "devices" && <DevicesPanel timeZone={selectedState?.class.timezone ?? selected.timezone} onError={setError} />}
        </>
      ) : (
        <section className="empty-state">
          <span className="empty-icon">⌁</span>
          <h2>No class periods yet</h2>
          <p>Create your first period, then pair a kiosk or display.</p>
          <button className="button primary" onClick={() => setShowNewClass(true)}>
            Create a class period
          </button>
        </section>
      )}
      {showNewClass && (
        <NewClassDialog
          schoolId={user.homeSchoolId}
          onClose={() => setShowNewClass(false)}
          onCreated={async (id) => {
            await loadClasses();
            setViewPeriodId(id);
            setAllPeriods(false);
            setTab("roster");
            setShowNewClass(false);
          }}
        />
      )}
      {rollover?.status === "PENDING" && rollover.currentTerm && (
        <RolloverDialog
          schoolId={user.homeSchoolId}
          rollover={rollover}
          onClose={() => setRollover((current) => current ? { ...current, status: "NOT_AVAILABLE" } : current)}
          onCompleted={async () => {
            await loadClasses();
            await loadRollover();
          }}
        />
      )}
    </>
  );
}

interface RetroactiveOptions {
  students: Array<{ id: string; student_number: string; first_name: string; last_name: string; preferred_first_name: string | null; preferred_last_name: string | null }>;
  categories: Array<{ id: string; label: string; is_tardy: number }>;
  timezone: string;
}

interface RetroactiveEntry { recordType: "PASS" | "TARDY"; studentId: string; categoryId: string; occurredAt: string; durationMinutes: number; }

function blankRetroactiveEntry(timeZone: string, recordType: "PASS" | "TARDY" = "PASS"): RetroactiveEntry {
  return { recordType, studentId: "", categoryId: "", occurredAt: dateTimeInputAtTimeZone(timeZone), durationMinutes: 10 };
}

function retroactiveTimeText(time: string) {
  const [rawHour, minute = "00"] = time.split(":");
  const hour = Number(rawHour);
  const period = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 || 12}:${minute} ${period}`;
}

function normalizedRetroactiveTime(value: string) {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3]?.toUpperCase();
  if (minute > 59 || hour > (period ? 12 : 23) || hour < (period ? 1 : 0)) return null;
  const hour24 = period ? (hour % 12) + (period === "PM" ? 12 : 0) : hour;
  return `${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function RetroactiveTimeInput({ date, time, label, listId, onChange }: { date: string; time: string; label: string; listId: string; onChange: (value: string) => void }) {
  function commitTime(rawValue: string, target: HTMLInputElement) {
    const normalized = normalizedRetroactiveTime(rawValue);
    if (!normalized) {
      target.value = retroactiveTimeText(time);
      return;
    }
    onChange(`${date}T${normalized}`);
  }

  return <><input aria-label={`${label} time`} defaultValue={retroactiveTimeText(time)} list={listId} inputMode="text" placeholder="3:45 PM" onBlur={(event) => commitTime(event.target.value, event.currentTarget)} required /><datalist id={listId}>{Array.from({ length: 48 }, (_, index) => { const hour = Math.floor(index / 2); const minute = index % 2 ? "30" : "00"; return <option key={index} value={retroactiveTimeText(`${String(hour).padStart(2, "0")}:${minute}`)} />; })}</datalist></>;
}

function BellScheduleTimeInput({ value, label, listId, onChange }: { value: string; label: string; listId: string; onChange: (value: string) => void }) {
  return <><input aria-label={label} defaultValue={retroactiveTimeText(value)} list={listId} inputMode="text" placeholder="8:00 AM" onBlur={(event) => {
    const normalized = normalizedRetroactiveTime(event.currentTarget.value);
    if (!normalized) event.currentTarget.value = retroactiveTimeText(value);
    else onChange(normalized);
  }} required /><datalist id={listId}>{Array.from({ length: 48 }, (_, index) => { const hour = Math.floor(index / 2); const minute = index % 2 ? "30" : "00"; return <option key={index} value={retroactiveTimeText(`${String(hour).padStart(2, "0")}:${minute}`)} />; })}</datalist></>;
}

function RetroactiveRecordDialog({ classId, timeZone, initialType, onClose, onCreated }: { classId: string; timeZone: string; initialType: "PASS" | "TARDY"; onClose: () => void; onCreated: () => void }) {
  const [options, setOptions] = useState<RetroactiveOptions | null>(null);
  const [entries, setEntries] = useState<RetroactiveEntry[]>(() => [blankRetroactiveEntry(timeZone, initialType)]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api<RetroactiveOptions>(`/api/classes/${classId}/retroactive-options`)
      .then(setOptions)
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Retroactive-record options could not be loaded"));
  }, [classId]);

  function update(index: number, change: Partial<RetroactiveEntry>) {
    setEntries((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, ...change } : entry));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      for (const entry of entries) {
        await api(`/api/classes/${classId}/retroactive-records`, { method: "POST", body: JSON.stringify(entry) });
      }
      onCreated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Past records could not be added. Any earlier valid rows were saved.");
    } finally {
      setSaving(false);
    }
  }

  return <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
    <form className="dialog wide-dialog retroactive-record-dialog" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
      <div className="panel-heading"><div><p className="eyebrow ink">Historical entry</p><h2>Add past records</h2><p className="form-note">Completed historical entries only — they do not affect the live queue, capacity, or student-safety rules. Times use the school’s local timezone.</p></div><button type="button" className="icon-button" onClick={onClose}>×</button></div>
      {error && <div className="alert error">{error}</div>}
      <div className="table-wrap roster-entry-table retroactive-entry-table"><table><thead><tr><th>Type</th><th>Student</th><th>Reason</th><th>Date</th><th>Time</th><th>Duration</th><th><span className="sr-only">Remove row</span></th></tr></thead><tbody>{entries.map((entry, index) => {
        const categories = (options?.categories ?? []).filter((category) => Boolean(category.is_tardy) === (entry.recordType === "TARDY"));
        const [date, time = "00:00"] = entry.occurredAt.split("T");
        return <tr key={index}><td><select aria-label={`Record type, row ${index + 1}`} value={entry.recordType} onChange={(event) => update(index, { recordType: event.target.value as "PASS" | "TARDY", categoryId: "" })}><option value="PASS">Hall pass</option><option value="TARDY">Tardy</option></select></td><td><select aria-label={`Student, row ${index + 1}`} value={entry.studentId} onChange={(event) => update(index, { studentId: event.target.value })} required disabled={!options}><option value="" disabled>Select student</option>{options?.students.map((student) => <option key={student.id} value={student.id}>{studentDisplayName(student)} · {student.student_number}</option>)}</select></td><td><select aria-label={`Reason, row ${index + 1}`} value={entry.categoryId} onChange={(event) => update(index, { categoryId: event.target.value })} required disabled={!categories.length}><option value="" disabled>{categories.length ? "Select reason" : "No active reason"}</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.label}</option>)}</select></td><td><input aria-label={`Date, row ${index + 1}`} type="date" value={date} max={dateTimeInputAtTimeZone(timeZone).slice(0, 10)} onChange={(event) => update(index, { occurredAt: `${event.target.value}T${time}` })} required /></td><td><RetroactiveTimeInput date={date} time={time} label={`Time, row ${index + 1}`} listId={`retroactive-time-${index}`} onChange={(occurredAt) => update(index, { occurredAt })} /></td><td>{entry.recordType === "PASS" ? <input aria-label={`Duration, row ${index + 1}`} type="number" min="1" max="720" inputMode="numeric" value={entry.durationMinutes} onChange={(event) => update(index, { durationMinutes: Number(event.target.value) })} required /> : <span className="muted">—</span>}</td><td><button type="button" className="icon-button" aria-label={`Remove row ${index + 1}`} disabled={entries.length === 1} onClick={() => setEntries((current) => current.filter((_, entryIndex) => entryIndex !== index))}>×</button></td></tr>;
      })}</tbody></table></div>
      <div className="roster-entry-actions"><button type="button" className="button quiet small" onClick={() => setEntries((current) => [...current, blankRetroactiveEntry(timeZone, initialType)])}>Add a row</button><div className="dialog-actions"><button type="button" className="button quiet" onClick={onClose}>Cancel</button><button className="button primary" disabled={saving || !options}>{saving ? "Saving…" : `Add ${entries.length} record${entries.length === 1 ? "" : "s"}`}</button></div></div>
    </form>
  </div>;
}

function HistoryRecordDialog({
  classId,
  timeZone,
  recordType,
  record,
  categories,
  onClose,
  onSaved,
  onError,
}: {
  classId: string;
  timeZone: string;
  recordType: "PASS" | "TARDY";
  record: { id: string; category_id?: string | null; requested_at: string; duration_seconds?: number };
  categories: PassCategory[];
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const choices = categories.filter((category) => Boolean(category.is_tardy) === (recordType === "TARDY") && Boolean(category.active));
  const [categoryId, setCategoryId] = useState(record.category_id && choices.some((category) => category.id === record.category_id) ? record.category_id : choices[0]?.id ?? "");
  const [occurredAt, setOccurredAt] = useState(() => dateTimeValueAtTimeZone(record.requested_at, timeZone));
  const [durationMinutes, setDurationMinutes] = useState(() => Math.max(1, Math.round((record.duration_seconds ?? 0) / 60)));
  const [saving, setSaving] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await api(`/api/classes/${classId}/history-records/${record.id}`, {
        method: "PATCH",
        body: JSON.stringify({ categoryId, occurredAt, ...(recordType === "PASS" ? { durationMinutes } : {}) }),
      });
      onSaved();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "History record could not be updated");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete this ${recordType === "TARDY" ? "tardy" : "hall pass"} record? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api(`/api/classes/${classId}/history-records/${record.id}`, { method: "DELETE" });
      onSaved();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "History record could not be deleted");
    } finally {
      setSaving(false);
    }
  }

  return <div className="dialog-backdrop" role="presentation" onMouseDown={() => { if (!saving) onClose(); }}>
    <form className="dialog history-record-dialog" onSubmit={save} onMouseDown={(event) => event.stopPropagation()}>
      <div className="panel-heading"><div><p className="eyebrow ink">Historical record</p><h2>Edit {recordType === "TARDY" ? "tardy" : "hall pass"}</h2></div><button type="button" className="icon-button" onClick={onClose} disabled={saving} aria-label="Close">x</button></div>
      <label>Category<select value={categoryId} onChange={(event) => setCategoryId(event.target.value)} required>{choices.map((category) => <option key={category.id} value={category.id}>{category.label}</option>)}</select></label>
      <label>Date and time<input type="datetime-local" value={occurredAt} max={dateTimeInputAtTimeZone(timeZone)} onChange={(event) => setOccurredAt(event.target.value)} required /></label>
      {recordType === "PASS" && <label>Duration (minutes)<input type="number" min="1" max="720" value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.target.value))} required /></label>}
      <p className="form-note">Times use the school&apos;s local timezone.</p>
      <div className="dialog-actions"><button type="button" className="button quiet danger-text" onClick={() => void remove()} disabled={saving}>Delete</button><button type="button" className="button quiet" onClick={onClose} disabled={saving}>Cancel</button><button className="button primary" disabled={saving || !categoryId}>{saving ? "Saving..." : "Save changes"}</button></div>
    </form>
  </div>;
}

interface RolloverSelection {
  sourceClassId: string;
  selected: boolean;
  name: string;
  periodLabel: string;
  copyRoster: boolean;
}

function RolloverDialog({
  schoolId,
  rollover,
  onClose,
  onCompleted,
}: {
  schoolId: string | null;
  rollover: RolloverResponse;
  onClose: () => void;
  onCompleted: () => Promise<void>;
}) {
  const [selections, setSelections] = useState<RolloverSelection[]>(() => rollover.sourceClasses.map((source) => ({
    sourceClassId: source.id,
    selected: true,
    name: source.name,
    periodLabel: source.period_label,
    copyRoster: true,
  })));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<"rollover" | "skip" | null>(null);

  useEffect(() => {
    setSelections(rollover.sourceClasses.map((source) => ({
      sourceClassId: source.id,
      selected: true,
      name: source.name,
      periodLabel: source.period_label,
      copyRoster: true,
    })));
  }, [rollover.sourceClasses]);

  function update(sourceClassId: string, patch: Partial<RolloverSelection>) {
    setSelections((current) => current.map((selection) => selection.sourceClassId === sourceClassId
      ? { ...selection, ...patch }
      : selection));
  }

  async function submit(action: "ROLL_OVER" | "SKIP") {
    if (!schoolId || !rollover.currentTerm) return setError("Your account needs a home school and a current academic term.");
    const selected = selections.filter((selection) => selection.selected);
    if (action === "ROLL_OVER" && !selected.length) {
      return setError("Select a class to roll over, or choose Start with all new classes.");
    }
    setBusy(action === "ROLL_OVER" ? "rollover" : "skip");
    setError("");
    try {
      await api("/api/classes/rollover", {
        method: "POST",
        body: JSON.stringify({
          schoolId,
          targetTermId: rollover.currentTerm.id,
          action,
          classes: action === "ROLL_OVER"
            ? selected.map(({ sourceClassId, name, periodLabel, copyRoster }) => ({ sourceClassId, name, periodLabel, copyRoster }))
            : [],
        }),
      });
      await onCompleted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Semester rollover could not be completed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation">
      <section className="dialog rollover-dialog" aria-modal="true" role="dialog" aria-labelledby="rollover-title">
        <div className="panel-heading">
          <div>
            <p className="eyebrow ink">New semester</p>
            <h2 id="rollover-title">Set up {rollover.currentTerm?.name}</h2>
            <p className="form-note">Choose prior classes to copy. Each new class receives its own roster and settings; past passes and tardies stay with the archived class.</p>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close rollover wizard">×</button>
        </div>
        {error && <div className="alert error">{error}</div>}
        <div className="rollover-class-list">
          {rollover.sourceClasses.map((source) => {
            const selection = selections.find((item) => item.sourceClassId === source.id);
            if (!selection) return null;
            return (
              <article key={source.id} className={selection.selected ? "selected" : ""}>
                <label className="check-row rollover-select">
                  <input type="checkbox" checked={selection.selected} onChange={(event) => update(source.id, { selected: event.target.checked })} />
                  <span><strong>{source.period_label} · {source.name}</strong><small>{source.term_name} · {source.roster_count} active student{source.roster_count === 1 ? "" : "s"}</small></span>
                </label>
                {selection.selected && <div className="rollover-fields">
                  <label><span>New period</span><input value={selection.periodLabel} maxLength={80} onChange={(event) => update(source.id, { periodLabel: event.target.value })} /></label>
                  <label><span>New class name</span><input value={selection.name} maxLength={140} onChange={(event) => update(source.id, { name: event.target.value })} /></label>
                  <label className="check-row"><input type="checkbox" checked={selection.copyRoster} onChange={(event) => update(source.id, { copyRoster: event.target.checked })} />Copy active roster and exempt rules</label>
                </div>}
              </article>
            );
          })}
        </div>
        <div className="dialog-actions rollover-actions">
          <button type="button" className="button quiet" disabled={busy !== null} onClick={() => void submit("SKIP")}>{busy === "skip" ? "Saving…" : "Start with all new classes"}</button>
          <button type="button" className="button primary" disabled={busy !== null} onClick={() => void submit("ROLL_OVER")}>{busy === "rollover" ? "Creating classes…" : "Roll over selected classes"}</button>
        </div>
      </section>
    </div>
  );
}

function ArchivedClassesPanel({
  schoolId,
  onError,
}: {
  schoolId: string | null;
  onError: (message: string) => void;
}) {
  const [classes, setClasses] = useState<ArchivedClass[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [view, setView] = useState<"history" | "tardies">("history");
  const selected = classes.find((item) => item.id === selectedId) ?? null;

  const load = useCallback(async () => {
    try {
      const query = schoolId ? `?schoolId=${encodeURIComponent(schoolId)}` : "";
      const response = await api<{ classes: ArchivedClass[] }>(`/api/classes/archived${query}`);
      setClasses(response.classes);
      setSelectedId((current) => current && response.classes.some((item) => item.id === current)
        ? current
        : response.classes[0]?.id ?? "");
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Archived classes could not be loaded");
    }
  }, [onError, schoolId]);

  useEffect(() => { void load(); }, [load]);

  return (
    <section className="archived-classes">
      <div className="archived-class-list panel">
        <div className="panel-heading"><div><p className="eyebrow ink">Previous semesters</p><h2>Archived classes</h2></div><span className="count-badge">{classes.length}</span></div>
        {classes.map((classPeriod) => (
          <button key={classPeriod.id} className={classPeriod.id === selectedId ? "archived-class-card active" : "archived-class-card"} onClick={() => { setSelectedId(classPeriod.id); setView("history"); }}>
            <strong>{classPeriod.period_label} · {classPeriod.name}</strong>
            <small>{classPeriod.term_name ?? "Prior class"}{classPeriod.term_starts_on && classPeriod.term_ends_on ? ` · ${classPeriod.term_starts_on} to ${classPeriod.term_ends_on}` : ""}</small>
            <span>{classPeriod.roster_count} students · {classPeriod.pass_count} passes · {classPeriod.tardy_count} tardies</span>
          </button>
        ))}
        {!classes.length && <MiniEmpty text="No archived classes are available yet." />}
      </div>
      <div className="archived-report">
        {selected ? <>
          <nav className="tabs"><button className={view === "history" ? "active" : ""} onClick={() => setView("history")}>Pass history</button><button className={view === "tardies" ? "active" : ""} onClick={() => setView("tardies")}>Tardies</button></nav>
          {view === "history"
            ? <HistoryPanel classId={selected.id} schoolId={selected.school_id} timeZone={selected.timezone} defaultTermId={selected.term_id} onError={onError} />
            : <TardyPanel classId={selected.id} schoolId={selected.school_id} timeZone={selected.timezone} defaultTermId={selected.term_id} onError={onError} />}
        </> : <section className="empty-state"><span className="empty-icon">⌁</span><h2>Select an archived class</h2><p>Its pass history and tardy records remain available here.</p></section>}
      </div>
    </section>
  );
}

function Dismissible({
  message,
  kind = "success",
  onClose,
}: {
  message: string;
  kind?: "success" | "error";
  onClose: () => void;
}) {
  return (
    <div className={`alert ${kind}`}>
      <span>{message}</span>
      <button onClick={onClose} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}

function Carryover({
  passes,
  receivedAt,
}: {
  passes: ClassState["carryover"];
  receivedAt: number;
}) {
  const now = useClock();
  return (
    <section className="carryover">
      <div>
        <span className="status-dot warning" />
        <strong>Previous-period students still out</strong>
      </div>
      {passes.map((pass) => {
        const tone = restroomTimerTone(
          pass,
          now,
          pass.warning_minutes,
          pass.restroom_alert_limit_minutes,
          receivedAt,
        );
        const presentation = timerPresentation(tone, pass);
        return (
        <span key={pass.id} className={cx("carryover-pass", tone, presentation.className)} style={presentation.style}>
          {pass.first_name} {pass.last_name.slice(0, 1)}. · {pass.period_label} ·{" "}
          {elapsed(pass.approved_at, now, pass.elapsed_seconds, receivedAt)}
        </span>
        );
      })}
    </section>
  );
}

function passesNeedingTeacherAction(passes: Pass[]) {
  return passes.filter(
    (pass) => pass.status === "PENDING_APPROVAL"
      || (pass.approval_mode === "ACKNOWLEDGE" && !pass.approval_acknowledged_at),
  );
}

function TeacherApprovalPanel({
  state,
  refresh,
  onError,
  onSubstituteModeChange,
  substituteModeChanging = false,
  showWhenEmpty = false,
}: {
  state: ClassState;
  refresh: () => Promise<void>;
  onError: (message: string) => void;
  onSubstituteModeChange?: (enabled: boolean) => void;
  substituteModeChanging?: boolean;
  showWhenEmpty?: boolean;
}) {
  const pendingApproval = passesNeedingTeacherAction(state.passes);
  if (!pendingApproval.length && !showWhenEmpty) return null;

  async function act(passId: string, action: "approve" | "acknowledge" | "deny") {
    if (action === "deny" && !window.confirm("Deny this student’s pass request?")) return;
    try {
      await api(`/api/passes/${passId}/${action}`, { method: "POST" });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Pass action failed");
    }
  }

  return (
    <section className="panel approval-panel teacher-approval-panel" aria-live="polite">
      <div className="panel-heading">
        <div>
          <p className="eyebrow ink">Teacher action</p>
          <h2>Approvals</h2>
        </div>
        <div className="approval-heading-actions">
          {onSubstituteModeChange && <button type="button" className={cx("button", "quiet", "small", "substitute-mode-toggle", Boolean(state.class.substitute_mode) && "active")} aria-pressed={Boolean(state.class.substitute_mode)} title="When on, pass requests do not wait for in-app teacher approval or bell-schedule rules. The substitute handles any needed approval verbally." disabled={substituteModeChanging} onClick={() => onSubstituteModeChange(!Boolean(state.class.substitute_mode))}>Substitute mode{state.class.substitute_mode ? ": On" : ""}</button>}
          <span className="count-badge">{pendingApproval.length}</span>
        </div>
      </div>
      {Boolean(state.class.substitute_mode) && <p className="form-note substitute-mode-note">Pass requests proceed without in-app approval or bell-schedule rules while Substitute mode is on. Capacity and separation safeguards still apply.</p>}
      {pendingApproval.length ? <div className="queue-list">
        {pendingApproval.map((pass) => {
          const fullApproval = pass.status === "PENDING_APPROVAL";
          return (
            <article key={pass.id}>
              <div>
                <strong>{studentDisplayName(pass)}</strong>
                <small>{pass.period_label ?? "Unassigned period"} · {categoryLabel(pass.reason_code, state.categories, pass.reason_label)} · {Boolean(pass.emergency_requested) ? "Emergency approval requested" : fullApproval ? "Waiting for approval" : "Acknowledge this pass"}</small>
              </div>
              {fullApproval ? <div className="approval-actions">
                <button className="button quiet small" onClick={() => void act(pass.id, "approve")}>Approve</button>
                <button className="button quiet small danger-button" onClick={() => void act(pass.id, "deny")}>Deny</button>
              </div> : <button className="button quiet small" onClick={() => void act(pass.id, "acknowledge")}>Acknowledge</button>}
            </article>
          );
        })}
      </div> : <MiniEmpty text="No approvals are waiting." />}
    </section>
  );
}

function LiveRoom({
  state,
  receivedAt,
  refresh,
  onError,
}: {
  state: ClassState;
  receivedAt: number;
  refresh: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const now = useClock();
  const active = state.passes.filter((pass) => pass.status === "ACTIVE");
  const waiting = state.passes.filter((pass) => ["WAITLISTED", "READY"].includes(pass.status));
  const pendingApproval = passesNeedingTeacherAction(state.passes);
  const [substituteModeChanging, setSubstituteModeChanging] = useState(false);
  const restroomOut = active.filter((pass) => Boolean(pass.uses_restroom_capacity)).length;
  const waitingPositions = new Map<string, number>();
  const periodQueueCounts = new Map<string, number>();
  for (const pass of waiting) {
    const periodKey = pass.class_period_id ?? "UNASSIGNED";
    const position = (periodQueueCounts.get(periodKey) ?? 0) + 1;
    periodQueueCounts.set(periodKey, position);
    waitingPositions.set(pass.id, position);
  }

  async function passAction(
    passId: string,
    action: "return" | "not-returned" | "override" | "cancel",
  ) {
    if (
      action === "not-returned"
      && !window.confirm("Mark this student as not returned and stop the pass timer now?")
    ) return;
    if (
      action === "cancel"
      && !window.confirm("Remove this student from the waitlist?")
    ) return;
    try {
      await api(`/api/passes/${passId}/${action}`, { method: "POST" });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Pass action failed");
    }
  }

  async function clearWaitlist() {
    if (!waiting.length) return;
    if (!window.confirm(`Clear ${waiting.length} waiting pass${waiting.length === 1 ? "" : "es"} across all periods?`)) return;
    try {
      await api(`/api/classes/${state.class.id}/waitlist/clear`, { method: "POST" });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Waitlist could not be cleared");
    }
  }

  async function setSubstituteMode(enabled: boolean) {
    setSubstituteModeChanging(true);
    try {
      await api(`/api/classes/${state.class.id}/substitute-mode`, { method: "PUT", body: JSON.stringify({ enabled }) });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Substitute mode could not be updated");
    } finally {
      setSubstituteModeChanging(false);
    }
  }

  return (
    <div className="dashboard-grid live-room-grid">
      <section className="metric-strip">
        <Metric value={String(restroomOut)} label="Restroom out · all periods" />
        <Metric value={String(active.length)} label="Total out · all periods" />
        <Metric value={String(waiting.length)} label="Waiting · all periods" />
        <Metric value={String(pendingApproval.length)} label="Needs attention · all periods" />
      </section>
      <section className="panel live-room-students">
        <div className="panel-heading">
          <div>
            <p className="eyebrow ink">Right now</p>
            <h2>Students out</h2>
          </div>
          <span className="live-pill"><i /> Live</span>
        </div>
        {active.length ? (
          <div className="pass-list">
            {active.map((pass) => {
              const tone = restroomTimerTone(
                pass,
                now,
                pass.restroom_warning_minutes ?? state.class.warning_minutes,
                pass.restroom_alert_limit_minutes ?? state.class.pass_limit_minutes,
                receivedAt,
              );
              const presentation = timerPresentation(tone, passTimerRules(pass, state.class));
              return (
                <article className={cx("pass-card", "live-pass-card", tone === "limit" && "overdue")} key={pass.id}>
                  <div className="pass-person">
                    <strong>{studentDisplayName(pass)}</strong>
                    <span>{pass.period_label ?? "Unassigned period"} · {categoryLabel(pass.reason_code, state.categories, pass.reason_label)}</span>
                  </div>
                  {pass.override_used ? <span className="override-label">Override</span> : null}
                  <div className={cx("timer", tone, presentation.className)} style={presentation.style}>
                    <small>{tone === "limit" ? "Limit reached" : tone === "warning" ? "Warning" : "Elapsed"}</small>
                    <strong>{elapsed(pass.approved_at, now, pass.elapsed_seconds, receivedAt)}</strong>
                  </div>
                  <div className="pass-actions">
                    <button className="button quiet" onClick={() => void passAction(pass.id, "return")}>Returned</button>
                    <button className="text-button danger-text" onClick={() => void passAction(pass.id, "not-returned")}>Not returned</button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <MiniEmpty text="Everyone is currently in the room." />
        )}
      </section>
      <section className="panel live-room-waitlist">
        <div className="panel-heading">
          <div>
            <p className="eyebrow ink">Pass clearance</p>
            <h2>Waitlist</h2>
          </div>
          <div className="panel-heading-actions">
            <span className="count-badge">{waiting.length}</span>
            {waiting.length > 0 && <button className="text-button danger-text" onClick={() => void clearWaitlist()}>Clear waitlist</button>}
          </div>
        </div>
        {waiting.length ? (
          <div className="queue-list">
            {waiting.map((pass) => (
              <article key={pass.id}>
                <span className="queue-position">{waitingPositions.get(pass.id)}</span>
                <div>
                  <strong>{studentDisplayName(pass)}</strong>
                  <small>{pass.period_label ?? "Unassigned period"} · {pass.status === "READY" ? "Ready to leave" : Boolean(pass.separation_waiting) ? "Waiting due to a restricted conflict" : pass.schedule_hold_until && new Date(pass.schedule_hold_until).getTime() > now ? `Held until ${formatDateAtTimeZone(pass.schedule_hold_until, state.class.timezone)}` : "Waiting"}</small>
                </div>
                <div className="waitlist-actions">
                  <button
                    className="button secondary small"
                    onClick={() => void passAction(pass.id, "override")}
                  >
                    Override
                  </button>
                  <button
                    className="button quiet small danger-button"
                    onClick={() => void passAction(pass.id, "cancel")}
                  >
                    Remove
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <MiniEmpty text="No one is waiting." />
        )}
      </section>
      <aside className="live-room-side">
        <TeacherApprovalPanel state={state} refresh={refresh} onError={onError} onSubstituteModeChange={(enabled) => void setSubstituteMode(enabled)} substituteModeChanging={substituteModeChanging} showWhenEmpty />
        <TodayBellScheduleCard
          classId={state.class.id}
          schedule={state.schedule}
          schoolSchedule={state.schoolSchedule}
          options={state.bellScheduleOptions}
          refresh={refresh}
          onError={onError}
        />
      </aside>
    </div>
  );
}

type TodayBellScheduleSelection = "SCHOOL_DEFAULT" | "NO_SCHEDULE" | string;

function TodayBellScheduleCard({
  classId,
  schedule,
  schoolSchedule,
  options,
  refresh,
  onError,
}: {
  classId: string;
  schedule: ClassState["schedule"];
  schoolSchedule: ClassState["schoolSchedule"];
  options: ClassState["bellScheduleOptions"];
  refresh: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const selection = schedule?.source === "TEACHER_NONE"
    ? "NO_SCHEDULE"
    : schedule?.source === "TEACHER_OVERRIDE" && schedule.scheduleId
      ? schedule.scheduleId
      : "SCHOOL_DEFAULT";
  const [chosen, setChosen] = useState<TodayBellScheduleSelection>(selection);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setChosen(selection);
  }, [selection]);

  const schoolDefaultLabel = schoolSchedule?.scheduleName
    ? `School calendar — ${schoolSchedule.scheduleName}`
    : schoolSchedule?.noSchool
      ? "School calendar — No school"
      : "School calendar — No schedule assigned";
  const activeLabel = schedule?.source === "TEACHER_NONE"
    ? "No bell schedule"
    : schedule?.scheduleName ?? (schedule?.noSchool ? "No school" : "No schedule assigned");
  const sourceLabel = schedule?.source === "TEACHER_OVERRIDE"
    ? "Teacher override for today"
    : schedule?.source === "TEACHER_NONE"
      ? "Teacher selected all-day behavior"
      : "School calendar for today";

  async function choose(next: TodayBellScheduleSelection) {
    const prior = chosen;
    setChosen(next);
    setSaving(true);
    try {
      await api(`/api/classes/${classId}/today-bell-schedule`, {
        method: "PUT",
        body: JSON.stringify(
          next === "SCHOOL_DEFAULT"
            ? { selection: "SCHOOL_DEFAULT" }
            : next === "NO_SCHEDULE"
              ? { selection: "NO_SCHEDULE" }
              : { selection: "BELL_SCHEDULE", bellScheduleId: next },
        ),
      });
      await refresh();
    } catch (caught) {
      setChosen(prior);
      onError(caught instanceof Error ? caught.message : "Today's bell schedule could not be saved");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="panel today-bell-schedule-card">
      <div className="panel-heading">
        <div><p className="eyebrow ink">Today’s school day</p><h2>Bell schedule</h2></div>
      </div>
      <strong className="today-bell-schedule-name">{activeLabel}</strong>
      <small className="today-bell-schedule-source">{sourceLabel}</small>
      <label className="today-bell-schedule-select">
        Use for today
        <select value={chosen} disabled={saving} onChange={(event) => void choose(event.target.value)}>
          <option value="SCHOOL_DEFAULT">{schoolDefaultLabel}</option>
          <option value="NO_SCHEDULE">No bell schedule</option>
          {options.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
        </select>
      </label>
      <p className="form-note">Set bell schedule to apply schedule-related features.</p>
    </section>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function MiniEmpty({ text }: { text: string }) {
  return (
    <div className="mini-empty">
      <span>✓</span>
      <p>{text}</p>
    </div>
  );
}

function SettingsCard({
  settings,
  schedule,
  onSaved,
  onError,
}: {
  settings: ClassState["class"];
  schedule: ClassState["schedule"];
  onSaved: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [capacity, setCapacity] = useState(settings.max_restroom_out);
  const [limit, setLimit] = useState(settings.pass_limit_minutes);
  const [warning, setWarning] = useState(settings.warning_minutes);
  const [timerColorsEnabled, setTimerColorsEnabled] = useState(Boolean(settings.timer_colors_enabled));
  const [timerNormalColor, setTimerNormalColor] = useState(settings.timer_normal_color);
  const [timerWarningColor, setTimerWarningColor] = useState(settings.timer_warning_color);
  const [timerAlertColor, setTimerAlertColor] = useState(settings.timer_alert_color);
  const [semesterPassLimit, setSemesterPassLimit] = useState(settings.semester_pass_limit?.toString() ?? "");
  const [emergencyPassLimit, setEmergencyPassLimit] = useState(settings.emergency_pass_limit?.toString() ?? "");
  const [scheduleRulesEnabled, setScheduleRulesEnabled] = useState(Boolean(settings.schedule_rules_enabled));
  const [scheduleHoldMinutes, setScheduleHoldMinutes] = useState(settings.schedule_hold_minutes);
  const [scheduleEndWarningMinutes, setScheduleEndWarningMinutes] = useState(settings.schedule_end_warning_minutes);
  const [scheduleNotReturnedMinutes, setScheduleNotReturnedMinutes] = useState(settings.schedule_not_returned_minutes);
  const [busy, setBusy] = useState<"period" | "all" | null>(null);

  useEffect(() => {
    setCapacity(settings.max_restroom_out);
    setLimit(settings.pass_limit_minutes);
    setWarning(settings.warning_minutes);
    setTimerColorsEnabled(Boolean(settings.timer_colors_enabled));
    setTimerNormalColor(settings.timer_normal_color);
    setTimerWarningColor(settings.timer_warning_color);
    setTimerAlertColor(settings.timer_alert_color);
    setSemesterPassLimit(settings.semester_pass_limit?.toString() ?? "");
    setEmergencyPassLimit(settings.emergency_pass_limit?.toString() ?? "");
    setScheduleRulesEnabled(Boolean(settings.schedule_rules_enabled));
    setScheduleHoldMinutes(settings.schedule_hold_minutes);
    setScheduleEndWarningMinutes(settings.schedule_end_warning_minutes);
    setScheduleNotReturnedMinutes(settings.schedule_not_returned_minutes);
  }, [settings.id, settings.max_restroom_out, settings.pass_limit_minutes, settings.warning_minutes, settings.timer_colors_enabled, settings.timer_normal_color, settings.timer_warning_color, settings.timer_alert_color, settings.semester_pass_limit, settings.emergency_pass_limit, settings.schedule_rules_enabled, settings.schedule_hold_minutes, settings.schedule_end_warning_minutes, settings.schedule_not_returned_minutes]);

  function rulePayload() {
    return {
      maxRestroomOut: capacity,
      passLimitMinutes: limit,
      warningMinutes: warning,
      timerColorsEnabled,
      timerNormalColor,
      timerWarningColor,
      timerAlertColor,
      semesterPassLimit: semesterPassLimit.trim() ? Number(semesterPassLimit) : null,
      emergencyPassLimit: emergencyPassLimit.trim() ? Number(emergencyPassLimit) : null,
      scheduleRulesEnabled,
      scheduleHoldMinutes,
      scheduleEndWarningMinutes,
      scheduleNotReturnedMinutes,
    };
  }

  async function save(scope: "period" | "all") {
    if (
      scope === "all"
      && !window.confirm(
        "Apply these pass rules to every active period for this teacher? Class names and period labels will not change.",
      )
    ) {
      return;
    }
    setBusy(scope);
    try {
      await api(
        scope === "all"
          ? `/api/classes/${settings.id}/rules/all-periods`
          : `/api/classes/${settings.id}`,
        {
          method: "PATCH",
          body: JSON.stringify(rulePayload()),
        },
      );
      await onSaved();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Pass rules could not be saved");
    } finally {
      setBusy(null);
    }
  }

  function savePeriod(event: FormEvent) {
    event.preventDefault();
    void save("period");
  }

  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow ink">This period</p>
          <h2>Pass rules</h2>
        </div>
      </div>
      <form className="settings-form" onSubmit={savePeriod}>
        <div className="pass-rule-fields">
          <label>
            Maximum restroom students out
            <input
              type="number"
              min="1"
              max="20"
              value={capacity}
              onChange={(event) => setCapacity(Number(event.target.value))}
            />
          </label>
          <label>
            Warn after
            <div className="input-suffix">
              <input
                type="number"
                min="0"
                max={Math.max(0, limit - 1)}
                value={warning}
                onChange={(event) => setWarning(Number(event.target.value))}
              />
              <span>min</span>
            </div>
          </label>
          <label>
            Restroom alert limit
            <div className="input-suffix">
              <input
                type="number"
                min="1"
                max="180"
                value={limit}
                onChange={(event) => setLimit(Number(event.target.value))}
              />
              <span>min</span>
            </div>
          </label>
        </div>
        <div className="timer-color-settings">
          <label className="check-row">
            <input type="checkbox" checked={timerColorsEnabled} onChange={(event) => setTimerColorsEnabled(event.target.checked)} />
            Use timer colors on the live room, kiosk, and display
          </label>
          <fieldset disabled={!timerColorsEnabled}>
            <legend>Timer colors</legend>
            <div className="timer-color-grid">
              <label>Active<input type="color" value={timerNormalColor} onChange={(event) => setTimerNormalColor(event.target.value)} /></label>
              <label>Warning<input type="color" value={timerWarningColor} onChange={(event) => setTimerWarningColor(event.target.value)} /></label>
              <label>Alert<input type="color" value={timerAlertColor} onChange={(event) => setTimerAlertColor(event.target.value)} /></label>
            </div>
          </fieldset>
          {!timerColorsEnabled && <p className="form-note">All active timers will appear black, including warning and alert states.</p>}
        </div>
        <div className="field-row">
          <label>
            Semester pass limit
            <input type="number" min="1" max="999" value={semesterPassLimit} onChange={(event) => setSemesterPassLimit(event.target.value)} placeholder="No alert limit" />
          </label>
          <label>
            Emergency pass limit
            <input type="number" min="1" max="999" value={emergencyPassLimit} onChange={(event) => setEmergencyPassLimit(event.target.value)} placeholder="No alert limit" />
          </label>
        </div>
        <details className="settings-info"><summary>How pass limits and alerts work</summary><p className="form-note">The warning and restroom alert limit change timer colors and create alerts; they do not remove a student. Every active pass has a fixed 45-minute not-returned timeout. When school-schedule rules are enabled, a restroom pass may instead be marked not returned sooner at the scheduled end-of-period cutoff. Saving a changed time or semester limit reassesses eligible historical passes and alerts. Emergency passes are activated only from the restroom waitlist.</p></details>
        <fieldset className="schedule-rule-settings">
          <legend>School bell schedule</legend>
          <label className="check-row"><input type="checkbox" checked={scheduleRulesEnabled} onChange={(event) => setScheduleRulesEnabled(event.target.checked)} />Use the school bell schedule for restroom requests</label>
          {schedule?.slot ? <p className="form-note">Available now: {schedule.slot.label} · {schedule.scheduleName ?? "School schedule"}. Timing follows the current school block, even when a student is temporarily visiting this classroom.</p> : schedule?.noSchool ? <p className="form-note">The school calendar marks today as no school. These rules will remain inactive today.</p> : schedule?.calendarAvailable ? <p className="form-note">A calendar is assigned today, but there is no instructional bell block right now. These rules apply only while a scheduled period is in progress.</p> : <p className="form-note">No school bell schedule is assigned for today. Enabling this retains normal pass behavior until a usable schedule is available.</p>}
          <div className="schedule-rule-fields">
            <label><span>Waitlist for first</span><input type="number" min="0" max="60" inputMode="numeric" disabled={!scheduleRulesEnabled} value={scheduleHoldMinutes} onChange={(event) => setScheduleHoldMinutes(Math.min(60, Number(event.target.value)))} /><small>minutes of class</small></label>
            <label><span>Emergency pass issued for last</span><input type="number" min="0" max="60" inputMode="numeric" disabled={!scheduleRulesEnabled} value={scheduleEndWarningMinutes} onChange={(event) => setScheduleEndWarningMinutes(Math.min(60, Number(event.target.value)))} /><small>minutes before class ends</small></label>
            <label><span>Mark not returned</span><input type="number" min="0" max="30" inputMode="numeric" disabled={!scheduleRulesEnabled} value={scheduleNotReturnedMinutes} onChange={(event) => setScheduleNotReturnedMinutes(Math.min(30, Number(event.target.value)))} /><small>minutes after the scheduled bell</small></label>
          </div>
        </fieldset>
        <div className="settings-save-actions">
          <button className="button secondary" disabled={busy !== null}>
            {busy === "period" ? "Saving…" : "Save to this period"}
          </button>
          <button type="button" className="button primary" disabled={busy !== null} onClick={() => void save("all")}>
            {busy === "all" ? "Saving…" : "Save to all periods"}
          </button>
        </div>
      </form>
    </section>
  );
}

interface RosterEntryDraft {
  studentNumber: string;
  firstName: string;
  lastName: string;
  preferredFirstName: string;
  preferredLastName: string;
}

function blankRosterEntry(): RosterEntryDraft {
  return { studentNumber: "", firstName: "", lastName: "", preferredFirstName: "", preferredLastName: "" };
}

function RosterPanel({
  classId,
  roster,
  refresh,
  onError,
  onViewHistory,
}: {
  classId: string;
  roster: Student[];
  refresh: () => Promise<void>;
  onError: (message: string) => void;
  onViewHistory: (studentId: string) => void;
}) {
  const [entryRows, setEntryRows] = useState<RosterEntryDraft[]>([blankRosterEntry()]);
  const [view, setView] = useState<"ROSTER" | "ADD">("ROSTER");
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState<Student | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const visible = roster.filter((student) =>
    `${student.student_number} ${student.first_name} ${student.last_name} ${student.preferred_first_name ?? ""} ${student.preferred_last_name ?? ""}`
      .toLowerCase()
      .includes(filter.toLowerCase()),
  );

  async function add(event: FormEvent) {
    event.preventDefault();
    const entries = entryRows.filter((entry) => Object.values(entry).some((value) => value.trim()));
    if (!entries.length) return onError("Add at least one student before submitting.");
    const incomplete = entries.find((entry) => !entry.studentNumber.trim() || !entry.firstName.trim() || !entry.lastName.trim());
    if (incomplete) return onError("Every student needs an ID, first name, and last name.");
    try {
      const quoteCsv = (value: string) => `"${value.replaceAll('"', '""')}"`;
      const csv = [
        "student_id,first_name,last_name,preferred_first_name,preferred_last_name",
        ...entries.map((entry) => [entry.studentNumber, entry.firstName, entry.lastName, entry.preferredFirstName, entry.preferredLastName].map(quoteCsv).join(",")),
      ].join("\n");
      await api(`/api/classes/${classId}/roster/import`, {
        method: "POST",
        body: JSON.stringify({ csv }),
      });
      setEntryRows([blankRosterEntry()]);
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Student could not be added");
    }
  }

  async function importCsv(file: File) {
    try {
      const csv = await file.text();
      const response = await api<{ imported: number }>(
        `/api/classes/${classId}/roster/import`,
        { method: "POST", body: JSON.stringify({ csv }) },
      );
      window.alert(`${response.imported} students imported.`);
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "CSV import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function remove(student: Student) {
    if (!window.confirm(`Remove ${student.first_name} ${student.last_name} from this roster?`)) return;
    try {
      await api(`/api/classes/${classId}/roster/${student.id}`, { method: "DELETE" });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Student could not be removed");
    }
  }

  async function setRestroomExemption(student: Student, restroomExempt: boolean) {
    try {
      await api(`/api/classes/${classId}/roster/${student.id}/restroom-exemption`, {
        method: "PATCH",
        body: JSON.stringify({ restroomExempt }),
      });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Rules exemption could not be updated");
    }
  }

  return (
    <div className="roster-workspace">
      <nav className="tabs" aria-label="Roster views"><button className={view === "ROSTER" ? "active" : ""} onClick={() => setView("ROSTER")}>Class roster</button><button className={view === "ADD" ? "active" : ""} onClick={() => setView("ADD")}>Add students</button></nav>
      {view === "ADD" && <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow ink">Manual entry</p>
            <h2>Add a student</h2>
          </div>
        </div>
        <form className="stack-form" onSubmit={add}>
          <div className="table-wrap roster-entry-table"><table><thead><tr><th>Student ID</th><th>First name</th><th>Last name</th><th>Preferred first name</th><th>Preferred last name</th><th><span className="sr-only">Remove row</span></th></tr></thead><tbody>{entryRows.map((entry, index) => <tr key={index}><td><input aria-label={`Student ID, row ${index + 1}`} value={entry.studentNumber} onChange={(event) => setEntryRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, studentNumber: event.target.value } : row))} /></td><td><input aria-label={`First name, row ${index + 1}`} value={entry.firstName} onChange={(event) => setEntryRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, firstName: event.target.value } : row))} /></td><td><input aria-label={`Last name, row ${index + 1}`} value={entry.lastName} onChange={(event) => setEntryRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, lastName: event.target.value } : row))} /></td><td><input aria-label={`Preferred first name, row ${index + 1}`} value={entry.preferredFirstName} onChange={(event) => setEntryRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, preferredFirstName: event.target.value } : row))} /></td><td><input aria-label={`Preferred last name, row ${index + 1}`} value={entry.preferredLastName} onChange={(event) => setEntryRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, preferredLastName: event.target.value } : row))} /></td><td><button type="button" className="icon-button" aria-label={`Remove row ${index + 1}`} disabled={entryRows.length === 1} onClick={() => setEntryRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}>×</button></td></tr>)}</tbody></table></div>
          <div className="roster-entry-actions"><button type="button" className="button quiet small" onClick={() => setEntryRows((current) => [...current, blankRosterEntry()])}>Add a row</button><button className="button primary">Add to roster</button></div>
        </form>
        <div className="divider"><span>or import</span></div>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void importCsv(file);
          }}
        />
        <button className="upload-zone" onClick={() => fileRef.current?.click()}>
          <strong>Choose a CSV file</strong>
          <span className="csv-field-list"><strong>Required:</strong><span>student_id<br />first_name<br />last_name</span><strong>Optional:</strong><span>preferred_first_name<br />preferred_last_name</span></span>
        </button>
        <a className="text-button" href="https://docs.google.com/spreadsheets/d/1DF1YIVaiPFuqlEySvEiFhKNXxAKEzpS1aJZTJ22UFgc/copy" target="_blank" rel="noreferrer">Get the Google Sheets CSV template</a>
      </section>}
      {view === "ROSTER" && <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow ink">{roster.length} enrolled</p>
            <h2>Class roster</h2>
          </div>
          <input
            className="search-input"
            placeholder="Search roster"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Student name</th><th>Preferred</th><th className="center-column">ID</th><th className="center-column" title="Exempt rules: exempts this student from pass-count limits, restroom-capacity maximums, maximum-time alerts, and kiosk over-limit confirmation.">Exempt rules</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {visible.map((student) => (
                <tr key={student.id}>
<td><strong>{student.last_name}, {student.first_name}</strong></td>
                  <td>{student.preferred_first_name ?? "—"}</td>
                  <td className="mono center-column">{student.student_number}</td>
                  <td className="center-column toggle-column">
                    <label className="check-row" title="Exempt rules: exempts this student from pass-count limits, restroom-capacity maximums, maximum-time alerts, and kiosk over-limit confirmation.">
                      <input
                        type="checkbox"
                        checked={Boolean(student.restroom_exempt)}
                        onChange={(event) => void setRestroomExemption(student, event.target.checked)}
                        aria-label={`Exempt rules for ${student.first_name} ${student.last_name}`}
                      />
                    </label>
                  </td>
                  <td>
                    <button className="text-button history-button" onClick={() => onViewHistory(student.id)}>View history</button>
                    <button className="text-button" onClick={() => setEditing(student)}>Edit</button>
                    <button className="text-button danger-text" onClick={() => void remove(student)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!visible.length && <MiniEmpty text="No students match your search." />}
        </div>
      </section>}
      {editing && (
        <StudentEditorDialog
          key={editing.id}
          classId={classId}
          student={editing}
          onClose={() => setEditing(null)}
          onUpdated={refresh}
          onError={onError}
        />
      )}
    </div>
  );
}

interface StudentImpact {
  student: Student;
  activeClassCount: number;
  activeTeacherCount: number;
  activeEnrollments: MoveTarget[];
}

interface MoveTarget {
  id: string;
  name: string;
  period_label: string;
  teacher_id: string;
  teacher_name: string;
}

interface AllPeriodsStudent extends Student {
  class_period_id: string;
  period_label: string;
  class_name: string;
}

function AllPeriodsRosterPanel({
  schoolId,
  onError,
}: {
  schoolId: string;
  onError: (message: string) => void;
}) {
  const [students, setStudents] = useState<AllPeriodsStudent[]>([]);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const response = await api<{ students: AllPeriodsStudent[] }>(
        `/api/classes/all/roster?${new URLSearchParams({ schoolId }).toString()}`,
      );
      setStudents(response.students);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "All-period roster could not be loaded");
    }
  }, [onError, schoolId]);

  useEffect(() => { void load(); }, [load]);

  const visible = students.filter((student) =>
    `${student.student_number} ${student.first_name} ${student.last_name} ${student.preferred_first_name ?? ""} ${student.preferred_last_name ?? ""} ${student.period_label} ${student.class_name}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <section className="panel">
      <div className="panel-heading">
        <div><p className="eyebrow ink">Teacher-wide roster</p><h2>All periods</h2></div>
        <input className="search-input" placeholder="Student, ID, or period" value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>
      <p className="form-note">This read-only view keeps each enrollment on its own row so students in different periods are never mixed together.</p>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Student name</th><th>Preferred</th><th className="center-column">ID</th><th>Period</th><th>Class</th></tr></thead>
          <tbody>
            {visible.map((student) => (
              <tr key={`${student.id}:${student.class_period_id}`}>
                <td><strong>{student.first_name} {student.last_name}</strong></td>
                <td>{student.preferred_first_name ?? "—"}</td>
                <td className="mono center-column">{student.student_number}</td>
                <td>{student.period_label}</td>
                <td>{student.class_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!visible.length && <MiniEmpty text="No enrolled students match this search." />}
      </div>
    </section>
  );
}

function StudentEditorDialog({
  classId,
  student,
  onClose,
  onUpdated,
  onError,
}: {
  classId: string;
  student: Student;
  onClose: () => void;
  onUpdated: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [studentNumber, setStudentNumber] = useState(student.student_number);
  const [firstName, setFirstName] = useState(student.first_name);
  const [lastName, setLastName] = useState(student.last_name);
  const [preferredFirstName, setPreferredFirstName] = useState(student.preferred_first_name ?? "");
  const [preferredLastName, setPreferredLastName] = useState(student.preferred_last_name ?? "");
  const [impact, setImpact] = useState<StudentImpact | null>(null);
  const [sourceClassId, setSourceClassId] = useState(classId);
  const [targets, setTargets] = useState<MoveTarget[]>([]);
  const [targetTeacherId, setTargetTeacherId] = useState("");
  const [targetClassId, setTargetClassId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let current = true;
    void (async () => {
      try {
        const impactResponse = await api<StudentImpact>(`/api/classes/${classId}/roster/${student.id}/impact`);
        if (!current) return;
        setImpact(impactResponse);
        const source = impactResponse.activeEnrollments.find((item) => item.id === classId) ?? impactResponse.activeEnrollments[0];
        setSourceClassId(source?.id ?? "");
      } catch (caught) {
        if (!current) return;
        const message = caught instanceof Error ? caught.message : "Student details could not be loaded";
        setError(message);
        onError(message);
      }
    })();
    return () => { current = false; };
  }, [classId, onError, student.id]);

  useEffect(() => {
    if (!sourceClassId) { setTargets([]); return; }
    let current = true;
    void api<{ targetClasses: MoveTarget[] }>(`/api/classes/${sourceClassId}/roster/move-targets`).then((response) => {
      if (!current) return;
      setTargets(response.targetClasses);
      setTargetClassId("");
      setTargetTeacherId((currentTeacherId) => response.targetClasses.some((item) => item.teacher_id === currentTeacherId) ? currentTeacherId : response.targetClasses[0]?.teacher_id ?? "");
    }).catch((caught) => {
      if (!current) return;
      const message = caught instanceof Error ? caught.message : "Destination periods could not be loaded";
      setError(message);
      onError(message);
    });
    return () => { current = false; };
  }, [onError, sourceClassId]);

  const targetTeachers = useMemo(() => targets.filter((entry, index, entries) => entries.findIndex((candidate) => candidate.teacher_id === entry.teacher_id) === index), [targets]);
  const targetPeriods = useMemo(() => targets.filter((entry) => entry.teacher_id === targetTeacherId), [targetTeacherId, targets]);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api(`/api/classes/${classId}/roster/${student.id}`, {
        method: "PATCH",
        body: JSON.stringify({ studentNumber, firstName, lastName, preferredFirstName, preferredLastName }),
      });
      await onUpdated();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Student information could not be saved");
    } finally {
      setBusy(false);
    }
  }

  async function moveStudent() {
    const source = impact?.activeEnrollments.find((item) => item.id === sourceClassId);
    const target = targets.find((item) => item.id === targetClassId);
    if (!source || !target) return;
    if (!window.confirm(`Move ${student.first_name} ${student.last_name} from ${source.period_label} - ${source.name} to ${target.period_label} - ${target.name}? Their pass history will remain unchanged.`)) return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/classes/${sourceClassId}/roster/${student.id}/move`, {
        method: "POST",
        body: JSON.stringify({ targetClassId }),
      });
      await onUpdated();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Student could not be moved");
    } finally {
      setBusy(false);
    }
  }

  const isShared = (impact?.activeClassCount ?? 0) > 1;
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="dialog account-dialog student-editor-dialog" onMouseDown={(event) => event.stopPropagation()}>
        <div className="panel-heading">
          <div><p className="eyebrow ink">Student record</p><h2>Edit {student.first_name} {student.last_name}</h2></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">Close</button>
        </div>
        {error && <div className="alert error">{error}</div>}
        <div className="student-profile-impact">
          <strong>{isShared ? "This is a shared school record" : "This is a school record"}</strong>
          {impact ? (
            <p>{isShared ? `This student is enrolled in ${impact.activeClassCount} active periods with ${impact.activeTeacherCount} teacher${impact.activeTeacherCount === 1 ? "" : "s"}. Saving registered or preferred names updates the profile everywhere at this school, but does not change any pass history.` : "Correcting this student's registered or preferred name keeps the same student record and pass history."}</p>
          ) : <p>Checking current enrollmentsâ€¦</p>}
        </div>
        <form className="stack-form" onSubmit={saveProfile}>
          <label>Student ID<input value={studentNumber} onChange={(event) => setStudentNumber(event.target.value)} required disabled={busy} /></label>
          <div className="field-row">
            <label>Registered first name<input value={firstName} onChange={(event) => setFirstName(event.target.value)} required disabled={busy} /></label>
            <label>Registered last name<input value={lastName} onChange={(event) => setLastName(event.target.value)} required disabled={busy} /></label>
          </div>
          <div className="field-row">
            <label>Preferred first name <small>Optional; used in student-facing displays.</small><input value={preferredFirstName} onChange={(event) => setPreferredFirstName(event.target.value)} disabled={busy} /></label>
            <label>Preferred last name <small>Optional; does not replace the registered name.</small><input value={preferredLastName} onChange={(event) => setPreferredLastName(event.target.value)} disabled={busy} /></label>
          </div>
          <div className="dialog-actions"><button type="button" className="button quiet" onClick={onClose} disabled={busy}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Savingâ€¦" : "Save corrections"}</button></div>
        </form>
        <section className="student-move">
          <div><p className="eyebrow ink">Schedule change</p><h3>Move to another period</h3><p className="form-note">Only the enrollment changes. Other teachers' rosters and all pass history remain intact.</p></div>
          {impact?.activeEnrollments.length ? (
            <div className="field-row move-controls">
              <label>From class<select value={sourceClassId} onChange={(event) => setSourceClassId(event.target.value)} disabled={busy}><option value="">Choose a current class</option>{impact.activeEnrollments.map((source) => <option key={source.id} value={source.id}>{source.teacher_name} - {source.period_label} - {source.name}</option>)}</select></label>
              <label>To teacher<select value={targetTeacherId} onChange={(event) => { setTargetTeacherId(event.target.value); setTargetClassId(""); }} disabled={busy || !targetTeachers.length}><option value="">{targetTeachers.length ? "Choose a destination teacher" : "No eligible destination teachers"}</option>{targetTeachers.map((target) => <option key={target.teacher_id} value={target.teacher_id}>{target.teacher_name}</option>)}</select></label>
              <label>To period<select value={targetClassId} onChange={(event) => setTargetClassId(event.target.value)} disabled={busy || !targetTeacherId || !targetPeriods.length}><option value="">{targetPeriods.length ? "Choose a destination period" : "No eligible destination periods"}</option>{targetPeriods.map((target) => <option key={target.id} value={target.id}>{target.period_label} - {target.name}</option>)}</select></label>
              <button type="button" className="button secondary" disabled={busy || !sourceClassId || !targetClassId} onClick={() => void moveStudent()}>Move student</button>
            </div>
          ) : <p className="form-note">There are no active class periods to move this student from.</p>}
        </section>
      </div>
    </div>
  );
}

type HistoryFilterMode = "DATE_RANGE" | "SEMESTER";

interface AcademicTerm {
  id: string;
  name: string;
  starts_on: string | null;
  ends_on: string | null;
  active: number;
}

interface StudentHistorySummary {
  student_id: string;
  student_number: string;
  first_name: string;
  last_name: string;
  preferred_first_name: string | null;
  preferred_last_name: string | null;
  class_period_id?: string;
  period_label?: string;
  class_name?: string;
  semester_pass_limit?: number | null;
  restroom_pass_count: number;
  duration_seconds: number;
  restroom_duration_seconds: number;
  office_duration_seconds: number;
  time_exceeded_count: number;
  emergency_pass_count: number;
  over_limit: boolean;
  over_semester_limit: boolean;
}

interface HistoryItem {
  id: string;
  category_id: string | null;
  student_number: string;
  first_name: string;
  last_name: string;
  preferred_first_name?: string | null;
  preferred_last_name?: string | null;
  reason_code: string;
  reason_label?: string;
  status: string;
  requested_at: string;
  approved_at: string | null;
  returned_at: string | null;
  due_at: string | null;
  timed_out: number;
  is_emergency: number;
  archived_at: string | null;
  duration_seconds: number;
}

interface PassTypeSummary {
  reason_code: string;
  reason_label?: string;
  pass_count: number;
  duration_seconds: number;
}

interface HistoryResponse {
  students: StudentHistorySummary[];
  terms: AcademicTerm[];
  mode: HistoryFilterMode;
  termId: string | null;
  startDate: string | null;
  endDate: string | null;
  semesterPassLimit: number | null;
  timezone: string;
}

interface StudentHistoryResponse {
  student: {
    id: string;
    student_number: string;
    first_name: string;
    last_name: string;
    preferred_first_name: string | null;
    preferred_last_name: string | null;
  };
  byType: PassTypeSummary[];
  metrics: {
    restroom_pass_count: number;
    time_exceeded_count: number;
    emergency_pass_count: number;
  };
  history: HistoryItem[];
  categories: PassCategory[];
  terms: AcademicTerm[];
  mode: HistoryFilterMode;
  termId: string | null;
  startDate: string | null;
  endDate: string | null;
  timezone: string;
}

interface PassListRecord extends HistoryItem {
  class_period_id: string | null;
  report_class_period_id: string;
  period_label: string;
  class_name: string;
}

interface PassListResponse {
  records: PassListRecord[];
  periods: Array<{ id: string; period_label: string; name: string }>;
  categories: PassCategory[];
  timezone: string;
}

function PassesHistoryList({ schoolId, classId, allPeriods, timeZone, onError, onRecordsAdded }: {
  schoolId: string; classId: string; allPeriods: boolean; timeZone: string;
  onError: (message: string) => void; onRecordsAdded?: () => void;
}) {
  const [periodId, setPeriodId] = useState(allPeriods ? "" : classId);
  const [startDate, setStartDate] = useState(() => dateInputAtTimeZone(timeZone));
  const [endDate, setEndDate] = useState(() => dateInputAtTimeZone(timeZone));
  const [report, setReport] = useState<PassListResponse | null>(null);
  const [editingRecord, setEditingRecord] = useState<PassListRecord | null>(null);

  useEffect(() => {
    setPeriodId(allPeriods ? "" : classId);
    const today = dateInputAtTimeZone(timeZone);
    setStartDate(today); setEndDate(today);
  }, [allPeriods, classId, schoolId, timeZone]);

  const load = useCallback(async () => {
    try {
      const query = new URLSearchParams({ schoolId, startDate, endDate });
      if (periodId) query.set("classPeriodId", periodId);
      setReport(await api<PassListResponse>(`/api/classes/all/pass-records?${query.toString()}`));
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Passes could not be loaded"); }
  }, [endDate, onError, periodId, schoolId, startDate]);

  useEffect(() => { void load(); }, [load]);
  const records = report?.records ?? [];
  return <>
    <div className="history-filters pass-list-filters">
      <label>Period<select value={periodId} onChange={(event) => setPeriodId(event.target.value)}><option value="">All periods</option>{(report?.periods ?? []).map((period) => <option key={period.id} value={period.id}>{period.period_label} · {period.name}</option>)}</select></label>
      <label>Start date<input type="date" value={startDate} max={endDate || undefined} onChange={(event) => setStartDate(event.target.value)} required /></label>
      <label>End date<input type="date" value={endDate} min={startDate || undefined} onChange={(event) => setEndDate(event.target.value)} required /></label>
    </div>
    <div className="table-wrap">
      <table><thead><tr><th>Student</th><th>Period</th><th>Pass type</th><th className="center-column">Emergency</th><th>Status</th><th>Date</th><th>Time</th><th className="center-column">Duration</th><th /></tr></thead><tbody>
        {records.map((item) => <tr key={item.id}>
          <td><strong>{item.first_name} {item.last_name}</strong><small>{item.student_number}</small></td>
          <td><strong>{item.period_label}</strong><small>{item.class_name}</small></td>
          <td>{item.reason_label ?? reasonLabels[item.reason_code] ?? item.reason_code}</td>
          <td className="center-column">{item.is_emergency ? <span className="status-chip emergency">Emergency</span> : "—"}</td>
          <td><span className={`status-chip ${historyStatusClass(item.status, item.timed_out)}`}>{historyStatusLabel(item.status, item.timed_out)}</span></td>
          <td>{formatDateOnlyAtTimeZone(item.requested_at, report?.timezone ?? timeZone)}</td><td>{formatTimestampTimeAtTimeZone(item.requested_at, report?.timezone ?? timeZone)}</td>
          <td className="mono center-column">{["CANCELLED", "PERIOD_ENDED"].includes(item.status) ? "—" : reportDuration(item.duration_seconds, Boolean(item.approved_at))}</td>
          <td>{isEditableHistoryRecord(item.status) && <button type="button" className="text-button" onClick={() => setEditingRecord(item)}>Edit</button>}</td>
        </tr>)}
      </tbody></table>
      {!records.length && <MiniEmpty text="No hall passes match these filters." />}
    </div>
    {editingRecord && <HistoryRecordDialog classId={editingRecord.report_class_period_id} timeZone={report?.timezone ?? timeZone} recordType="PASS" record={editingRecord} categories={report?.categories ?? []} onClose={() => setEditingRecord(null)} onSaved={() => { setEditingRecord(null); void load(); onRecordsAdded?.(); }} onError={onError} />}
  </>;
}

function reportDuration(seconds: number | null | undefined, hasTimedPass = true) {
  if (!hasTimedPass) return "—";
  return formatElapsedSeconds(Math.max(0, Number(seconds) || 0));
}

function historyStatusLabel(status: string, timedOut: number | boolean) {
  // A timeout is not a student return.  The database keeps RETURNED as the
  // terminal lifecycle value so capacity is released consistently, while the
  // reporting language says what actually happened to the teacher.
  return timedOut && status === "RETURNED" ? "Not returned" : status.replace("_", " ");
}

function historyStatusClass(status: string, timedOut: number | boolean) {
  return timedOut && status === "RETURNED" ? "not-returned" : status.toLowerCase();
}

function isEditableHistoryRecord(status: string) {
  return !["PENDING_APPROVAL", "ACTIVE", "WAITLISTED", "READY", "CANCELLED", "PERIOD_ENDED"].includes(status);
}

function historySelectionLabel(
  mode: HistoryFilterMode,
  startDate: string,
  endDate: string,
  terms: AcademicTerm[],
  termId: string,
) {
  if (mode === "SEMESTER") {
    return terms.find((term) => term.id === termId)?.name ?? "Semester";
  }
  if (!startDate || !endDate) return "Selected dates";
  return startDate === endDate
    ? formatDateInputTitle(startDate)
    : `${formatDateInputTitle(startDate)} to ${formatDateInputTitle(endDate)}`;
}

function HistoryPanel({
  classId,
  schoolId,
  allPeriods = false,
  historyTeacherId,
  timeZone,
  defaultTermId,
  focusStudentId,
  onFocusHandled,
  onOpenPeriod,
  onRecordsAdded,
  onError,
}: {
  classId: string;
  schoolId: string;
  allPeriods?: boolean;
  historyTeacherId?: string;
  timeZone: string;
  defaultTermId?: string | null;
  focusStudentId?: string | null;
  onFocusHandled?: () => void;
  onOpenPeriod?: (classId: string, studentId: string) => void;
  onRecordsAdded?: () => void;
  onError: (message: string) => void;
}) {
  const [report, setReport] = useState<HistoryResponse | null>(null);
  const [detail, setDetail] = useState<StudentHistoryResponse | null>(null);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<HistoryFilterMode>("SEMESTER");
  const [termId, setTermId] = useState(defaultTermId ?? "");
  const [startDate, setStartDate] = useState(() => dateInputAtTimeZone(timeZone));
  const [endDate, setEndDate] = useState(() => dateInputAtTimeZone(timeZone));
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [showRetroactive, setShowRetroactive] = useState(false);
  const [editingRecord, setEditingRecord] = useState<HistoryItem | null>(null);
  const [historyView, setHistoryView] = useState<"students" | "passes">("students");

  useEffect(() => {
    const today = dateInputAtTimeZone(timeZone);
    setStartDate(today);
    setEndDate(today);
    setTermId(defaultTermId ?? "");
    setSelectedStudentId("");
    setDetail(null);
  }, [classId, defaultTermId, schoolId, timeZone]);

  useEffect(() => {
    if (allPeriods) {
      setSelectedStudentId("");
      setDetail(null);
      return;
    }
    if (!focusStudentId) return;
    setSelectedStudentId(focusStudentId);
    setDetail(null);
    onFocusHandled?.();
  }, [allPeriods, focusStudentId, onFocusHandled]);

  const availableTerms = report?.terms ?? detail?.terms ?? [];
  const isArchivedTerm = mode === "SEMESTER"
    && Boolean(termId && availableTerms.some((term) => term.id === termId && !term.active));
  const reportAllPeriods = allPeriods || isArchivedTerm;

  const query = useMemo(() => {
    const parameters = new URLSearchParams({ mode });
    if (mode === "DATE_RANGE") {
      parameters.set("startDate", startDate);
      parameters.set("endDate", endDate);
    } else if (termId) {
      parameters.set("termId", termId);
    }
    if (reportAllPeriods) parameters.set("schoolId", schoolId);
    if (reportAllPeriods && historyTeacherId) parameters.set("teacherId", historyTeacherId);
    return parameters.toString();
  }, [endDate, historyTeacherId, mode, reportAllPeriods, schoolId, startDate, termId]);

  const load = useCallback(async () => {
    try {
      const parameters = new URLSearchParams(query);
      if (search.trim()) parameters.set("search", search.trim());
      const result = await api<HistoryResponse>(
        `/api/classes/${reportAllPeriods ? "all" : classId}/history?${parameters.toString()}`,
      );
      setReport(result);
      if (mode === "SEMESTER" && !termId && result.termId) setTermId(result.termId);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "History could not be loaded");
    }
  }, [classId, mode, onError, query, reportAllPeriods, search, termId]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  const loadDetail = useCallback(async () => {
    if (reportAllPeriods) {
      setDetail(null);
      return;
    }
    if (!selectedStudentId) {
      setDetail(null);
      return;
    }
    try {
      const result = await api<StudentHistoryResponse>(
        `/api/classes/${classId}/history/${selectedStudentId}?${query}`,
      );
      setDetail(result);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Student history could not be loaded");
    }
  }, [classId, onError, query, reportAllPeriods, selectedStudentId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  function chooseMode(nextMode: HistoryFilterMode) {
    setMode(nextMode);
    setSelectedStudentId("");
    setDetail(null);
    if (nextMode !== "SEMESTER") setTermId("");
  }

  const selectionLabel = historySelectionLabel(
    mode,
    startDate,
    endDate,
    report?.terms ?? detail?.terms ?? [],
    termId,
  );

  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow ink">Student-first reporting</p>
          <h2>{historyView === "passes" ? "Hall passes" : detail ? `${detail.student.first_name} ${detail.student.last_name}` : mode === "SEMESTER" ? "Semester pass history" : `Pass history: ${selectionLabel}`}</h2>
          {(detail?.student.preferred_first_name || detail?.student.preferred_last_name) && <p className="form-note">Preferred name: {studentDisplayName(detail.student)}</p>}
        </div>
        {historyView === "students" && !detail && <div className="panel-heading-actions"><input className="search-input" placeholder="Student name or ID" value={search} onChange={(event) => setSearch(event.target.value)} />{!reportAllPeriods && <button type="button" className="button quiet small" onClick={() => setShowRetroactive(true)}>Add past records</button>}</div>}
      </div>
      <nav className="tabs history-subtabs" aria-label="Pass history views"><button type="button" className={historyView === "students" ? "active" : ""} onClick={() => setHistoryView("students")}>Students</button><button type="button" className={historyView === "passes" ? "active" : ""} onClick={() => { setHistoryView("passes"); setSelectedStudentId(""); setDetail(null); }}>Passes</button></nav>
      {historyView === "passes" ? <PassesHistoryList schoolId={schoolId} classId={classId} allPeriods={allPeriods} timeZone={timeZone} onError={onError} onRecordsAdded={onRecordsAdded} /> : <>
      <div className="history-filters">
        <label>
          View
          <select value={mode} onChange={(event) => chooseMode(event.target.value as HistoryFilterMode)}>
            <option value="DATE_RANGE">Date range</option>
            <option value="SEMESTER">Semester</option>
          </select>
        </label>
        {mode === "DATE_RANGE" && <>
          <label>Start date<input type="date" value={startDate} max={endDate || undefined} onChange={(event) => setStartDate(event.target.value)} required /></label>
          <label>End date<input type="date" value={endDate} min={startDate || undefined} onChange={(event) => setEndDate(event.target.value)} required /></label>
        </>}
        {mode === "SEMESTER" && <label>Term<select value={termId} onChange={(event) => setTermId(event.target.value)}><option value="" disabled>Select term</option>{availableTerms.map((term) => <option key={term.id} value={term.id}>{term.name}{term.active ? "" : " (archived)"}</option>)}</select></label>}
        {detail && <button className="button quiet history-back" onClick={() => { setSelectedStudentId(""); setDetail(null); }}>← All students</button>}
      </div>
      {!detail && <div className="table-wrap">
        <table>
          <thead><tr><th>Student name</th><th>Preferred</th>{reportAllPeriods && <th>Period</th>}<th className="center-column">Restroom</th><th className="center-column">Emergencies</th><th className="center-column">Over time</th><th className="center-column">Restroom time</th><th className="center-column">Office time</th><th className="center-column">Total time</th><th /></tr></thead>
          <tbody>
            {(report?.students ?? []).map((student) => {
              const item = student as unknown as HistoryItem;
              const returnedAt = timestampMs(item.returned_at);
              const duration =
                item.approved_at && returnedAt !== null
                  ? elapsed(item.approved_at, returnedAt)
                  : "—";
              return (
                <tr key={`${student.student_id}:${student.class_period_id ?? "single"}`} className={student.over_limit ? "history-over-limit" : ""}>
                  <td><strong>{student.first_name} {student.last_name}</strong><small>{student.student_number}{student.over_limit ? " · Over category limit" : ""}</small></td>
                  <td>{student.preferred_first_name ?? "—"}</td>
                  {allPeriods && <td><strong>{student.period_label ?? "—"}</strong><small>{student.class_name ?? ""}</small></td>}
                  {isArchivedTerm && !allPeriods && <td><strong>{student.period_label ?? "No period"}</strong><small>{student.class_name ?? ""}</small></td>}
                  <td
                    className={cx("mono", "center-column", student.over_semester_limit && "semester-limit-exceeded")}
                    title={student.over_semester_limit ? "Exceeds the teacher's semester pass allowance" : undefined}
                  >
                    {student.restroom_pass_count}
                  </td>
                  <td className="mono center-column">{student.emergency_pass_count}</td>
                  <td className="mono center-column">{student.time_exceeded_count}</td>
                  <td className="mono center-column">{reportDuration(student.restroom_duration_seconds)}</td>
                  <td className="mono center-column">{reportDuration(student.office_duration_seconds)}</td>
                  <td className="mono center-column">{reportDuration(student.duration_seconds)}</td>
                  <td>{isArchivedTerm ? "Archived report" : <button className="text-button" onClick={() => {
                    if (reportAllPeriods && student.class_period_id) onOpenPeriod?.(student.class_period_id, student.student_id);
                    else setSelectedStudentId(student.student_id);
                  }}>View history</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!(report?.students.length) && <MiniEmpty text="No pass records found for this selection." />}
      </div>}
      {detail && (
        <div className="history-detail">
          <div className="student-history-metrics">
            <Metric value={String(detail.metrics.restroom_pass_count)} label="Restroom passes" />
            <Metric value={String(detail.metrics.time_exceeded_count)} label="Restroom time exceeded" />
            <Metric value={String(detail.metrics.emergency_pass_count)} label="Emergency passes" />
          </div>
          <div className="history-detail-heading">
            <div><p className="eyebrow ink">Pass type totals</p><h3>Summary by type</h3></div>
            <span>{selectionLabel} · {detail.student.student_number}</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Pass type</th><th className="center-column">Completed passes</th><th className="center-column">Total time</th></tr></thead>
              <tbody>
                {detail.byType.map((item) => (
                  <tr key={item.reason_code}>
                    <td><strong>{item.reason_label ?? reasonLabels[item.reason_code] ?? item.reason_code}</strong></td>
                    <td className="mono center-column">{item.pass_count}</td>
                    <td className="mono center-column">{reportDuration(item.duration_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="history-detail-heading history-records-heading">
            <div><p className="eyebrow ink">Individual pass records</p><h3>Full history</h3></div>
            <span>{detail.history.length} {detail.history.length === 1 ? "record" : "records"}</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Pass type</th><th aria-label="Edit record" /><th className="center-column">Emergency</th><th>Status</th><th>Date</th><th>Time</th><th className="center-column">Duration</th></tr></thead>
              <tbody>
                {detail.history.map((item) => (
                  <tr key={item.id}>
                    <td>{item.reason_label ?? reasonLabels[item.reason_code] ?? item.reason_code}</td>
                    <td>{isEditableHistoryRecord(item.status) && <button type="button" className="button quiet small" onClick={() => setEditingRecord(item)}>Edit</button>}</td>
                    <td className="center-column">{item.is_emergency ? <span className="status-chip emergency">Emergency</span> : "—"}</td>
                    <td><span className={`status-chip ${historyStatusClass(item.status, item.timed_out)}`}>{historyStatusLabel(item.status, item.timed_out)}</span></td>
                    <td>{formatDateOnlyAtTimeZone(item.requested_at, detail.timezone || timeZone)}</td>
                    <td>{formatTimestampTimeAtTimeZone(item.requested_at, detail.timezone || timeZone)}</td>
                    <td className="mono center-column">{["CANCELLED", "PERIOD_ENDED"].includes(item.status) ? "—" : reportDuration(item.duration_seconds, Boolean(item.approved_at))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!detail.history.length && <MiniEmpty text="No pass records found for this student." />}
          </div>
        </div>
      )}
      </>}
      {showRetroactive && <RetroactiveRecordDialog classId={classId} timeZone={timeZone} initialType="PASS" onClose={() => setShowRetroactive(false)} onCreated={() => { setShowRetroactive(false); onRecordsAdded?.(); }} />}
      {editingRecord && <HistoryRecordDialog classId={classId} timeZone={detail?.timezone || timeZone} recordType="PASS" record={editingRecord} categories={detail?.categories ?? []} onClose={() => setEditingRecord(null)} onSaved={() => { setEditingRecord(null); void load(); void loadDetail(); onRecordsAdded?.(); }} onError={onError} />}
    </section>
  );
}

interface TardyStudent {
  student_id: string;
  student_number: string;
  first_name: string;
  last_name: string;
  preferred_first_name: string | null;
  preferred_last_name: string | null;
  class_period_id?: string;
  period_label?: string;
  class_name?: string;
  tardy_count: number;
  last_tardy_at: string | null;
}

interface TardyRecord {
  id: string;
  category_id: string | null;
  reason_code: string;
  reason_label?: string;
  requested_at: string;
}

type TardyFilterMode = "DATE_RANGE" | "SEMESTER";

interface TardyResponse {
  students: TardyStudent[];
  semesterRoster: TardyStudent[];
  semesterTerm: { id: string | null; name: string } | null;
  terms: AcademicTerm[];
  termId: string | null;
  mode: TardyFilterMode;
  startDate: string | null;
  endDate: string | null;
  timezone: string;
}

interface TardyDetailResponse {
  student: {
    id: string;
    student_number: string;
    first_name: string;
    last_name: string;
    preferred_first_name: string | null;
    preferred_last_name: string | null;
  };
  records: TardyRecord[];
  categories: PassCategory[];
  terms: AcademicTerm[];
  termId: string | null;
  mode: TardyFilterMode;
  startDate: string | null;
  endDate: string | null;
  timezone: string;
}

function TardyPanel({
  classId,
  schoolId,
  allPeriods = false,
  timeZone,
  defaultTermId,
  defaultMode = "DATE_RANGE",
  focusStudent,
  onFocusHandled,
  onOpenPeriod,
  onRecordsAdded,
  onError,
}: {
  classId: string;
  schoolId: string;
  allPeriods?: boolean;
  timeZone: string;
  defaultTermId?: string | null;
  defaultMode?: TardyFilterMode;
  focusStudent?: TardyFocus | null;
  onFocusHandled?: () => void;
  onOpenPeriod?: (classId: string, studentId: string, scope: TardyDetailScope) => void;
  onRecordsAdded?: () => void;
  onError: (message: string) => void;
}) {
  const [students, setStudents] = useState<TardyStudent[]>([]);
  const [semesterRoster, setSemesterRoster] = useState<TardyStudent[]>([]);
  const [semesterTerm, setSemesterTerm] = useState<{ id: string | null; name: string } | null>(null);
  const [terms, setTerms] = useState<AcademicTerm[]>([]);
  const [detail, setDetail] = useState<TardyDetailResponse | null>(null);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<TardyFilterMode>(defaultMode);
  const [startDate, setStartDate] = useState(() => dateInputAtTimeZone(timeZone));
  const [endDate, setEndDate] = useState(() => dateInputAtTimeZone(timeZone));
  const [termId, setTermId] = useState(defaultTermId ?? "");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [detailScope, setDetailScope] = useState<TardyDetailScope>("FILTER");
  const [showRetroactive, setShowRetroactive] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TardyRecord | null>(null);

  useEffect(() => {
    const today = dateInputAtTimeZone(timeZone);
    setStartDate(today);
    setEndDate(today);
    setTermId(defaultTermId ?? "");
    setMode(defaultMode);
    setSelectedStudentId("");
    setDetail(null);
  }, [classId, defaultMode, defaultTermId, schoolId, timeZone]);

  useEffect(() => {
    if (allPeriods) {
      setSelectedStudentId("");
      setDetail(null);
      return;
    }
    if (!focusStudent) return;
    setSelectedStudentId(focusStudent.studentId);
    setDetailScope(focusStudent.scope);
    setDetail(null);
    onFocusHandled?.();
  }, [allPeriods, focusStudent, onFocusHandled]);

  const isArchivedTerm = mode === "SEMESTER"
    && Boolean(termId && terms.some((term) => term.id === termId && !term.active));
  const reportAllPeriods = allPeriods || isArchivedTerm;

  const query = useMemo(() => {
    const parameters = new URLSearchParams({ mode });
    if (mode === "DATE_RANGE") {
      parameters.set("startDate", startDate);
      parameters.set("endDate", endDate);
    } else if (termId) {
      parameters.set("termId", termId);
    }
    if (reportAllPeriods) parameters.set("schoolId", schoolId);
    return parameters.toString();
  }, [endDate, mode, reportAllPeriods, schoolId, startDate, termId]);

  const load = useCallback(async () => {
    try {
      const parameters = new URLSearchParams(query);
      if (search.trim()) parameters.set("search", search.trim());
      const response = await api<TardyResponse>(
        `/api/classes/${reportAllPeriods ? "all" : classId}/tardies?${parameters.toString()}`,
      );
      setStudents(response.students);
      setSemesterRoster(response.semesterRoster);
      setSemesterTerm(response.semesterTerm);
      setTerms(response.terms);
      if (mode === "SEMESTER" && !termId && response.termId) setTermId(response.termId);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Tardies could not be loaded");
    }
  }, [classId, mode, onError, query, reportAllPeriods, search, termId]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  const loadDetail = useCallback(async () => {
    if (reportAllPeriods || !selectedStudentId) {
      setDetail(null);
      return;
    }
    try {
      const detailQuery = detailScope === "SEMESTER"
        ? new URLSearchParams({
          mode: "SEMESTER",
          ...(semesterTerm?.id ? { termId: semesterTerm.id } : {}),
        }).toString()
        : query;
      const result = await api<TardyDetailResponse>(
        `/api/classes/${classId}/tardies/${selectedStudentId}?${detailQuery}`,
      );
      setDetail(result);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Tardy history could not be loaded");
    }
  }, [classId, detailScope, onError, query, reportAllPeriods, selectedStudentId, semesterTerm]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  function chooseMode(nextMode: TardyFilterMode) {
    setMode(nextMode);
    setSelectedStudentId("");
    setDetailScope("FILTER");
    setDetail(null);
    if (nextMode !== "SEMESTER") setTermId("");
  }

  return (
    <section className="panel">
      <div className="panel-heading">
        <div><p className="eyebrow ink">Separate attendance log</p><h2>{detail ? `${detail.student.first_name} ${detail.student.last_name}` : mode === "DATE_RANGE" ? startDate === endDate ? `Tardies for ${startDate ? formatDateInputTitle(startDate) : "selected day"}` : `Tardies from ${startDate ? formatDateInputTitle(startDate) : "start date"} to ${endDate ? formatDateInputTitle(endDate) : "end date"}` : "Semester tardies"}</h2>{(detail?.student.preferred_first_name || detail?.student.preferred_last_name) && <p className="form-note">Preferred name: {studentDisplayName(detail.student)}</p>}</div>
        {!detail && <div className="panel-heading-actions"><input className="search-input" placeholder="Student name or ID" value={search} onChange={(event) => setSearch(event.target.value)} />{!reportAllPeriods && <button type="button" className="button quiet small" onClick={() => setShowRetroactive(true)}>Add past records</button>}</div>}
      </div>
      <div className="history-filters">
        <label>View<select value={mode} onChange={(event) => chooseMode(event.target.value as TardyFilterMode)}><option value="DATE_RANGE">Date range</option><option value="SEMESTER">Semester</option></select></label>
        {mode === "DATE_RANGE" && <>
          <label>Start date<input type="date" value={startDate} max={endDate || undefined} onChange={(event) => setStartDate(event.target.value)} required /></label>
          <label>End date<input type="date" value={endDate} min={startDate || undefined} onChange={(event) => setEndDate(event.target.value)} required /></label>
        </>}
        {mode === "SEMESTER" && <label>Term<select value={termId} onChange={(event) => setTermId(event.target.value)}><option value="" disabled>Select term</option>{terms.map((term) => <option key={term.id} value={term.id}>{term.name}{term.active ? "" : " (archived)"}</option>)}</select></label>}
        {detail && <button className="button quiet history-back" onClick={() => { setSelectedStudentId(""); setDetailScope("FILTER"); setDetail(null); }}>← All students</button>}
      </div>
      {!detail && <>
        <div className="history-detail-heading">
          <div><p className="eyebrow ink">Selected time period</p><h3>Tardies in this selection</h3></div>
          <span>{students.length} {students.length === 1 ? "student" : "students"}</span>
        </div>
        <div className="table-wrap">
          <table>
          <thead><tr><th>Student name</th><th>Preferred</th>{reportAllPeriods && <th>Period</th>}<th className="center-column">Total tardies</th><th>Most recent</th><th /></tr></thead>
            <tbody>
              {students.map((student) => (
                <tr key={`${student.student_id}:${student.class_period_id ?? "single"}`}>
                  <td><strong>{student.first_name} {student.last_name}</strong><small>{student.student_number}</small></td>
                  <td>{student.preferred_first_name ?? "—"}</td>
                  {allPeriods && <td><strong>{student.period_label ?? "—"}</strong><small>{student.class_name ?? ""}</small></td>}
                  {isArchivedTerm && !allPeriods && <td><strong>{student.period_label ?? "No period"}</strong><small>{student.class_name ?? ""}</small></td>}
                  <td className="mono center-column">{student.tardy_count}</td>
                  <td>{formatDateAtTimeZone(student.last_tardy_at, timeZone)}</td>
                  <td>{isArchivedTerm ? "Archived report" : <button className="text-button" onClick={() => {
                    if (reportAllPeriods && student.class_period_id) onOpenPeriod?.(student.class_period_id, student.student_id, "FILTER");
                    else { setSelectedStudentId(student.student_id); setDetailScope("FILTER"); }
                  }}>View tardy history</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!students.length && <MiniEmpty text="No tardies were recorded in this selected time period." />}
        </div>
        {!isArchivedTerm && <>
        <div className="history-detail-heading history-records-heading">
          <div><p className="eyebrow ink">Full active roster</p><h3>{semesterTerm?.name ?? "Current semester"} tardies</h3></div>
          <span>{semesterRoster.length} {semesterRoster.length === 1 ? "student" : "students"}</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Student name</th><th>Preferred</th>{reportAllPeriods && <th>Period</th>}<th className="center-column">Semester tardies</th><th>Most recent</th><th /></tr></thead>
            <tbody>
              {semesterRoster.map((student) => (
                <tr key={`semester:${student.student_id}:${student.class_period_id ?? "single"}`}>
                  <td><strong>{student.first_name} {student.last_name}</strong><small>{student.student_number}</small></td>
                  <td>{student.preferred_first_name ?? "—"}</td>
                  {allPeriods && <td><strong>{student.period_label ?? "—"}</strong><small>{student.class_name ?? ""}</small></td>}
                  <td className="mono center-column">{student.tardy_count}</td>
                  <td>{formatDateAtTimeZone(student.last_tardy_at, timeZone)}</td>
                  <td><button className="text-button" onClick={() => {
                    if (allPeriods && student.class_period_id) onOpenPeriod?.(student.class_period_id, student.student_id, "SEMESTER");
                    else { setSelectedStudentId(student.student_id); setDetailScope("SEMESTER"); }
                  }}>View tardy history</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!semesterRoster.length && <MiniEmpty text={semesterTerm ? "No students are enrolled in this class." : "Set an active academic semester to view the full roster."} />}
        </div>
        </>}
      </>}
      {detail && <div className="history-detail">
        <div className="student-history-metrics">
          <Metric value={String(detail.records.length)} label={detailScope === "SEMESTER" ? "Tardies this semester" : "Tardies in this selection"} />
        </div>
        <div className="history-detail-heading history-records-heading">
          <div><p className="eyebrow ink">Every recorded arrival</p><h3>{detailScope === "SEMESTER" ? "Semester tardy history" : "Tardy history"}</h3></div>
          <span>{detail.records.length} {detail.records.length === 1 ? "record" : "records"}</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Category</th><th>Recorded</th></tr></thead>
            <tbody>
              {detail.records.map((record) => (
                <tr key={record.id}>
                  <td>{record.reason_label ?? "Tardy"}</td>
                  <td>{formatDateAtTimeZone(record.requested_at, detail.timezone || timeZone)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!detail.records.length && <MiniEmpty text={detailScope === "SEMESTER" ? "No tardies were recorded for this student this semester." : "No tardies were recorded for this student in the selected time period."} />}
        </div>
        {!!detail.records.length && <div className="history-record-actions">{detail.records.map((record) => <button type="button" className="button quiet small" key={`edit-${record.id}`} onClick={() => setEditingRecord(record)}>Edit</button>)}</div>}
      </div>}
      {showRetroactive && <RetroactiveRecordDialog classId={classId} timeZone={timeZone} initialType="TARDY" onClose={() => setShowRetroactive(false)} onCreated={() => { setShowRetroactive(false); onRecordsAdded?.(); }} />}
      {editingRecord && <HistoryRecordDialog classId={classId} timeZone={detail?.timezone || timeZone} recordType="TARDY" record={editingRecord} categories={detail?.categories ?? []} onClose={() => setEditingRecord(null)} onSaved={() => { setEditingRecord(null); void load(); void loadDetail(); onRecordsAdded?.(); }} onError={onError} />}
    </section>
  );
}

interface TimeExceededAlert {
  pass_id: string;
  class_period_id: string;
  student_id: string;
  student_number: string;
  first_name: string;
  last_name: string;
  reason_label: string;
  approved_at: string;
  returned_at: string | null;
  duration_seconds: number;
  limit_minutes: number;
  period_label: string;
  violation_key: string;
}

interface CountExceededAlert {
  class_period_id: string;
  student_id: string;
  student_number: string;
  first_name: string;
  last_name: string;
  pass_count: number;
  limit: number;
  period_label: string;
  term_id: string | null;
  term_name: string | null;
  violation_key: string;
}

interface AlertsResponse {
  timeExceeded: TimeExceededAlert[];
  passLimitExceeded: CountExceededAlert[];
  emergencyLimitExceeded: CountExceededAlert[];
  term: { id: string; name: string | null } | null;
}

function totalAlerts(alerts: AlertsResponse) {
  return alerts.timeExceeded.length + alerts.passLimitExceeded.length + alerts.emergencyLimitExceeded.length;
}

function AlertsPanel({
  classId,
  timeZone,
  onError,
  onCountChanged,
  onOpenStudent,
}: {
  classId: string;
  timeZone: string;
  onError: (message: string) => void;
  onCountChanged: (count: number) => void;
  onOpenStudent: (classId: string, studentId: string) => void;
}) {
  const [alerts, setAlerts] = useState<AlertsResponse | null>(null);
  const [clearingType, setClearingType] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api<AlertsResponse>(`/api/classes/${classId}/alerts`);
      setAlerts(response);
      onCountChanged(totalAlerts(response));
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Alerts could not be loaded");
    }
  }, [classId, onCountChanged, onError]);
  useEffect(() => { void load(); }, [load]);

  async function dismiss(
    alertType: "TIME_EXCEEDED" | "PASS_LIMIT_EXCEEDED" | "EMERGENCY_LIMIT_EXCEEDED",
    violationKey: string,
    studentId: string,
  ) {
    try {
      await api(`/api/classes/${classId}/alerts/dismiss`, {
        method: "POST",
        body: JSON.stringify({ alertType, violationKey, studentId }),
      });
      await load();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Alert could not be cleared");
    }
  }

  async function dismissAll(
    alertType: "TIME_EXCEEDED" | "PASS_LIMIT_EXCEEDED" | "EMERGENCY_LIMIT_EXCEEDED",
    items: Array<{ violation_key: string; student_id: string }>,
  ) {
    if (!items.length) return;
    setClearingType(alertType);
    try {
      await api(`/api/classes/${classId}/alerts/dismiss-all`, {
        method: "POST",
        body: JSON.stringify({
          alertType,
          alerts: items.map((item) => ({
            violationKey: item.violation_key,
            studentId: item.student_id,
          })),
        }),
      });
      await load();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Alerts could not be cleared");
    } finally {
      setClearingType(null);
    }
  }

  const total = alerts ? totalAlerts(alerts) : 0;
  return (
    <div className="alerts-panel">
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">All-period monitoring</p><h2>Alerts</h2><p className="form-note">This page includes every active class period. Clearing an alert acknowledges this specific violation; a new qualifying pass or threshold crossing creates a new alert.</p></div><span className="count-badge">{total}</span></div>
        <div className="alert-sections">
          <div className="alert-section time-alerts">
            <div className="history-detail-heading"><div><p className="eyebrow ink">1. Time exceeded</p><h3>Restroom passes over the time limit</h3></div><div className="alert-heading-actions"><span>{alerts?.timeExceeded.length ?? 0}</span>{Boolean(alerts?.timeExceeded.length) && <button className="text-button" disabled={clearingType === "TIME_EXCEEDED"} onClick={() => void dismissAll("TIME_EXCEEDED", alerts?.timeExceeded ?? [])}>Clear all</button>}</div></div>
            {(alerts?.timeExceeded ?? []).map((alert) => (
              <article key={alert.violation_key}>
                <div><button className="alert-student-link" onClick={() => onOpenStudent(alert.class_period_id, alert.student_id)}>{alert.first_name} {alert.last_name}</button><small>{alert.period_label} · {alert.reason_label}</small></div>
                <p>{reportDuration(alert.duration_seconds)} out; limit {alert.limit_minutes} min. {alert.returned_at ? "Returned" : "Still out"}.</p>
                <button className="text-button" onClick={() => void dismiss("TIME_EXCEEDED", alert.violation_key, alert.student_id)}>Clear</button>
              </article>
            ))}
            {!alerts?.timeExceeded.length && <MiniEmpty text="No restroom time alerts." />}
          </div>
          <div className="alert-section pass-limit-alerts">
            <div className="history-detail-heading"><div><p className="eyebrow ink">2. Number of passes exceeded</p><h3>Semester pass limit</h3></div><div className="alert-heading-actions"><span>{alerts?.passLimitExceeded.length ?? 0}</span>{Boolean(alerts?.passLimitExceeded.length) && <button className="text-button" disabled={clearingType === "PASS_LIMIT_EXCEEDED"} onClick={() => void dismissAll("PASS_LIMIT_EXCEEDED", alerts?.passLimitExceeded ?? [])}>Clear all</button>}</div></div>
            {(alerts?.passLimitExceeded ?? []).map((alert) => (
              <article key={alert.violation_key}>
                <div><button className="alert-student-link" onClick={() => onOpenStudent(alert.class_period_id, alert.student_id)}>{alert.first_name} {alert.last_name}</button><small>{alert.period_label} · {alert.term_name ?? "No active term"}</small></div>
                <p>{alert.pass_count} passes this semester; limit {alert.limit}.</p>
                <button className="text-button" onClick={() => void dismiss("PASS_LIMIT_EXCEEDED", alert.violation_key, alert.student_id)}>Clear</button>
              </article>
            ))}
            {!alerts?.passLimitExceeded.length && <MiniEmpty text="No semester pass-limit alerts." />}
          </div>
          <div className="alert-section emergency-alerts">
            <div className="history-detail-heading"><div><p className="eyebrow ink">3. Emergency passes used</p><h3>Semester emergency limit</h3></div><div className="alert-heading-actions"><span>{alerts?.emergencyLimitExceeded.length ?? 0}</span>{Boolean(alerts?.emergencyLimitExceeded.length) && <button className="text-button" disabled={clearingType === "EMERGENCY_LIMIT_EXCEEDED"} onClick={() => void dismissAll("EMERGENCY_LIMIT_EXCEEDED", alerts?.emergencyLimitExceeded ?? [])}>Clear all</button>}</div></div>
            {(alerts?.emergencyLimitExceeded ?? []).map((alert) => (
              <article key={alert.violation_key}>
                <div><button className="alert-student-link" onClick={() => onOpenStudent(alert.class_period_id, alert.student_id)}>{alert.first_name} {alert.last_name}</button><small>{alert.period_label} · {alert.term_name ?? "No active term"}</small></div>
                <p>{alert.pass_count} emergency passes this semester; limit {alert.limit}.</p>
                <button className="text-button" onClick={() => void dismiss("EMERGENCY_LIMIT_EXCEEDED", alert.violation_key, alert.student_id)}>Clear</button>
              </article>
            ))}
            {!alerts?.emergencyLimitExceeded.length && <MiniEmpty text="No emergency-pass alerts." />}
          </div>
        </div>
      </section>
    </div>
  );
}

function TeacherSettingsPanel({
  classId,
  settings,
  schedule,
  refresh,
  onError,
  onSettingsSaved,
}: {
  classId: string;
  settings: ClassState["class"];
  schedule: ClassState["schedule"];
  refresh: () => Promise<void>;
  onError: (message: string) => void;
  onSettingsSaved: () => Promise<void>;
}) {
  return (
    <div className="settings-page">
      <div className="settings-page-heading"><div><p className="eyebrow ink">Teacher settings</p><h2>Pass rules & categories</h2><p>These category rules apply to your kiosk and all of your classroom periods at this school.</p></div></div>
      <div className="teacher-settings-layout">
        <SettingsCard settings={settings} schedule={schedule} onSaved={onSettingsSaved} onError={onError} />
        <CategorySettingsPanel classId={classId} refresh={refresh} onError={onError} />
      </div>
    </div>
  );
}

function CategorySettingsPanel({
  classId,
  refresh,
  onError,
}: {
  classId: string;
  refresh: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [categories, setCategories] = useState<PassCategory[]>([]);
  const [editing, setEditing] = useState<PassCategory | null>(null);
  const [creating, setCreating] = useState(false);
  const [restoringDefaults, setRestoringDefaults] = useState(false);
  const [maskOtherDestinations, setMaskOtherDestinations] = useState(false);
  const [rosterNamePickerEnabled, setRosterNamePickerEnabled] = useState(true);
  const [otherDestinationLabel, setOtherDestinationLabel] = useState("Office");
  const [otherDestinationSymbol, setOtherDestinationSymbol] = useState("🏢");

  const load = useCallback(async () => {
    try {
      const response = await api<{ categories: PassCategory[]; maskOtherDestinations: boolean; rosterNamePickerEnabled: boolean; otherDestinationLabel: string; otherDestinationSymbol: string }>(`/api/classes/${classId}/categories`);
      setCategories(response.categories);
      setMaskOtherDestinations(response.maskOtherDestinations);
      setRosterNamePickerEnabled(response.rosterNamePickerEnabled);
      setOtherDestinationLabel(response.otherDestinationLabel);
      setOtherDestinationSymbol(response.otherDestinationSymbol);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Pass categories could not be loaded");
    }
  }, [classId, onError]);
  useEffect(() => { void load(); }, [load]);

  async function restoreDefaults() {
    if (!window.confirm("Restore the standard pass categories? This removes your current category choices and settings. Existing pass history will remain unchanged.")) return;
    setRestoringDefaults(true);
    try {
      await api(`/api/classes/${classId}/categories/restore-defaults`, { method: "POST" });
      await load();
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Default categories could not be restored");
    } finally {
      setRestoringDefaults(false);
    }
  }

  async function updatePrivacy(nextValue: boolean) {
    setMaskOtherDestinations(nextValue);
    try {
      await api(`/api/classes/${classId}/categories/privacy`, { method: "PATCH", body: JSON.stringify({ maskOtherDestinations: nextValue }) });
      await refresh();
    } catch (caught) {
      setMaskOtherDestinations(!nextValue);
      onError(caught instanceof Error ? caught.message : "Kiosk privacy setting could not be saved");
    }
  }

  async function updateRosterPicker(nextValue: boolean) {
    setRosterNamePickerEnabled(nextValue);
    try { await api(`/api/classes/${classId}/categories/privacy`, { method: "PATCH", body: JSON.stringify({ rosterNamePickerEnabled: nextValue }) }); await refresh(); }
    catch (caught) { setRosterNamePickerEnabled(!nextValue); onError(caught instanceof Error ? caught.message : "Name picker setting could not be saved"); }
  }

  async function saveOtherDestinationPresentation() {
    try {
      await api(`/api/classes/${classId}/categories/privacy`, { method: "PATCH", body: JSON.stringify({ otherDestinationLabel, otherDestinationSymbol }) });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Generic kiosk display could not be saved");
    }
  }

  return (
    <section className="panel category-settings">
      <div className="panel-heading">
        <div><p className="eyebrow ink">Kiosk choices</p><h2>Pass categories</h2></div>
        <div className="panel-heading-actions">
          <button className="button quiet small" onClick={() => void restoreDefaults()} disabled={restoringDefaults}>
            {restoringDefaults ? "Restoring…" : "Restore defaults"}
          </button>
          <button className="button quiet small" onClick={() => setCreating(true)}>+ Add category</button>
        </div>
      </div>
      <label className="check-row"><input type="checkbox" checked={maskOtherDestinations} onChange={(event) => void updatePrivacy(event.target.checked)} />Show “Other” destinations as Office 🏢 on the kiosk&apos;s Currently out list</label>
      {maskOtherDestinations && <div className="category-privacy-fields">
        <label>Generic kiosk label<input value={otherDestinationLabel} maxLength={100} onChange={(event) => setOtherDestinationLabel(event.target.value)} /></label>
        <label>Generic kiosk emoji<input value={otherDestinationSymbol} maxLength={32} onChange={(event) => setOtherDestinationSymbol(event.target.value)} /></label>
        <button type="button" className="button quiet small" onClick={() => void saveOtherDestinationPresentation()}>Save kiosk display</button>
      </div>}
      <label className="check-row"><input type="checkbox" checked={rosterNamePickerEnabled} onChange={(event) => void updateRosterPicker(event.target.checked)} />Allow students to choose their name from the kiosk roster</label>
      <p className="form-note">Choose which categories contribute to the restroom-pass total and semester maximum. Exempt destinations never trigger its kiosk confirmation or semester alert. A category history limit remains separate.</p>
      <div className="table-wrap">
        <table>
          <thead><tr><th title="The pass type shown to students on the kiosk.">Category</th><th title="Whether completed passes in this category count toward the student's restroom-pass total and semester maximum.">Restroom total</th><th title="The maximum completed passes in this category for a student during a term. Exceeding it is highlighted in History but does not block a pass.">History limit</th><th title="Whether the teacher must acknowledge or approve a request before the student may leave.">Approval</th><th title="Whether students can choose this category on the kiosk.">Kiosk</th><th /></tr></thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.id} className={!category.active ? "inactive-row" : ""}>
                <td><strong>{category.label}</strong><small>{category.is_tardy ? "Tardy log" : category.capacity_group === "RESTROOM" ? "Uses restroom capacity" : "Standard pass"}</small></td>
                <td>{category.counts_toward_semester_limit ? "Counts" : "Exempt"}</td>
                <td>{category.pass_count_limit ?? "—"}</td>
                <td>{category.approval_mode === "NONE" ? "None" : category.approval_mode === "ACKNOWLEDGE" ? "Acknowledge" : "Full approval"}</td>
                <td>{category.kiosk_group === "PRIMARY" ? "Main" : "More"}</td>
                <td><button className="text-button" onClick={() => setEditing(category)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(editing || creating) && (
        <CategoryDialog
          classId={classId}
          category={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={async () => { setEditing(null); setCreating(false); await load(); await refresh(); }}
          onError={onError}
        />
      )}
    </section>
  );
}

function CategoryDialog({
  classId,
  category,
  onClose,
  onSaved,
  onError,
}: {
  classId: string;
  category: PassCategory | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [label, setLabel] = useState(category?.label ?? "");
  const [kioskGroup, setKioskGroup] = useState<PassCategory["kiosk_group"]>(category?.kiosk_group ?? "MORE");
  const [capacityGroup, setCapacityGroup] = useState<PassCategory["capacity_group"]>(category?.capacity_group ?? "NONE");
  const [approvalMode, setApprovalMode] = useState<PassCategory["approval_mode"]>(category?.approval_mode ?? "NONE");
  const [limit, setLimit] = useState(category?.pass_count_limit?.toString() ?? "");
  const [countsTowardSemesterLimit, setCountsTowardSemesterLimit] = useState(Boolean(category?.counts_toward_semester_limit));
  const [isTardy, setIsTardy] = useState(Boolean(category?.is_tardy));
  const [displayOrder, setDisplayOrder] = useState(String(category?.display_order ?? 100));
  const [active, setActive] = useState(Boolean(category?.active ?? true));
  const [symbol, setSymbol] = useState(category?.symbol ?? (category ? reasonIcon(category.code) ?? "" : ""));
  const [buttonColor, setButtonColor] = useState(category?.button_color ?? "#f8faf7");
  const [textColor, setTextColor] = useState(category?.text_color ?? "#35413c");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const passCountLimit = limit.trim() ? Number(limit) : null;
    setBusy(true);
    try {
      const body = {
        label,
        symbol: symbol.trim() || null,
        buttonColor,
        textColor,
        kioskGroup,
        capacityGroup: isTardy ? "NONE" : capacityGroup,
        approvalMode: isTardy ? "NONE" : approvalMode,
        passCountLimit,
        countsTowardSemesterLimit: isTardy ? false : countsTowardSemesterLimit,
        isTardy,
        displayOrder: Number(displayOrder),
        ...(category ? { active } : {}),
      };
      await api(
        category ? `/api/classes/${classId}/categories/${category.id}` : `/api/classes/${classId}/categories`,
        { method: category ? "PATCH" : "POST", body: JSON.stringify(body) },
      );
      await onSaved();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Category could not be saved");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!category || !window.confirm(`Remove ${category.label} from future pass choices? Existing history will remain.`)) return;
    setBusy(true);
    try {
      await api(`/api/classes/${classId}/categories/${category.id}`, { method: "DELETE" });
      await onSaved();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Category could not be removed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="dialog category-dialog" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
        <div className="panel-heading"><div><p className="eyebrow ink">Pass category</p><h2>{category ? `Edit ${category.label}` : "Add category"}</h2></div><button type="button" className="icon-button" onClick={onClose}>×</button></div>
        <label>Category name<input value={label} onChange={(event) => setLabel(event.target.value)} required /></label>
        <fieldset className="symbol-picker">
          <legend>Pass symbol</legend>
          <p className="form-note">Choose a symbol for the kiosk, or paste one from your device&apos;s emoji picker.</p>
          <div className="symbol-choice-grid">
            {PASS_SYMBOL_CHOICES.map(([choice, choiceLabel]) => (
              <button type="button" key={choice} className={symbol === choice ? "selected" : ""} onClick={() => setSymbol(choice)} aria-label={choiceLabel} title={choiceLabel}>{choice}</button>
            ))}
          </div>
          <label className="symbol-paste">Paste a symbol or emoji<input value={symbol} maxLength={32} placeholder="Paste an emoji" onChange={(event) => setSymbol(event.target.value)} /></label>
        </fieldset>
        <div className="field-row category-color-fields">
          <label>Button color<input type="color" value={buttonColor} onChange={(event) => setButtonColor(event.target.value)} /></label>
          <label>Text color<input type="color" value={textColor} onChange={(event) => setTextColor(event.target.value)} /></label>
        </div>
        <div className="field-row">
          <label>Kiosk placement<select value={kioskGroup} onChange={(event) => setKioskGroup(event.target.value as PassCategory["kiosk_group"])}><option value="PRIMARY">Main screen</option><option value="MORE">Other menu</option></select></label>
          <label>History pass limit <input type="number" min="1" max="999" value={limit} onChange={(event) => setLimit(event.target.value)} placeholder="No limit" /></label>
        </div>
        <label className="check-row"><input type="checkbox" checked={isTardy} onChange={(event) => setIsTardy(event.target.checked)} />This is a tardy category (kept out of Pass history)</label>
        {!isTardy && <>
          <label className="check-row"><input type="checkbox" checked={countsTowardSemesterLimit} onChange={(event) => setCountsTowardSemesterLimit(event.target.checked)} />Count this category toward the restroom-pass total and semester maximum</label>
          <label>Capacity behavior<select value={capacityGroup} onChange={(event) => setCapacityGroup(event.target.value as PassCategory["capacity_group"])}><option value="NONE">No shared capacity</option><option value="RESTROOM">Use restroom capacity</option></select></label>
          <label>Teacher action<select value={approvalMode} onChange={(event) => setApprovalMode(event.target.value as PassCategory["approval_mode"])}><option value="NONE">No action required</option><option value="ACKNOWLEDGE">Acknowledge after student leaves</option><option value="FULL">Full approval before student leaves</option></select></label>
        </>}
        <div className="field-row"><label>Display order<input type="number" min="0" max="9999" value={displayOrder} onChange={(event) => setDisplayOrder(event.target.value)} required /></label>{category && <label className="check-row"><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />Show on kiosk</label>}</div>
        <div className="dialog-actions">{category && <button type="button" className="button quiet danger-text" onClick={() => void remove()} disabled={busy}>Remove category</button>}<button type="button" className="button quiet" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : "Save category"}</button></div>
      </form>
    </div>
  );
}

interface DeviceItem {
  id: string;
  device_type: "KIOSK" | "DISPLAY";
  name: string;
  scale_percent: number | null;
  active: number;
  last_seen_at: string | null;
  created_at: string;
}

function DevicesPanel({
  timeZone,
  onError,
}: {
  timeZone: string;
  onError: (message: string) => void;
}) {
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [pairing, setPairing] = useState<{ code: string; type: string } | null>(null);
  const scalePresets = [100, 125, 150, 175, 200];

  const load = useCallback(async () => {
    const response = await api<{ devices: DeviceItem[] }>("/api/devices");
    setDevices(response.devices);
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function createPairing(deviceType: "KIOSK" | "DISPLAY") {
    try {
      const response = await api<{ code: string }>("/api/devices/pairing", {
        method: "POST",
        body: JSON.stringify({ deviceType }),
      });
      setPairing({ code: response.code, type: deviceType });
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Pairing code could not be created");
    }
  }

  async function revoke(id: string) {
    if (!window.confirm("Revoke this device? It will need to be paired again.")) return;
    await api(`/api/devices/${id}`, { method: "DELETE" });
    await load();
  }

  async function updateScale(device: DeviceItem, nextValue: string) {
    let scalePercent: number | null;
    if (nextValue === "AUTO") {
      scalePercent = null;
    } else if (nextValue === "CUSTOM") {
      const entered = window.prompt("Enter a custom screen scale from 75% to 250%.", String(device.scale_percent ?? 150));
      if (entered === null) return;
      scalePercent = Number(entered.replace(/[^\d]/g, ""));
    } else {
      scalePercent = Number(nextValue);
    }
    if (!Number.isInteger(scalePercent) && scalePercent !== null) {
      return onError("Enter a whole-number screen scale.");
    }
    try {
      await api(`/api/devices/${device.id}/scale`, {
        method: "PATCH",
        body: JSON.stringify({ scalePercent }),
      });
      await load();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Device scale could not be updated");
    }
  }

  return (
    <div className="two-column">
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">Classroom hardware</p><h2>Pair a device</h2></div></div>
        <p className="muted">Open the matching page on the classroom device, then enter a one-time code.</p>
        <div className="pair-actions">
          <button className="device-choice" onClick={() => void createPairing("KIOSK")}><span>▣</span><strong>Student kiosk</strong><small>Creates and returns passes</small></button>
          <button className="device-choice" onClick={() => void createPairing("DISPLAY")}><span>◫</span><strong>Live display</strong><small>Read-only classroom board</small></button>
        </div>
        {pairing && (
          <div className="pairing-code">
            <span>{pairing.type} code</span>
            <strong>{pairing.code.slice(0, 3)} {pairing.code.slice(3)}</strong>
            <small>Expires in 10 minutes and works once.</small>
          </div>
        )}
      </section>
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">{devices.filter((item) => item.active).length} active</p><h2>Paired devices</h2></div></div>
        <div className="device-list">
          {devices.map((device) => (
            <article key={device.id} className={!device.active ? "disabled" : ""}>
              <span className="device-icon">{device.device_type === "KIOSK" ? "▣" : "◫"}</span>
              <div><strong>{device.name}</strong><small>{device.device_type} · {device.last_seen_at ? `Seen ${formatDateAtTimeZone(device.last_seen_at, timeZone)}` : "Never connected"}</small>{device.active && <label className="device-scale-control">Screen scale<select value={device.scale_percent === null ? "AUTO" : scalePresets.includes(device.scale_percent) ? String(device.scale_percent) : "CUSTOM"} onChange={(event) => void updateScale(device, event.target.value)}><option value="AUTO">Automatic (screen-based)</option>{scalePresets.map((percent) => <option key={percent} value={percent}>{percent}%</option>)}<option value="CUSTOM">{device.scale_percent !== null && !scalePresets.includes(device.scale_percent) ? `Custom (${device.scale_percent}%)` : "Custom…"}</option></select></label>}</div>
              {device.active && <button className="text-button danger-text" onClick={() => void revoke(device.id)}>Revoke</button>}
            </article>
          ))}
          {!devices.length && <MiniEmpty text="No devices paired yet." />}
        </div>
      </section>
    </div>
  );
}

function NewClassDialog({
  schoolId,
  onClose,
  onCreated,
}: {
  schoolId: string | null;
  onClose: () => void;
  onCreated: (id: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [bellPeriods, setBellPeriods] = useState<Array<{ id: string; name: string }>>([]);
  const [bellPeriodId, setBellPeriodId] = useState("");
  const [bellPeriodsLoaded, setBellPeriodsLoaded] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!schoolId) return;
    setBellPeriodsLoaded(false);
    void api<{ periods: Array<{ id: string; name: string }> }>(`/api/classes/bell-periods?schoolId=${encodeURIComponent(schoolId)}`)
      .then((response) => {
        setBellPeriods(response.periods);
        setBellPeriodId((current) => response.periods.some((period) => period.id === current) ? current : response.periods[0]?.id ?? "");
        setBellPeriodsLoaded(true);
      })
      .catch((caught) => setError(caught instanceof Error ? caught.message : "School bell periods could not be loaded"));
  }, [schoolId]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!schoolId) return setError("Your account needs a home school.");
    try {
      const result = await api<{ id: string }>("/api/classes", {
        method: "POST",
        body: JSON.stringify({ schoolId, name, bellPeriodId }),
      });
      await onCreated(result.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Class could not be created");
    }
  }
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="dialog" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
        <div className="panel-heading"><div><p className="eyebrow ink">New classroom</p><h2>Create a class period</h2></div><button type="button" className="icon-button" onClick={onClose}>×</button></div>
        {error && <div className="alert error">{error}</div>}
        <label>School bell period<select value={bellPeriodId} onChange={(event) => setBellPeriodId(event.target.value)} required disabled={!bellPeriodsLoaded || !bellPeriods.length}><option value="" disabled>{bellPeriodsLoaded ? "No periods available" : "Loading periods…"}</option>{bellPeriods.map((period) => <option key={period.id} value={period.id}>{period.name}</option>)}</select><small>{bellPeriods.length ? "Choose the configured school bell period. The class subject is entered separately below." : "An administrator must add school bell periods in Administration → School calendar before classes can be created."}</small></label>
        <label>Class name<input placeholder="English 9" value={name} onChange={(event) => setName(event.target.value)} required /></label>
        <p className="form-note">This class will be assigned to the school’s current academic term. Default rules: 2 restroom students, 10-minute limit, warning after 8 minutes.</p>
        <div className="dialog-actions"><button type="button" className="button quiet" onClick={onClose}>Cancel</button><button className="button primary" disabled={!bellPeriodsLoaded || !bellPeriodId}>Create period</button></div>
      </form>
    </div>
  );
}

interface PasswordResetRequest {
  id: string;
  user_id: string;
  display_name: string;
  email: string;
  school_name: string | null;
  timezone: string;
  status: "PENDING" | "ISSUED" | "EXPIRED";
  requested_at: string;
  issued_at: string | null;
  expires_at: string | null;
}

interface AdminData {
  districts: Array<{ id: string; name: string; code: string; timezone: string; active: number; school_count?: number; active_class_count?: number; teacher_count?: number; student_count?: number; pass_count?: number; active_pass_count?: number }>;
  schools: Array<{ id: string; district_id: string; district_name: string; name: string; code: string; active: number; active_class_count?: number; teacher_count?: number; student_count?: number; pass_count?: number; active_pass_count?: number }>;
  terms: Array<{ id: string; school_id: string; district_term_id: string | null; name: string; starts_on: string | null; ends_on: string | null; active: number }>;
  districtTerms: Array<{ id: string; district_id: string; name: string; starts_on: string | null; ends_on: string | null; active: number }>;
  users: Array<{ id: string; username: string; display_name: string; title: string | null; email: string; home_district_id: string | null; home_district_name: string | null; home_school_id: string | null; school_name: string | null; active: number; email_warning_enabled: number; email_overdue_enabled: number }>;
  grants: RoleGrant[];
  homeLocationRequests: Array<{ id: string; user_id: string; display_name: string; email: string; current_district_name: string | null; current_school_name: string | null; requested_district_id: string; requested_district_name: string; requested_school_id: string | null; requested_school_name: string | null; requested_at: string }>;
  accessRequests: Array<{ id: string; display_name: string; title: string | null; email: string; district_id: string | null; school_id: string | null; district_name: string | null; school_name: string | null; proposed_district_name: string | null; proposed_school_name: string | null; introduction: string | null; internal_note: string | null; status: "PENDING" | "FOLLOW_UP" | "APPROVED" | "DECLINED"; requested_at: string }>;
  passwordResetRequests: PasswordResetRequest[];
}

type AdministrationScope =
  | { level: "GLOBAL" }
  | { level: "DISTRICT"; districtId: string }
  | { level: "SCHOOL"; schoolId: string; districtId: string };

function administrationScope(user: CurrentUser, data: AdminData): AdministrationScope {
  if (user.roles.some((role) => role.role === "GLOBAL_ADMIN")) return { level: "GLOBAL" };
  const districtGrant = user.roles.find((role) => role.role === "DISTRICT_ADMIN" && data.districts.some((district) => district.id === role.scope_id));
  if (districtGrant) return { level: "DISTRICT", districtId: districtGrant.scope_id };
  const schoolGrant = user.roles.find((role) => role.role === "SCHOOL_ADMIN" && data.schools.some((school) => school.id === role.scope_id));
  const school = data.schools.find((item) => item.id === schoolGrant?.scope_id || item.id === user.homeSchoolId);
  return { level: "SCHOOL", schoolId: school?.id ?? "", districtId: school?.district_id ?? "" };
}

function AdminLocationSelectors({
  data,
  user,
  districtId,
  schoolId,
  onDistrictChange,
  onSchoolChange,
  schoolLabel = "School",
}: {
  data: AdminData;
  user: CurrentUser;
  districtId: string;
  schoolId: string;
  onDistrictChange: (districtId: string) => void;
  onSchoolChange: (schoolId: string) => void;
  schoolLabel?: string;
}) {
  const scope = administrationScope(user, data);
  if (scope.level === "SCHOOL") return null;
  const districts = scope.level === "GLOBAL"
    ? data.districts.filter((district) => data.schools.some((school) => school.district_id === district.id))
    : data.districts.filter((district) => district.id === scope.districtId);
  const schools = data.schools.filter((school) => school.district_id === districtId);
  return <>
    {scope.level === "GLOBAL" && <label>District<select value={districtId} onChange={(event) => onDistrictChange(event.target.value)} required><option value="" disabled>Select district</option>{districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label>}
    <label>{schoolLabel}<select value={schoolId} onChange={(event) => onSchoolChange(event.target.value)} required disabled={!schools.length}><option value="" disabled>Select school</option>{schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select></label>
  </>;
}

function AdminPage({ user, onUserChange }: { user: CurrentUser; onUserChange: () => Promise<void> }) {
  const [data, setData] = useState<AdminData>({ districts: [], schools: [], terms: [], districtTerms: [], users: [], grants: [], homeLocationRequests: [], accessRequests: [], passwordResetRequests: [] });
  const [section, setSection] = useState<"live" | "people" | "schools" | "calendar" | "students" | "records" | "email" | "terms" | "feedback">("people");
  const [passHistoryTeacherId, setPassHistoryTeacherId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await api<AdminData>("/api/admin/overview"));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Administration data could not be loaded");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const global = user.roles.some((role) => role.role === "GLOBAL_ADMIN");

  return (
    <>
      <header className="page-header">
        <div><p className="eyebrow ink">Administration</p><h1>People & places</h1><p>Manage your organization without crossing school boundaries.</p></div>
        <span className="role-summary">{user.roles.filter((role) => role.role.includes("ADMIN")).map((role) => role.role.replace("_", " ")).join(" · ")}</span>
      </header>
      {error && <Dismissible message={error} kind="error" onClose={() => setError("")} />}
      {message && <Dismissible message={message} onClose={() => setMessage("")} />}
      <nav className="tabs">
        <button className={section === "live" ? "active" : ""} onClick={() => setSection("live")}>Live passes</button>
        <button className={section === "people" ? "active" : ""} onClick={() => setSection("people")}>People & permissions</button>
        <button className={section === "schools" ? "active" : ""} onClick={() => setSection("schools")}>Districts & schools</button>
        <button className={section === "calendar" ? "active" : ""} onClick={() => setSection("calendar")}>School calendar</button>
        <button className={section === "students" ? "active" : ""} onClick={() => { setPassHistoryTeacherId(""); setSection("students"); }}>Records</button>
        <button className={section === "records" ? "active" : ""} onClick={() => setSection("records")}>Records retention</button>
        {global && <button className={section === "feedback" ? "active" : ""} onClick={() => setSection("feedback")}>Feedback</button>}
        {global && <button className={section === "terms" ? "active" : ""} onClick={() => setSection("terms")}>Terms &amp; privacy</button>}
        {global && <button className={section === "email" ? "active" : ""} onClick={() => setSection("email")}>Email delivery</button>}
      </nav>
      {section === "live" ? (
        <AdminLivePasses data={data} currentUser={user} onError={setError} />
      ) : section === "people" ? (
        <PeopleAdmin data={data} currentUser={user} refresh={load} onError={setError} onMessage={setMessage} onOpenPassHistory={(teacherId) => { setPassHistoryTeacherId(teacherId); setSection("students"); }} />
      ) : section === "schools" ? (
        <SchoolAdmin data={data} currentUser={user} refresh={load} onError={setError} />
      ) : section === "calendar" ? (
        <SchoolCalendarAdmin data={data} currentUser={user} onError={setError} onMessage={setMessage} />
      ) : section === "students" ? (
        <StudentRecordsAdmin data={data} currentUser={user} onError={setError} onMessage={setMessage} passHistoryTeacherId={passHistoryTeacherId} />
      ) : section === "records" ? (
        <RecordsRetentionAdmin data={data} currentUser={user} onError={setError} onMessage={setMessage} />
      ) : section === "feedback" ? (
        <FeedbackAdmin onError={setError} onMessage={setMessage} />
      ) : section === "terms" ? (
        <TermsAdmin onError={setError} onMessage={setMessage} onPublished={onUserChange} />
      ) : (
        <EmailAdmin currentUser={user} onError={setError} onMessage={setMessage} />
      )}
    </>
  );
}

interface FeedbackRecord {
  id: string;
  type: keyof typeof feedbackTypeLabels;
  comment: string;
  submitterName: string;
  districtName: string;
  schoolName: string;
  permissions: string;
  createdAt: string;
}

function FeedbackAdmin({ onError, onMessage }: { onError: (message: string) => void; onMessage: (message: string) => void }) {
  const [feedback, setFeedback] = useState<FeedbackRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => { setLoading(true); try { const result = await api<{ feedback: FeedbackRecord[] }>("/api/admin/feedback"); setFeedback(result.feedback); } catch (caught) { onError(caught instanceof Error ? caught.message : "Feedback could not be loaded"); } finally { setLoading(false); } }, [onError]);
  useEffect(() => { void load(); }, [load]);
  async function remove(item: FeedbackRecord) { if (!window.confirm(`Remove this ${feedbackTypeLabels[item.type].toLowerCase()} feedback item after review?`)) return; try { await api(`/api/admin/feedback/${item.id}`, { method: "DELETE" }); setFeedback((current) => current.filter((entry) => entry.id !== item.id)); onMessage("Feedback item removed."); } catch (caught) { onError(caught instanceof Error ? caught.message : "Feedback could not be removed"); } }
  return <section className="panel feedback-admin"><div className="panel-heading"><div><p className="eyebrow ink">Global review queue</p><h2>User feedback</h2><p className="form-note">Feedback includes the submitter’s name, school, district, and assigned permissions as recorded when it was sent.</p></div><span className="count-badge">{feedback.length}</span></div><div className="feedback-list">{feedback.map((item) => <article key={item.id}><div className="feedback-item-header"><div><span className="role-summary">{feedbackTypeLabels[item.type]}</span><strong>{item.submitterName}</strong><small>{item.schoolName} · {item.districtName} · {item.permissions}</small><small>{new Date(item.createdAt).toLocaleString()}</small></div><button type="button" className="button quiet small" onClick={() => void remove(item)}>Remove</button></div><p>{item.comment}</p></article>)}{!loading && !feedback.length && <MiniEmpty text="No feedback is waiting for review." />}</div></section>;
}

function TermsAdmin({ onError, onMessage, onPublished }: { onError: (message: string) => void; onMessage: (message: string) => void; onPublished: () => Promise<void> }) {
  const [documents, setDocuments] = useState<TermsDocument[]>([]);
  const [version, setVersion] = useState(""); const [title, setTitle] = useState(""); const [privacySummary, setPrivacySummary] = useState(""); const [termsBody, setTermsBody] = useState(""); const [saving, setSaving] = useState(false);
  const load = useCallback(async () => { try { const result = await api<{ documents: TermsDocument[] }>("/api/admin/terms-and-privacy"); setDocuments(result.documents); const current = result.documents.find((document) => document.isCurrent); if (current) { setTitle((value) => value || current.title); setPrivacySummary((value) => value || current.privacySummary); setTermsBody((value) => value || current.termsBody); } } catch (caught) { onError(caught instanceof Error ? caught.message : "Terms & Privacy could not be loaded"); } }, [onError]);
  useEffect(() => { void load(); }, [load]);
  async function publish(event: FormEvent) { event.preventDefault(); if (!window.confirm("Publish this version? Every signed-in user, including you, will have to agree before continuing.")) return; setSaving(true); try { await api("/api/admin/terms-and-privacy", { method: "POST", body: JSON.stringify({ version, title, privacySummary, termsBody }) }); onMessage("Terms & Privacy published. Users must agree to continue."); await onPublished(); } catch (caught) { onError(caught instanceof Error ? caught.message : "Terms & Privacy could not be published"); } finally { setSaving(false); } }
  return <section className="panel terms-admin"><div className="panel-heading"><div><p className="eyebrow ink">Global policy</p><h2>Terms &amp; Privacy</h2><p className="form-note">Published versions are retained for acceptance records and cannot be edited. Publish a new version whenever language changes or receives legal review.</p></div></div><form onSubmit={publish} className="terms-editor"><div className="form-grid two"><label>New version<input value={version} onChange={(event) => setVersion(event.target.value)} placeholder="e.g. 1.1" maxLength={40} required /></label><label>Document title<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} required /></label></div><label>Student privacy summary<textarea value={privacySummary} onChange={(event) => setPrivacySummary(event.target.value)} maxLength={4000} required /></label><label>Terms and conditions<textarea className="terms-body-input" value={termsBody} onChange={(event) => setTermsBody(event.target.value)} maxLength={60000} required /><small>Use blank lines between sections or paragraphs.</small></label><div className="dialog-actions"><button className="button primary" disabled={saving}>{saving ? "Publishing…" : "Publish new version"}</button></div></form><div className="terms-version-list"><h3>Version history</h3>{documents.map((document) => <article key={document.id}><div><strong>{document.title} · {document.version}</strong><small>Published {new Date(document.publishedAt).toLocaleString()}{document.publisherName ? ` by ${document.publisherName}` : ""}</small></div>{document.isCurrent && <span className="role-summary">Current</span>}</article>)}{!documents.length && <MiniEmpty text="No Terms & Privacy versions were found." />}</div></section>;
}

interface AdminLivePass {
  id: string;
  reason_code: string;
  reason_label: string;
  uses_restroom_capacity: number;
  approved_at: string | null;
  elapsed_seconds: number;
  first_name: string;
  last_name: string;
  preferred_first_name: string | null;
  preferred_last_name: string | null;
  teacher_name: string;
  class_name: string | null;
  period_label: string | null;
  warning_minutes: number;
  pass_limit_minutes: number;
  timer_colors_enabled: number;
  timer_normal_color: string | null;
  timer_warning_color: string | null;
  timer_alert_color: string | null;
}

function AdminLivePasses({
  data,
  currentUser,
  onError,
}: {
  data: AdminData;
  currentUser: CurrentUser;
  onError: (message: string) => void;
}) {
  const [districtId, setDistrictId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [passes, setPasses] = useState<AdminLivePass[]>([]);
  const [receivedAt, setReceivedAt] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const scope = administrationScope(currentUser, data);
  const selectedDistrictId = scope.level === "GLOBAL" ? districtId : scope.districtId;
  const selectedSchoolId = scope.level === "SCHOOL" ? scope.schoolId : schoolId;
  const districts = useMemo(
    () => data.districts.filter((district) => scope.level === "GLOBAL" || district.id === scope.districtId),
    [data.districts, scope],
  );
  const schools = useMemo(
    () => data.schools.filter((school) => {
      if (scope.level === "SCHOOL") return school.id === scope.schoolId;
      if (scope.level === "DISTRICT") return school.district_id === scope.districtId;
      return !districtId || school.district_id === districtId;
    }),
    [data.schools, districtId, scope],
  );
  const load = useCallback(async () => {
    // The administration overview supplies the locked district/school scope
    // for non-global administrators. Avoid an unscoped request while it is
    // still loading.
    if (scope.level !== "GLOBAL" && !selectedDistrictId) return;
    try {
      const query = new URLSearchParams();
      if (selectedDistrictId) query.set("districtId", selectedDistrictId);
      if (selectedSchoolId) query.set("schoolId", selectedSchoolId);
      const result = await api<{ passes: AdminLivePass[] }>(`/api/admin/live-passes?${query.toString()}`);
      setPasses(result.passes);
      setReceivedAt(Date.now());
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Live passes could not be loaded");
    } finally {
      setLoading(false);
    }
  }, [onError, scope.level, selectedDistrictId, selectedSchoolId]);
  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(interval);
  }, [load]);
  useEffect(() => {
    if (schoolId && !schools.some((school) => school.id === schoolId)) setSchoolId("");
  }, [schoolId, schools]);
  const now = useClock();

  return <section className="panel admin-live-passes">
    <div className="panel-heading">
      <div><p className="eyebrow ink">Live monitoring</p><h2>Students out</h2><p className="form-note">Read-only view of active passes in the locations you administer.</p></div>
      <div className="panel-heading-actions"><span className="live-pill"><i /> Live</span><span className="count-badge">{passes.length}</span></div>
    </div>
    <div className="form-grid admin-live-pass-filters">
      {scope.level === "GLOBAL" && <label>District<select value={districtId} onChange={(event) => { setDistrictId(event.target.value); setSchoolId(""); }}><option value="">All districts</option>{districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label>}
      {scope.level !== "SCHOOL" && <label>School<select value={schoolId} onChange={(event) => setSchoolId(event.target.value)}><option value="">All schools</option>{schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select></label>}
    </div>
    <div className="admin-live-pass-list">
      {passes.map((pass) => {
        const warningMinutes = Number(pass.warning_minutes) || 8;
        const limitMinutes = Number(pass.pass_limit_minutes) || 10;
        const tone = restroomTimerTone(pass, now, warningMinutes, limitMinutes, receivedAt);
        const presentation = timerPresentation(tone, pass);
        return <article key={pass.id} className={cx("pass-card", "admin-live-pass-card", tone === "limit" && "overdue")}>
          <div className="pass-person"><strong>{studentDisplayName(pass)}</strong><span>{pass.reason_label || reasonLabels[pass.reason_code] || pass.reason_code}</span></div>
          <div className="admin-live-pass-context"><span>{pass.period_label ?? "Unassigned period"}</span><span>{pass.teacher_name}</span><span>{pass.class_name ?? "No class assignment"}</span></div>
          <div className={cx("timer", tone, presentation.className)} style={presentation.style}><small>{tone === "limit" ? "Limit reached" : tone === "warning" ? "Warning" : "Elapsed"}</small><strong>{elapsed(pass.approved_at, now, pass.elapsed_seconds, receivedAt)}</strong></div>
        </article>;
      })}
      {!loading && !passes.length && <MiniEmpty text="No students are currently out in this selection." />}
    </div>
  </section>;
}

function AccessRequestsPanel({ requests, accessRoles, setAccessRoles, districtAdmin, global, onDecision }: {
  requests: AdminData["accessRequests"]; accessRoles: Record<string, RoleName>; setAccessRoles: React.Dispatch<React.SetStateAction<Record<string, RoleName>>>;
  districtAdmin: boolean; global: boolean; onDecision: (id: string, decision: "APPROVE" | "FOLLOW_UP" | "DECLINE") => Promise<void>;
}) {
  const render = (items: AdminData["accessRequests"]) => <div className="recovery-request-list">{items.map((item) => <article key={item.id}><div><strong>{item.display_name}{item.title ? ` · ${item.title}` : ""}</strong><small>{item.email}</small><small>{item.district_name ?? item.proposed_district_name ?? "New district"}{item.school_name ? ` · ${item.school_name}` : item.proposed_school_name ? ` · ${item.proposed_school_name}` : ""}</small>{item.introduction && <small>{item.introduction}</small>}{item.internal_note && <small>Follow-up: {item.internal_note}</small>}</div><div className="dialog-actions">{item.school_id && !item.proposed_district_name && !item.proposed_school_name && <label>Role<select value={accessRoles[item.id] ?? "TEACHER"} onChange={(event) => setAccessRoles((current) => ({ ...current, [item.id]: event.target.value as RoleName }))}><option value="TEACHER">Teacher</option>{districtAdmin && <option value="SCHOOL_ADMIN">School Admin</option>}{global && <option value="DISTRICT_ADMIN">District Admin</option>}</select></label>}<button className="button quiet small" onClick={() => void onDecision(item.id, "DECLINE")}>Decline</button>{item.status === "PENDING" && <button className="button quiet small" onClick={() => void onDecision(item.id, "FOLLOW_UP")}>Needs follow-up</button>}{item.school_id && !item.proposed_district_name && !item.proposed_school_name && <button className="button primary small" onClick={() => void onDecision(item.id, "APPROVE")}>Approve</button>}</div></article>)}{!items.length && <MiniEmpty text="No requests in this section." />}</div>;
  const pending = requests.filter((item) => item.status === "PENDING");
  const followUp = requests.filter((item) => item.status === "FOLLOW_UP");
  return <div className="tab-workspace"><div className="panel-heading"><div><p className="eyebrow ink">Approval queue</p><h2>Access requests</h2><p className="form-note">New schools and districts remain in follow-up until organization setup is complete. Email is sent manually after approval.</p></div></div><h3>Pending approval</h3>{render(pending)}<h3>Needs follow-up</h3>{render(followUp)}</div>;
}

function PeopleAdmin({
  data,
  currentUser,
  refresh,
  onError,
  onMessage,
  onOpenPassHistory,
}: {
  data: AdminData;
  currentUser: CurrentUser;
  refresh: () => Promise<void>;
  onError: (message: string) => void;
  onMessage: (message: string) => void;
  onOpenPassHistory: (teacherId: string) => void;
}) {
  const [schoolId, setSchoolId] = useState(data.schools[0]?.id ?? "");
  const [districtId, setDistrictId] = useState(data.schools[0]?.district_id ?? "");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [title, setTitle] = useState("");
  const [password, setPassword] = useState("");
  const [createRoles, setCreateRoles] = useState<RoleName[]>([]);
  const [grantSchool, setGrantSchool] = useState<Record<string, string>>({});
  const [issuedCredential, setIssuedCredential] = useState<{
    request: PasswordResetRequest;
    temporaryPassword: string;
    expiresAt: string;
  } | null>(null);
  const [emailUser, setEmailUser] = useState<AdminData["users"][number] | null>(null);
  const [userDistrictFilter, setUserDistrictFilter] = useState("");
  const [userSchoolFilter, setUserSchoolFilter] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState<"ALL" | "ADMIN" | "TEACHER">("ALL");
  const [peopleView, setPeopleView] = useState<"USERS" | "CREATE" | "RECOVERY" | "LOCATIONS" | "ACCESS">("USERS");
  const [accessRoles, setAccessRoles] = useState<Record<string, RoleName>>({});
  const [editingPermissionsId, setEditingPermissionsId] = useState<string | null>(null);
  const global = currentUser.roles.some((role) => role.role === "GLOBAL_ADMIN");
  const districtAdmin = global || currentUser.roles.some((role) => role.role === "DISTRICT_ADMIN");
  const scope = administrationScope(currentUser, data);
  const [selectedDistrictId, setSelectedDistrictId] = useState("");
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const availableDistricts = useMemo(
    () => data.districts
      .filter((district) => scope.level === "GLOBAL" || district.id === scope.districtId)
      .filter((district) => data.schools.some((school) => school.district_id === district.id))
      .sort((left, right) => left.name.localeCompare(right.name)),
    [data.districts, data.schools, scope],
  );
  const districtSchools = useMemo(
    () => data.schools
      .filter((school) => school.district_id === districtId && (scope.level !== "SCHOOL" || school.id === scope.schoolId))
      .sort((left, right) => left.name.localeCompare(right.name)),
    [data.schools, districtId, scope],
  );

  useEffect(() => {
    if (scope.level !== "GLOBAL") {
      setDistrictId(scope.districtId);
    } else if (!districtId || !availableDistricts.some((district) => district.id === districtId)) {
      setDistrictId(availableDistricts[0]?.id ?? "");
    }
  }, [availableDistricts, districtId, scope]);

  useEffect(() => {
    if (scope.level === "SCHOOL") {
      setSchoolId(scope.schoolId);
    } else if (!districtSchools.some((school) => school.id === schoolId)) {
      setSchoolId(districtSchools[0]?.id ?? "");
    }
  }, [districtSchools, schoolId, scope]);

  const manageableDistricts = useMemo(
    () => global
      ? data.districts
      : data.districts.filter((district) => currentUser.roles.some((role) => role.role === "DISTRICT_ADMIN" && role.scope_id === district.id)),
    [currentUser.roles, data.districts, global],
  );
  const hierarchyDistrictId = global ? selectedDistrictId : manageableDistricts[0]?.id ?? "";
  const hierarchySchools = useMemo(
    () => data.schools.filter((school) => school.district_id === hierarchyDistrictId),
    [data.schools, hierarchyDistrictId],
  );
  const hierarchySchoolId = districtAdmin ? selectedSchoolId : scope.level === "SCHOOL" ? scope.schoolId : currentUser.homeSchoolId ?? "";
  const grantsFor = (userId: string) => data.grants.filter((grant) => grant.user_id === userId);
  const roleLevel = (role: RoleGrant["role"]) => role === "GLOBAL_ADMIN" ? 3 : role === "DISTRICT_ADMIN" ? 2 : role === "SCHOOL_ADMIN" ? 1 : 0;
  const actorLevel = Math.max(0, ...currentUser.roles.map((role) => roleLevel(role.role)));
  const createRoleOptions: Array<{ role: RoleName; label: string }> = [
    { role: "GLOBAL_ADMIN" as RoleName, label: "Global administrator" },
    { role: "DISTRICT_ADMIN" as RoleName, label: "District administrator" },
    { role: "SCHOOL_ADMIN" as RoleName, label: "School administrator" },
    { role: "TEACHER" as RoleName, label: "Teacher" },
  ].filter((option) => roleLevel(option.role) <= actorLevel);
  const userLevel = (userId: string) => Math.max(0, ...grantsFor(userId).map((grant) => roleLevel(grant.role)));
  const userDistrictId = (person: AdminData["users"][number]) => {
    return person.home_district_id ?? grantsFor(person.id).find((grant) => grant.role === "DISTRICT_ADMIN")?.scope_id ?? "";
  };
  const visibleUsers = data.users.filter((person) => {
    if (userLevel(person.id) > actorLevel) return false;
    const grants = grantsFor(person.id);
    if (userDistrictFilter && userDistrictId(person) !== userDistrictFilter) return false;
    if (userSchoolFilter && person.home_school_id !== userSchoolFilter) return false;
    if (userRoleFilter === "ADMIN" && !grants.some((grant) => grant.role.includes("ADMIN"))) return false;
    if (userRoleFilter === "TEACHER" && !grants.some((grant) => grant.role === "TEACHER")) return false;
    return true;
  });
  const globalAdmins = data.users.filter((person) => grantsFor(person.id).some((grant) => grant.role === "GLOBAL_ADMIN"));
  const selectedDistrictAdmins = data.users.filter((person) => grantsFor(person.id).some((grant) => grant.role === "DISTRICT_ADMIN" && grant.scope_id === hierarchyDistrictId));
  const selectedSchoolAdmins = data.users.filter((person) => grantsFor(person.id).some((grant) => grant.role === "SCHOOL_ADMIN" && grant.scope_id === hierarchySchoolId));
  const selectedTeachers = data.users.filter((person) => grantsFor(person.id).some((grant) => grant.role === "TEACHER" && grant.scope_id === hierarchySchoolId));

  useEffect(() => {
    if (!manageableDistricts.some((district) => district.id === selectedDistrictId)) {
      setSelectedDistrictId(manageableDistricts[0]?.id ?? "");
    }
  }, [manageableDistricts, selectedDistrictId]);
  useEffect(() => {
    if (districtAdmin && !hierarchySchools.some((school) => school.id === selectedSchoolId)) {
      setSelectedSchoolId(hierarchySchools[0]?.id ?? "");
    }
  }, [districtAdmin, hierarchySchools, selectedSchoolId]);

  async function decideAccessRequest(id: string, decision: "APPROVE" | "FOLLOW_UP" | "DECLINE") {
    const internalNote = decision === "FOLLOW_UP" ? window.prompt("Internal follow-up note (optional):") ?? "" : "";
    try {
      const result = await api<{ temporaryPassword?: string; email?: string }>(`/api/admin/access-requests/${id}/decision`, { method: "POST", body: JSON.stringify({ decision, role: decision === "APPROVE" ? (accessRoles[id] ?? "TEACHER") : undefined, internalNote }) });
      if (result.temporaryPassword) window.alert(`Account created. Send this temporary password manually to ${result.email}:\n\n${result.temporaryPassword}`);
      await refresh(); onMessage(decision === "APPROVE" ? "Access request approved and account created." : decision === "FOLLOW_UP" ? "Access request moved to follow-up." : "Access request declined.");
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Access request could not be updated"); }
  }

  async function createUser(event: FormEvent) {
    event.preventDefault();
    try {
      await api("/api/admin/users", {
        method: "POST",
        body: JSON.stringify({ schoolId, email, displayName, title: title || null, password, roles: createRoles }),
      });
      setEmail(""); setDisplayName(""); setTitle(""); setPassword(""); setCreateRoles([]);
      await refresh();
      onMessage("User account created.");
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Account could not be created");
    }
  }

  async function grant(userId: string, role: "TEACHER" | "SCHOOL_ADMIN" | "DISTRICT_ADMIN" | "GLOBAL_ADMIN") {
    const person = data.users.find((item) => item.id === userId);
    const selectedSchool = role === "TEACHER"
      ? person?.home_school_id
      : grantSchool[userId] || person?.home_school_id || data.schools[0]?.id;
    if (role === "TEACHER" && !selectedSchool) {
      return onError("Assign a home school before granting the Teacher role.");
    }
    const school = data.schools.find((item) => item.id === selectedSchool);
    if (role !== "GLOBAL_ADMIN" && !school) return onError("Select a school first");
    const scopeType = role === "GLOBAL_ADMIN" ? "GLOBAL" : role === "DISTRICT_ADMIN" ? "DISTRICT" : "SCHOOL";
    const scopeId = role === "GLOBAL_ADMIN" ? GLOBAL_SCOPE_ID : role === "DISTRICT_ADMIN" ? school!.district_id : school!.id;
    try {
      await api(`/api/admin/users/${userId}/roles`, {
        method: "POST",
        body: JSON.stringify({ role, scopeType, scopeId }),
      });
      await refresh();
      onMessage(`${role.replace("_", " ")} permission granted.`);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Permission could not be granted");
    }
  }

  async function decideHomeLocation(requestId: string, decision: "APPROVE" | "REJECT") {
    try {
      await api(`/api/admin/home-location-requests/${requestId}`, {
        method: "PATCH",
        body: JSON.stringify({ decision }),
      });
      await refresh();
      onMessage(decision === "APPROVE" ? "Home location request approved." : "Home location request rejected.");
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Home location request could not be decided");
    }
  }

  async function removeRole(userId: string, grant: RoleGrant) {
    if (!window.confirm(`Remove ${grant.role.replace("_", " ")} permission?`)) return;
    try {
      await api(`/api/admin/users/${userId}/roles/${grant.id}`, { method: "DELETE" });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Permission could not be removed");
    }
  }

  async function toggleStatus(person: AdminData["users"][number]) {
    const verb = person.active ? "deactivate" : "activate";
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${person.display_name}?`)) return;
    try {
      await api(`/api/admin/users/${person.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ active: !person.active }),
      });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : `Account could not be ${verb}d`);
    }
  }

  async function issueTemporaryPassword(recovery: PasswordResetRequest) {
    const replacing = recovery.status !== "PENDING";
    if (!window.confirm(`${replacing ? "Replace" : "Generate"} the temporary password for ${recovery.display_name}? This immediately revokes their signed-in sessions and ${replacing ? "invalidates the previous temporary password" : "replaces their current password"}.`)) return;
    try {
      const result = await api<{ temporaryPassword: string; expiresAt: string }>(
        `/api/admin/password-reset-requests/${recovery.id}/issue`,
        { method: "POST" },
      );
      setIssuedCredential({ request: recovery, ...result });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Temporary password could not be generated");
    }
  }

  return (
    <div className="admin-layout people-admin-layout">
      {false && global && (
        <section className="panel password-recovery-panel">
          <div className="panel-heading">
            <div><p className="eyebrow ink">Global Admin only</p><h2>Password recovery requests</h2><p className="form-note">Generate a temporary password, then send it to the registered address using your normal email account. Passwords are displayed once and expire after 24 hours.</p></div>
            <span className="count-badge">{data.passwordResetRequests.length}</span>
          </div>
          <div className="recovery-request-list">
            {data.passwordResetRequests.map((recovery) => (
              <article key={recovery.id}>
                <div>
                  <strong>{recovery.display_name}</strong>
                  <small>{recovery.email} · {recovery.school_name ?? "Global account"}</small>
                  <small>Requested {formatDateAtTimeZone(recovery.requested_at, recovery.timezone)}</small>
                </div>
                <span className={cx("status-pill", recovery.status === "PENDING" && "success")}>{recovery.status === "PENDING" ? "Awaiting admin" : recovery.status === "EXPIRED" ? "Temporary password expired" : `Issued · expires ${formatDateAtTimeZone(recovery.expires_at, recovery.timezone)}`}</span>
                <button className="button quiet small" onClick={() => void issueTemporaryPassword(recovery)}>{recovery.status === "PENDING" ? "Generate temporary password" : "Generate replacement"}</button>
              </article>
            ))}
            {!data.passwordResetRequests.length && <MiniEmpty text="No open password recovery requests." />}
          </div>
        </section>
      )}
      {false && <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">New staff member</p><h2>Create teacher account</h2></div></div>
        <form className="stack-form" onSubmit={createUser}>
          <AdminLocationSelectors data={data} user={currentUser} districtId={districtId} schoolId={schoolId} schoolLabel="Home school" onDistrictChange={(nextDistrictId) => { setDistrictId(nextDistrictId); setSchoolId(data.schools.find((school) => school.district_id === nextDistrictId)?.id ?? ""); }} onSchoolChange={setSchoolId} />
          <label>Full name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></label>
          <label>Title <span className="muted">(optional)</span><input placeholder="Mr. / Ms. / Dr." value={title} onChange={(event) => setTitle(event.target.value)} /></label>
          <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label>Temporary password<input type="password" minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} required /><small>At least 12 characters</small></label>
          <button className="button primary" disabled={!data.schools.length}>Create teacher</button>
        </form>
      </section>}
      {false && <section className="panel user-hierarchy">
        <div className="panel-heading"><div><p className="eyebrow ink">Permission hierarchy</p><h2>Users by scope</h2><p className="form-note">Administrators can view users at their level and below; only users below their level can be changed or removed.</p></div></div>
        {global && <div className="user-scope-group"><h3>Global administrators</h3>{globalAdmins.map((person) => <div key={person.id}><strong>{person.display_name}</strong><small>{person.email}</small></div>)}</div>}
        {global && <label>District<select value={hierarchyDistrictId} onChange={(event) => setSelectedDistrictId(event.target.value)}><option value="" disabled>Select district</option>{manageableDistricts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label>}
        {districtAdmin && <><div className="user-scope-group"><h3>District administrators</h3>{selectedDistrictAdmins.map((person) => <div key={person.id}><strong>{person.display_name}</strong><small>{person.email}</small></div>)}{!selectedDistrictAdmins.length && <small>No district administrators assigned.</small>}</div><label>School<select value={hierarchySchoolId} onChange={(event) => setSelectedSchoolId(event.target.value)}><option value="" disabled>Select school</option>{hierarchySchools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select></label></>}
        {hierarchySchoolId && <div className="user-scope-group"><h3>School administrators</h3>{selectedSchoolAdmins.map((person) => <div key={person.id}><strong>{person.display_name}</strong><small>{person.email}</small></div>)}{!selectedSchoolAdmins.length && <small>No school administrators assigned.</small>}<h3>Teachers</h3>{selectedTeachers.map((person) => <div key={person.id}><strong>{person.display_name}</strong><small>{person.email}</small></div>)}{!selectedTeachers.length && <small>No teachers assigned.</small>}</div>}
      </section>}
      <section className="panel admin-people">
        <nav className="tabs" aria-label="Users and permissions views"><button className={peopleView === "USERS" ? "active" : ""} onClick={() => setPeopleView("USERS")}>Users & permissions</button><button className={peopleView === "ACCESS" ? "active" : ""} onClick={() => setPeopleView("ACCESS")}>Access requests <span className="count-badge">{data.accessRequests.filter((item) => ["PENDING", "FOLLOW_UP"].includes(item.status)).length}</span></button><button className={peopleView === "LOCATIONS" ? "active" : ""} onClick={() => setPeopleView("LOCATIONS")}>Home location requests <span className="count-badge">{data.homeLocationRequests.length}</span></button><button className={peopleView === "CREATE" ? "active" : ""} onClick={() => setPeopleView("CREATE")}>Create user</button><button className={peopleView === "RECOVERY" ? "active" : ""} onClick={() => setPeopleView("RECOVERY")}>Password recovery <span className="count-badge">{data.passwordResetRequests.length}</span></button></nav>
        {peopleView === "ACCESS" && <AccessRequestsPanel requests={data.accessRequests} accessRoles={accessRoles} setAccessRoles={setAccessRoles} districtAdmin={districtAdmin} global={global} onDecision={decideAccessRequest} />}
        {peopleView === "USERS" && <>
        <div className="panel-heading"><div><p className="eyebrow ink">{visibleUsers.length} accounts</p><h2>Users & permissions</h2><p className="form-note">Open a teacher’s pass history or manage permissions for users below your own level.</p></div></div>
        <div className="form-grid user-filters">
          {global && <label>District<select value={userDistrictFilter} onChange={(event) => { setUserDistrictFilter(event.target.value); setUserSchoolFilter(""); }}><option value="">All districts</option>{availableDistricts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label>}
          {scope.level !== "SCHOOL" && <label>School<select value={userSchoolFilter} onChange={(event) => setUserSchoolFilter(event.target.value)}><option value="">All schools</option>{data.schools.filter((school) => !userDistrictFilter || school.district_id === userDistrictFilter).map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select></label>}
          <label>Role<select value={userRoleFilter} onChange={(event) => setUserRoleFilter(event.target.value as typeof userRoleFilter)}><option value="ALL">All users</option><option value="ADMIN">All administrators</option><option value="TEACHER">All teachers</option></select></label>
        </div>
        <div className="people-list">
          {visibleUsers.map((person) => {
            const grants = data.grants.filter((grant) => grant.user_id === person.id);
            const canManage = userLevel(person.id) < actorLevel;
            return (
                <article key={person.id} className={cx(!person.active && "disabled")}>
                <div className="avatar">{person.display_name.slice(0, 1)}</div>
                <div className="person-info">
                  <div><strong>{[person.title, person.display_name].filter(Boolean).join(" ")}</strong></div>
                  <small>{person.email}</small>
                  <small>{grants.some((grant) => grant.role === "TEACHER" || grant.role === "SCHOOL_ADMIN") ? `${data.schools.find((school) => school.id === person.home_school_id)?.district_name ?? ""}${person.school_name ? ` · ${person.school_name}` : ""}` : grants.some((grant) => grant.role === "DISTRICT_ADMIN") ? data.districts.find((district) => district.id === userDistrictId(person))?.name ?? "District administrator" : "Global administration"}</small>
                  <div className="grant-list">
                    {grants.map((grant) => (
                      <span key={grant.id} className={grant.role.includes("ADMIN") ? "admin-grant" : ""}>
                        {grant.role.replace("_", " ")}
                        {person.id !== currentUser.id && canManage && (
                          <button onClick={() => void removeRole(person.id, grant)} aria-label={`Remove ${grant.role}`}>×</button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="user-card-actions">
                  {grants.some((grant) => grant.role === "TEACHER") && <button className="button quiet small" onClick={(event) => { event.stopPropagation(); onOpenPassHistory(person.id); }}>View history</button>}
                  {canManage && <button className="button quiet small" onClick={(event) => { event.stopPropagation(); setEditingPermissionsId((current) => current === person.id ? null : person.id); }}>{editingPermissionsId === person.id ? "Close permissions" : "Change permissions"}</button>}
                </div>
                {canManage && editingPermissionsId === person.id && <div className="grant-controls" onClick={(event) => event.stopPropagation()}>
                  <select value={grantSchool[person.id] ?? person.home_school_id ?? ""} onChange={(event) => setGrantSchool((current) => ({ ...current, [person.id]: event.target.value }))}>
                    {data.schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}
                  </select>
                  <button className="button quiet small" onClick={() => void grant(person.id, "TEACHER")}>+ Teacher</button>
                  <button className="button quiet small" onClick={() => void grant(person.id, "SCHOOL_ADMIN")}>+ School admin</button>
                  {actorLevel >= 2 && <button className="button quiet small" onClick={() => void grant(person.id, "DISTRICT_ADMIN")}>+ District admin</button>}
                  {global && <button className="button quiet small" onClick={() => void grant(person.id, "GLOBAL_ADMIN")}>+ Global admin</button>}
                  {person.id !== currentUser.id && (
                    <>
                      <button className="button quiet small" onClick={() => setEmailUser(person)}>Email alerts</button>
                      <button className="button quiet small" onClick={() => void toggleStatus(person)}>{person.active ? "Deactivate" : "Activate"}</button>
                    </>
                  )}
                </div>}
              </article>
            );
          })}
          {!visibleUsers.length && <MiniEmpty text="No staff accounts match this selection." />}
        </div>
        </>}
        {peopleView === "LOCATIONS" && <div className="tab-workspace location-request-panel"><div className="panel-heading"><div><p className="eyebrow ink">Approval queue</p><h2>Home district & school requests</h2><p className="form-note">Approve requests only for locations within your administrative scope. A request updates the user’s home location, not their roles.</p></div><span className="count-badge">{data.homeLocationRequests.length}</span></div><div className="recovery-request-list">{data.homeLocationRequests.map((locationRequest) => <article key={locationRequest.id}><div><strong>{locationRequest.display_name}</strong><small>{locationRequest.email}</small><small>Current: {locationRequest.current_district_name ?? "No home district"}{locationRequest.current_school_name ? ` · ${locationRequest.current_school_name}` : ""}</small><small>Requested: {locationRequest.requested_district_name}{locationRequest.requested_school_name ? ` · ${locationRequest.requested_school_name}` : ""}</small></div><div className="dialog-actions"><button className="button quiet small" onClick={() => void decideHomeLocation(locationRequest.id, "REJECT")}>Reject</button><button className="button primary small" onClick={() => void decideHomeLocation(locationRequest.id, "APPROVE")}>Approve</button></div></article>)}{!data.homeLocationRequests.length && <MiniEmpty text="No home location requests are awaiting your approval." />}</div></div>}
        {peopleView === "CREATE" && <div className="tab-workspace"><div className="panel-heading"><div><p className="eyebrow ink">New account</p><h2>Create user</h2><p className="form-note">Choose the user’s home school and any roles within your administrative authority.</p></div></div><form className="stack-form" onSubmit={createUser}><AdminLocationSelectors data={data} user={currentUser} districtId={districtId} schoolId={schoolId} schoolLabel="Home school" onDistrictChange={(nextDistrictId) => { setDistrictId(nextDistrictId); setSchoolId(data.schools.find((school) => school.district_id === nextDistrictId)?.id ?? ""); }} onSchoolChange={setSchoolId} /><label>Full name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></label><label>Title <span className="muted">(optional)</span><input placeholder="Mr. / Ms. / Dr." value={title} onChange={(event) => setTitle(event.target.value)} /></label><label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label><label>Temporary password<input type="password" minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} required /><small>At least 12 characters</small></label><fieldset className="role-picker"><legend>Privileges <span className="muted">(optional)</span></legend>{createRoleOptions.map((option) => <label className="check-row" key={option.role}><input type="checkbox" checked={createRoles.includes(option.role)} onChange={(event) => setCreateRoles((current) => event.target.checked ? [...current, option.role] : current.filter((role) => role !== option.role))} />{option.label}</label>)}<small>Privileges are scoped to the selected home school and its district.</small></fieldset><button className="button primary" disabled={!data.schools.length}>Create user</button></form></div>}
        {peopleView === "RECOVERY" && <div className="tab-workspace password-recovery-panel"><div className="panel-heading"><div><p className="eyebrow ink">Password recovery</p><h2>Open password recovery requests</h2><p className="form-note">Requests are shown for users in your authorized scope at your level or below. You may issue a temporary password only for users below their own administrator level.</p></div><span className="count-badge">{data.passwordResetRequests.length}</span></div><div className="recovery-request-list">{data.passwordResetRequests.map((recovery) => <article key={recovery.id}><div><strong>{recovery.display_name}</strong><small>{recovery.email} · {recovery.school_name ?? "Global account"}</small><small>Requested {formatDateAtTimeZone(recovery.requested_at, recovery.timezone)}</small></div><span className={cx("status-pill", recovery.status === "PENDING" && "success")}>{recovery.status === "PENDING" ? "Awaiting admin" : recovery.status === "EXPIRED" ? "Temporary password expired" : `Issued · expires ${formatDateAtTimeZone(recovery.expires_at, recovery.timezone)}`}</span><button className="button quiet small" disabled={userLevel(recovery.user_id) >= actorLevel} title={userLevel(recovery.user_id) >= actorLevel ? "Administrators can issue passwords only for users below their own level" : undefined} onClick={() => void issueTemporaryPassword(recovery)}>{recovery.status === "PENDING" ? "Generate temporary password" : "Generate replacement"}</button></article>)}{!data.passwordResetRequests.length && <MiniEmpty text="No open password recovery requests." />}</div></div>}
      </section>
      {issuedCredential && <TemporaryPasswordDialog credential={issuedCredential} onClose={() => setIssuedCredential(null)} />}
      {emailUser && (
        <UserEmailDialog
          person={emailUser}
          onClose={() => setEmailUser(null)}
          onSaved={async () => {
            setEmailUser(null);
            await refresh();
            onMessage(`Email preferences saved for ${emailUser.display_name}.`);
          }}
        />
      )}
    </div>
  );
}

function TeacherHistoryPanel({
  teacher,
  onBack,
  onError,
}: {
  teacher: AdminData["users"][number];
  onBack: () => void;
  onError: (message: string) => void;
}) {
  const schoolId = teacher.home_school_id ?? "";
  const [classes, setClasses] = useState<Array<{ id: string; school_id: string; term_id: string | null; name: string; period_label: string; active: number; timezone: string }>>([]);
  const [classId, setClassId] = useState("__ALL_CLASSES__");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!schoolId) return;
    let current = true;
    void api<{ classes: Array<{ id: string; school_id: string; term_id: string | null; name: string; period_label: string; active: number; timezone: string }> }>(`/api/admin/teachers/${teacher.id}/classes?schoolId=${encodeURIComponent(schoolId)}`)
      .then((result) => { if (current) setClasses(result.classes); })
      .catch((caught) => { if (current) onError(caught instanceof Error ? caught.message : "Teacher classes could not be loaded"); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [onError, schoolId, teacher.id]);
  const selected = classes.find((item) => item.id === classId) ?? classes[0];
  return <div className="teacher-history-page"><div className="panel-heading"><div><p className="eyebrow ink">Teacher history</p><h2>{[teacher.title, teacher.display_name].filter(Boolean).join(" ")}</h2></div><button className="button quiet small" onClick={onBack}>Back to users</button></div><label className="history-period-filter">Period<select value={classId} onChange={(event) => setClassId(event.target.value)} disabled={loading || !classes.length}><option value="__ALL_CLASSES__">All classes</option>{classes.map((item) => <option key={item.id} value={item.id}>{item.period_label} · {item.name}{item.active ? "" : " (archived)"}</option>)}</select></label>{classId === "__ALL_CLASSES__" && selected ? <HistoryPanel classId="all" schoolId={selected.school_id} allPeriods historyTeacherId={teacher.id} timeZone={selected.timezone} defaultTermId={selected.term_id} onError={onError} /> : selected ? <HistoryPanel classId={selected.id} schoolId={selected.school_id} timeZone={selected.timezone} defaultTermId={selected.term_id} onError={onError} /> : !loading && <MiniEmpty text="This teacher has no current or archived class periods." />}</div>;
}

function TeacherHistoryDialog({
  teacher,
  data,
  onClose,
  onError,
}: {
  teacher: AdminData["users"][number];
  data: AdminData;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const schoolId = teacher.home_school_id ?? "";
  const [classes, setClasses] = useState<Array<{ id: string; school_id: string; term_id: string | null; name: string; period_label: string; active: number; timezone: string }>>([]);
  const [classId, setClassId] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!schoolId) return;
    let current = true;
    void api<{ classes: Array<{ id: string; school_id: string; term_id: string | null; name: string; period_label: string; active: number; timezone: string }> }>(`/api/admin/teachers/${teacher.id}/classes?schoolId=${encodeURIComponent(schoolId)}`)
      .then((result) => { if (current) { setClasses(result.classes); setClassId(result.classes[0]?.id ?? ""); } })
      .catch((caught) => { if (current) onError(caught instanceof Error ? caught.message : "Teacher classes could not be loaded"); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [onError, schoolId, teacher.id]);
  const selected = classes.find((item) => item.id === classId);
  return <div className="dialog-backdrop teacher-history-dialog" role="presentation" onMouseDown={onClose}><div className="dialog wide-dialog" onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Teacher history</p><h2>{[teacher.title, teacher.display_name].filter(Boolean).join(" ")}</h2></div><button type="button" className="icon-button" onClick={onClose}>×</button></div>{schoolId && <label>Period<select value={classId} onChange={(event) => setClassId(event.target.value)} disabled={loading || !classes.length}><option value="" disabled>{loading ? "Loading periods…" : "No current or archived periods"}</option>{classes.map((item) => <option key={item.id} value={item.id}>{item.period_label} · {item.name}{item.active ? "" : " (archived)"}</option>)}</select></label>}{selected ? <HistoryPanel classId={selected.id} schoolId={selected.school_id} timeZone={selected.timezone} defaultTermId={selected.term_id} onError={onError} /> : !loading && <MiniEmpty text="This teacher has no current or archived class periods." />}</div></div>;
}

function TemporaryPasswordDialog({
  credential,
  onClose,
}: {
  credential: {
    request: PasswordResetRequest;
    temporaryPassword: string;
    expiresAt: string;
  };
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copyPassword() {
    try {
      if (!navigator.clipboard) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(credential.temporaryPassword);
    } catch {
      // Clipboard API normally requires HTTPS. Keep manual recovery usable in
      // the explicitly supported HTTP deployment by falling back to a
      // short-lived, selected textarea.
      const textarea = document.createElement("textarea");
      textarea.value = credential.temporaryPassword;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }
    setCopied(true);
  }

  function close() {
    if (!copied && !window.confirm("This password cannot be displayed again. Close without copying it?")) return;
    onClose();
  }

  return (
    <div className="dialog-backdrop" role="presentation">
      <div className="dialog temporary-password-dialog">
        <div className="panel-heading">
          <div><p className="eyebrow ink">Displayed once</p><h2>Temporary password</h2></div>
          <button type="button" className="icon-button" onClick={close} aria-label="Close">×</button>
        </div>
        <p>Send this password to <strong>{credential.request.email}</strong> using your normal email account. The user must replace it immediately after signing in.</p>
        <div className="temporary-password-value"><code>{credential.temporaryPassword}</code><button type="button" className="button secondary" onClick={() => void copyPassword()}>{copied ? "Copied" : "Copy password"}</button></div>
        <div className="alert success">Expires {formatDateAtTimeZone(credential.expiresAt, credential.request.timezone)}. Generating a replacement invalidates this one.</div>
        <p className="form-note">Virtual Hall Pass stores only the password hash. Closing this window permanently removes the plaintext password from the UI.</p>
        <div className="dialog-actions"><button type="button" className="button primary" onClick={close}>Done</button></div>
      </div>
    </div>
  );
}

function UserEmailDialog({
  person,
  onClose,
  onSaved,
}: {
  person: AdminData["users"][number];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [email, setEmail] = useState(person.email ?? "");
  const [warning, setWarning] = useState(Boolean(person.email_warning_enabled));
  const [overdue, setOverdue] = useState(Boolean(person.email_overdue_enabled));
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await api(`/api/admin/users/${person.id}/email-preferences`, {
        method: "PATCH",
        body: JSON.stringify({
          email,
          emailWarningEnabled: warning,
          emailOverdueEnabled: overdue,
        }),
      });
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Email preferences could not be saved");
    }
  }
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="dialog" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
        <div className="panel-heading"><div><p className="eyebrow ink">Teacher notifications</p><h2>{person.display_name}</h2></div><button type="button" className="icon-button" onClick={onClose}>×</button></div>
        {error && <div className="alert error">{error}</div>}
          <label>Email address<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="teacher@school.org" required /></label>
        <label className="check-row"><input type="checkbox" checked={warning} onChange={(event) => setWarning(event.target.checked)} />Send pass warning emails</label>
        <label className="check-row"><input type="checkbox" checked={overdue} onChange={(event) => setOverdue(event.target.checked)} />Send pass overdue emails</label>
        <p className="form-note">System-wide email delivery and event types must also be enabled by a Global Admin.</p>
        <div className="dialog-actions"><button type="button" className="button quiet" onClick={onClose}>Cancel</button><button className="button primary">Save preferences</button></div>
      </form>
    </div>
  );
}

interface EmailSettingsForm {
  enabled: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecurity: "TLS" | "STARTTLS" | "NONE";
  smtpUsername: string;
  passwordConfigured: boolean;
  fromEmail: string;
  fromName: string;
  sendWarnings: boolean;
  sendOverdue: boolean;
  lastVerifiedAt: string | null;
  lastVerifyError: string | null;
}

function EmailAdmin({
  currentUser,
  onError,
  onMessage,
}: {
  currentUser: CurrentUser;
  onError: (message: string) => void;
  onMessage: (message: string) => void;
}) {
  const [settings, setSettings] = useState<EmailSettingsForm>({
    enabled: false,
    smtpHost: "",
    smtpPort: 587,
    smtpSecurity: "STARTTLS",
    smtpUsername: "",
    passwordConfigured: false,
    fromEmail: "",
    fromName: "Virtual Hall Pass",
    sendWarnings: false,
    sendOverdue: true,
    lastVerifiedAt: null,
    lastVerifyError: null,
  });
  const [smtpPassword, setSmtpPassword] = useState("");
  const [clearPassword, setClearPassword] = useState(false);
  const [testRecipient, setTestRecipient] = useState(currentUser.email ?? "");
  const [busy, setBusy] = useState("");
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api<{ settings: EmailSettingsForm | null }>("/api/admin/email-settings");
      if (response.settings) setSettings(response.settings);
      setLoaded(true);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Email settings could not be loaded");
    }
  }, [onError]);
  useEffect(() => { void load(); }, [load]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy("save");
    try {
      await api("/api/admin/email-settings", {
        method: "PUT",
        body: JSON.stringify({
          enabled: settings.enabled,
          smtpHost: settings.smtpHost,
          smtpPort: settings.smtpPort,
          smtpSecurity: settings.smtpSecurity,
          smtpUsername: settings.smtpUsername || null,
          fromEmail: settings.fromEmail,
          fromName: settings.fromName,
          sendWarnings: settings.sendWarnings,
          sendOverdue: settings.sendOverdue,
          smtpPassword: smtpPassword || null,
          clearPassword,
        }),
      });
      setSmtpPassword("");
      setClearPassword(false);
      await load();
      onMessage("Email settings saved. Use Verify connection before enabling live delivery.");
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Email settings could not be saved");
    } finally {
      setBusy("");
    }
  }
  async function verifyConnection() {
    setBusy("verify");
    try {
      await api("/api/admin/email-settings/verify", { method: "POST" });
      await load();
      onMessage("SMTP connection verified.");
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "SMTP connection could not be verified");
      await load();
    } finally {
      setBusy("");
    }
  }
  async function sendTest() {
    setBusy("test");
    try {
      await api("/api/admin/email-settings/test", {
        method: "POST",
        body: JSON.stringify({ recipient: testRecipient }),
      });
      onMessage(`Test email sent to ${testRecipient}.`);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Test email could not be sent");
    } finally {
      setBusy("");
    }
  }

  if (!loaded) return <section className="panel"><MiniEmpty text="Loading email settings…" /></section>;
  return (
    <div className="admin-layout email-admin">
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">System delivery</p><h2>SMTP connection</h2></div><span className={cx("status-pill", settings.enabled && "success")}>{settings.enabled ? "Enabled" : "Disabled"}</span></div>
        <form className="stack-form" onSubmit={save}>
          <label className="check-row"><input type="checkbox" checked={settings.enabled} onChange={(event) => setSettings((current) => ({ ...current, enabled: event.target.checked }))} />Enable automatic teacher emails</label>
          <label>SMTP host<input value={settings.smtpHost} onChange={(event) => setSettings((current) => ({ ...current, smtpHost: event.target.value }))} placeholder="smtp.school.org" required /></label>
          <div className="form-grid two">
            <label>Port<input type="number" min={1} max={65535} value={settings.smtpPort} onChange={(event) => setSettings((current) => ({ ...current, smtpPort: Number(event.target.value) }))} required /></label>
            <label>Security<select value={settings.smtpSecurity} onChange={(event) => setSettings((current) => ({ ...current, smtpSecurity: event.target.value as EmailSettingsForm["smtpSecurity"] }))}><option value="STARTTLS">STARTTLS</option><option value="TLS">TLS</option><option value="NONE">None</option></select></label>
          </div>
          <label>SMTP username<input autoComplete="username" value={settings.smtpUsername} onChange={(event) => setSettings((current) => ({ ...current, smtpUsername: event.target.value }))} /></label>
          <label>SMTP password<input type="password" autoComplete="new-password" value={smtpPassword} onChange={(event) => setSmtpPassword(event.target.value)} placeholder={settings.passwordConfigured ? "Saved — leave blank to keep" : "Optional for unauthenticated relay"} /><small>The saved password is encrypted in MariaDB and is never returned to the browser.</small></label>
          {settings.passwordConfigured && <label className="check-row danger-check"><input type="checkbox" checked={clearPassword} onChange={(event) => setClearPassword(event.target.checked)} />Remove the saved SMTP password</label>}
          <div className="form-grid two">
            <label>From address<input type="email" value={settings.fromEmail} onChange={(event) => setSettings((current) => ({ ...current, fromEmail: event.target.value }))} placeholder="hallpass@school.org" required /></label>
            <label>From name<input value={settings.fromName} onChange={(event) => setSettings((current) => ({ ...current, fromName: event.target.value }))} required /></label>
          </div>
          <button className="button primary" disabled={Boolean(busy)}>{busy === "save" ? "Saving…" : "Save settings"}</button>
        </form>
      </section>
      <div className="stack-panels">
        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow ink">Events</p><h2>Teacher alerts</h2></div></div>
          <div className="stack-form">
            <label className="check-row"><input type="checkbox" form="none" checked={settings.sendWarnings} onChange={(event) => setSettings((current) => ({ ...current, sendWarnings: event.target.checked }))} />Allow warning-time emails</label>
            <label className="check-row"><input type="checkbox" form="none" checked={settings.sendOverdue} onChange={(event) => setSettings((current) => ({ ...current, sendOverdue: event.target.checked }))} />Allow overdue emails</label>
            <p className="form-note">Each teacher can opt in or out from Account security. Changes above take effect after you save the SMTP form.</p>
          </div>
        </section>
        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow ink">Connection test</p><h2>Verify delivery</h2></div></div>
          {settings.lastVerifiedAt && <div className="alert success">Verified {formatDateAtTimeZone(settings.lastVerifiedAt)}</div>}
          {settings.lastVerifyError && <div className="alert error">Last error: {settings.lastVerifyError}</div>}
          <div className="stack-form">
            <button type="button" className="button quiet" disabled={Boolean(busy) || !settings.smtpHost} onClick={() => void verifyConnection()}>{busy === "verify" ? "Connecting…" : "Verify SMTP connection"}</button>
            <label>Test recipient<input type="email" value={testRecipient} onChange={(event) => setTestRecipient(event.target.value)} placeholder="admin@school.org" /></label>
            <button type="button" className="button quiet" disabled={Boolean(busy) || !testRecipient} onClick={() => void sendTest()}>{busy === "test" ? "Sending…" : "Send test email"}</button>
          </div>
        </section>
      </div>
    </div>
  );
}

interface RetentionRecord {
  id: string;
  school_id: string;
  school_name: string;
  district_id: string;
  district_name: string;
  teacher_name: string;
  name: string;
  period_label: string;
  term_name: string | null;
  available_record_count: number;
  archived_record_count: number;
}

function RecordsRetentionAdmin({
  data,
  currentUser,
  onError,
  onMessage,
}: {
  data: AdminData;
  currentUser: CurrentUser;
  onError: (message: string) => void;
  onMessage: (message: string) => void;
}) {
  const [records, setRecords] = useState<RetentionRecord[]>([]);
  const [search, setSearch] = useState("");
  const [archivingId, setArchivingId] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [termFilter, setTermFilter] = useState("");
  const [teacherFilter, setTeacherFilter] = useState("");
  const scope = administrationScope(currentUser, data);

  const load = useCallback(async () => {
    try {
      const response = await api<{ records: RetentionRecord[] }>("/api/admin/records");
      setRecords(response.records);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Records retention data could not be loaded");
    }
  }, [onError]);
  useEffect(() => { void load(); }, [load]);

  const locationRecords = useMemo(() => records.filter((record) => {
    if (districtFilter && record.district_id !== districtFilter) return false;
    if (schoolFilter && record.school_id !== schoolFilter) return false;
    return true;
  }), [districtFilter, records, schoolFilter]);
  const availableTerms = useMemo(() => [...new Set(locationRecords.map((record) => record.term_name).filter((name): name is string => Boolean(name)))], [locationRecords]);
  const availableTeachers = useMemo(() => [...new Set(locationRecords.map((record) => record.teacher_name))], [locationRecords]);
  useEffect(() => { if (termFilter && !availableTerms.includes(termFilter)) setTermFilter(""); }, [availableTerms, termFilter]);
  useEffect(() => { if (teacherFilter && !availableTeachers.includes(teacherFilter)) setTeacherFilter(""); }, [availableTeachers, teacherFilter]);

  const visibleRecords = records.filter((record) => {
    if (districtFilter && record.district_id !== districtFilter) return false;
    if (schoolFilter && record.school_id !== schoolFilter) return false;
    if (termFilter && record.term_name !== termFilter) return false;
    if (teacherFilter && record.teacher_name !== teacherFilter) return false;
    const needle = search.trim().toLocaleLowerCase();
    if (!needle) return true;
    return [
      record.district_name,
      record.school_name,
      record.teacher_name,
      record.period_label,
      record.name,
      record.term_name ?? "",
    ].some((value) => value.toLocaleLowerCase().includes(needle));
  });

  async function archive(record: RetentionRecord) {
    const count = Number(record.available_record_count);
    if (!count) return;
    if (!window.confirm(
      `Archive ${count} completed pass record${count === 1 ? "" : "s"} for ${record.teacher_name}'s ${record.period_label} · ${record.name}? Archived records are retained in MariaDB but removed from normal history reports. This action cannot be reversed in the application.`,
    )) return;
    setArchivingId(record.id);
    try {
      const result = await api<{ archived: number }>(`/api/admin/classes/${record.id}/archive-history`, { method: "POST" });
      await load();
      onMessage(`${result.archived} completed records were archived for ${record.period_label} · ${record.name}.`);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Records could not be archived");
    } finally {
      setArchivingId("");
    }
  }

  async function restore(record: RetentionRecord) {
    if (!window.confirm(`Restore ${record.archived_record_count} archived records for ${record.period_label} · ${record.name}?`)) return;
    setArchivingId(record.id);
    try { const result = await api<{ restored: number }>(`/api/admin/classes/${record.id}/restore-history`, { method: "POST" }); await load(); onMessage(`${result.restored} records restored.`); }
    catch (caught) { onError(caught instanceof Error ? caught.message : "Records could not be restored"); }
    finally { setArchivingId(""); }
  }

  return (
    <section className="panel records-retention-admin">
      <div className="panel-heading">
        <div>
          <p className="eyebrow ink">Administrator-only retention</p>
          <h2>Archive completed pass records</h2>
          <p className="form-note">This does not archive a class or delete database rows. It hides completed records from the normal history reports; active and waiting passes are never included.</p>
        </div>
        <input className="search-input" placeholder="School, teacher, class, or term" value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>
      <div className="form-grid two">
        {scope.level === "GLOBAL" && <label>District<select value={districtFilter} onChange={(event) => { setDistrictFilter(event.target.value); setSchoolFilter(""); setTermFilter(""); setTeacherFilter(""); }}><option value="">All districts</option>{[...new Map(records.map((record) => [record.district_id, record.district_name])).entries()].map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>}
        {scope.level !== "SCHOOL" && <label>School<select value={schoolFilter} onChange={(event) => { setSchoolFilter(event.target.value); setTermFilter(""); setTeacherFilter(""); }}><option value="">All schools</option>{[...new Map(records.filter((record) => !districtFilter || record.district_id === districtFilter).map((record) => [record.school_id, record.school_name])).entries()].map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>}
        <label>Term<select value={termFilter} onChange={(event) => setTermFilter(event.target.value)}><option value="">All terms</option>{availableTerms.map((name) => <option key={name} value={name}>{name}</option>)}</select></label>
        <label>Teacher<select value={teacherFilter} onChange={(event) => setTeacherFilter(event.target.value)}><option value="">All teachers</option>{availableTeachers.map((name) => <option key={name} value={name}>{name}</option>)}</select></label>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>School</th><th>Teacher</th><th>Class</th><th>Term</th><th className="center-column">Available records</th><th className="center-column">Already archived</th><th /></tr></thead>
          <tbody>
            {visibleRecords.map((record) => (
              <tr key={record.id}>
                <td><strong>{record.school_name}</strong><small>{record.district_name}</small></td>
                <td>{record.teacher_name}</td>
                <td><strong>{record.period_label}</strong><small>{record.name}</small></td>
                <td>{record.term_name ?? "—"}</td>
                <td className="mono center-column">{record.available_record_count}</td>
                <td className="mono center-column">{record.archived_record_count}</td>
                <td>{Boolean(record.available_record_count) ? <button className="button quiet small" disabled={archivingId === record.id} onClick={() => void archive(record)}>{archivingId === record.id ? "Working…" : "Archive records"}</button> : Boolean(record.archived_record_count) ? <button className="text-button" disabled={archivingId === record.id} onClick={() => void restore(record)}>Restore archived</button> : <small className="muted">No completed records available</small>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!visibleRecords.length && <MiniEmpty text="No classes are available in your administrator scope." />}
      </div>
    </section>
  );
}

type BellSlotType = "PERIOD" | "BREAK" | "LUNCH" | "OTHER";
type CalendarOverrideMode = "BELL_SCHEDULE" | "NO_SCHOOL";

interface SchoolBellPeriod {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
}

interface SchoolBellSchedule {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  slots: Array<{ id: string; periodId: string | null; position: number; label: string; slotType: BellSlotType; startsAt: string; endsAt: string }>;
}

interface WeeklyBellSchedule {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  days: Array<{ weekday: number; bellScheduleId: string | null; isNoSchool: boolean }>;
}

interface SchoolCalendarData {
  periods: SchoolBellPeriod[];
  bellSchedules: SchoolBellSchedule[];
  weeklySchedules: WeeklyBellSchedule[];
  weekAssignments: Array<{ id: string; weekStartsOn: string; weeklyScheduleId: string }>;
  dateOverrides: Array<{ id: string; occursOn: string; mode: CalendarOverrideMode; bellScheduleId: string | null; note: string | null }>;
}

interface BellScheduleDraft {
  id?: string;
  name: string;
  description: string;
  active: boolean;
  slots: Array<{ periodId: string | null; label: string; slotType: BellSlotType; startsAt: string; endsAt: string }>;
}

interface WeeklyScheduleDraft {
  id?: string;
  name: string;
  description: string;
  active: boolean;
  days: Array<{ weekday: number; bellScheduleId: string | null; isNoSchool: boolean }>;
}

const calendarWeekdayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function isoCalendarDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function calendarWeekday(date: string): number {
  const day = new Date(`${date}T00:00:00.000Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

function calendarWeekStart(date: string): string {
  const value = new Date(`${date}T00:00:00.000Z`);
  value.setUTCDate(value.getUTCDate() - (calendarWeekday(date) - 1));
  return isoCalendarDate(value);
}

function changeCalendarMonth(month: string, direction: number): string {
  const date = new Date(`${month}-01T00:00:00.000Z`);
  date.setUTCMonth(date.getUTCMonth() + direction);
  return isoCalendarDate(date).slice(0, 7);
}

function calendarMonthDates(month: string): string[] {
  const first = new Date(`${month}-01T00:00:00.000Z`);
  first.setUTCDate(first.getUTCDate() - (calendarWeekday(isoCalendarDate(first)) - 1));
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(first);
    day.setUTCDate(first.getUTCDate() + index);
    return isoCalendarDate(day);
  });
}

function calendarWeekStarts(month: string): string[] {
  return [...new Set(calendarMonthDates(month).map(calendarWeekStart))];
}

function calendarMonthLabel(month: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T00:00:00.000Z`));
}

function blankWeeklyDays(): WeeklyScheduleDraft["days"] {
  return calendarWeekdayNames.map((_, index) => ({ weekday: index + 1, bellScheduleId: null, isNoSchool: index > 4 }));
}

function SchoolCalendarAdmin({
  data,
  currentUser,
  onError,
  onMessage,
}: {
  data: AdminData;
  currentUser: CurrentUser;
  onError: (message: string) => void;
  onMessage: (message: string) => void;
}) {
  const [schoolId, setSchoolId] = useState(data.schools[0]?.id ?? "");
  const [districtId, setDistrictId] = useState(data.schools[0]?.district_id ?? "");
  const [calendar, setCalendar] = useState<SchoolCalendarData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");
  const [month, setMonth] = useState(() => isoCalendarDate(new Date()).slice(0, 7));
  const [newPeriodName, setNewPeriodName] = useState("");
  const [periodDraft, setPeriodDraft] = useState<SchoolBellPeriod | null>(null);
  const [bellDraft, setBellDraft] = useState<BellScheduleDraft | null>(null);
  const bellSlotCount = useRef(0);
  const [weeklyDraft, setWeeklyDraft] = useState<WeeklyScheduleDraft | null>(null);
  const [overrideDraft, setOverrideDraft] = useState<{ date: string; mode: "DEFAULT" | CalendarOverrideMode; bellScheduleId: string | null; note: string } | null>(null);
  const scope = administrationScope(currentUser, data);

  const availableDistricts = useMemo(
    () => data.districts.filter((district) => (scope.level === "GLOBAL" || district.id === scope.districtId) && data.schools.some((school) => school.district_id === district.id)),
    [data.districts, data.schools, scope],
  );
  const districtSchools = useMemo(
    () => data.schools.filter((school) => school.district_id === districtId && (scope.level !== "SCHOOL" || school.id === scope.schoolId)),
    [data.schools, districtId, scope],
  );

  useEffect(() => {
    if (scope.level !== "GLOBAL") setDistrictId(scope.districtId);
    else if (!districtId || !availableDistricts.some((district) => district.id === districtId)) setDistrictId(availableDistricts[0]?.id ?? "");
  }, [availableDistricts, districtId, scope]);

  useEffect(() => {
    if (scope.level === "SCHOOL") setSchoolId(scope.schoolId);
    else if (!districtSchools.some((school) => school.id === schoolId)) setSchoolId(districtSchools[0]?.id ?? "");
  }, [districtSchools, schoolId, scope]);

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      setCalendar(await api<SchoolCalendarData>(`/api/admin/schools/${schoolId}/calendar`));
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "School calendar could not be loaded");
    } finally {
      setLoading(false);
    }
  }, [schoolId, onError]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!bellDraft) { bellSlotCount.current = 0; return; }
    const priorCount = bellSlotCount.current;
    bellSlotCount.current = bellDraft.slots.length;
    if (!priorCount || bellDraft.slots.length !== priorCount + 1) return;
    const previous = bellDraft.slots.at(-2);
    const added = bellDraft.slots.at(-1);
    if (!previous || !added || added.slotType !== "PERIOD") return;
    const periods = (calendar?.periods ?? []).filter((period) => period.active);
    const next = periods[periods.findIndex((period) => period.id === previous.periodId) + 1] ?? periods[0];
    if (!next) return;
    setBellDraft((current) => current && current.slots.length === bellSlotCount.current
      ? { ...current, slots: current.slots.map((slot, index) => index === current.slots.length - 1 ? { ...slot, periodId: next.id, label: next.name } : slot) }
      : current);
  }, [bellDraft, calendar?.periods]);

  useEffect(() => {
    if (!bellDraft) return;
    const rows = Array.from(document.querySelectorAll<HTMLDivElement>(".bell-slot-editor .bell-slot-row"));
    let sourceIndex = -1;
    const cleanups = rows.map((row, index) => {
      row.draggable = true;
      const dragStart = (event: DragEvent) => {
        // Form fields remain easy to edit; drag from the row's empty edge.
        if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement || event.target instanceof HTMLButtonElement) { event.preventDefault(); return; }
        sourceIndex = index; row.classList.add("dragging"); event.dataTransfer?.setData("text/plain", String(index));
      };
      const dragEnd = () => { sourceIndex = -1; row.classList.remove("dragging"); };
      const dragOver = (event: DragEvent) => { if (sourceIndex >= 0) event.preventDefault(); };
      const drop = (event: DragEvent) => {
        event.preventDefault();
        const from = sourceIndex >= 0 ? sourceIndex : Number(event.dataTransfer?.getData("text/plain"));
        if (!Number.isInteger(from) || from < 0 || from === index) return;
        setBellDraft((current) => {
          if (!current || from >= current.slots.length || index >= current.slots.length) return current;
          const slots = [...current.slots]; const [moved] = slots.splice(from, 1); slots.splice(index, 0, moved);
          return { ...current, slots };
        });
      };
      row.addEventListener("dragstart", dragStart); row.addEventListener("dragend", dragEnd); row.addEventListener("dragover", dragOver); row.addEventListener("drop", drop);
      return () => { row.removeEventListener("dragstart", dragStart); row.removeEventListener("dragend", dragEnd); row.removeEventListener("dragover", dragOver); row.removeEventListener("drop", drop); };
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [bellDraft]);

  useEffect(() => {
    if (!calendar) return;
    const list = document.querySelector<HTMLElement>(".calendar-period-list");
    if (!list) return;
    const cards = Array.from(list.querySelectorAll<HTMLElement>(":scope > article"));
    let sourceIndex = -1;
    const cleanups = cards.map((card, index) => {
      card.draggable = true;
      const start = (event: DragEvent) => {
        if (event.target instanceof HTMLButtonElement) { event.preventDefault(); return; }
        sourceIndex = index; card.classList.add("dragging"); event.dataTransfer?.setData("text/plain", String(index));
      };
      const end = () => { sourceIndex = -1; card.classList.remove("dragging"); };
      const over = (event: DragEvent) => { if (sourceIndex >= 0) event.preventDefault(); };
      const drop = (event: DragEvent) => {
        event.preventDefault();
        const from = sourceIndex >= 0 ? sourceIndex : Number(event.dataTransfer?.getData("text/plain"));
        if (!Number.isInteger(from) || from < 0 || from === index) return;
        const periods = [...calendar.periods]; const [moved] = periods.splice(from, 1); periods.splice(index, 0, moved);
        void Promise.all(periods.map((period, position) => api(`/api/admin/schools/${schoolId}/calendar/periods/${period.id}`, { method: "PATCH", body: JSON.stringify({ name: period.name, active: period.active, sortOrder: position + 1 }) })))
          .then(() => load()).then(() => onMessage("Bell period order updated.")).catch((caught) => onError(caught instanceof Error ? caught.message : "Bell period order could not be updated"));
      };
      card.addEventListener("dragstart", start); card.addEventListener("dragend", end); card.addEventListener("dragover", over); card.addEventListener("drop", drop);
      return () => { card.removeEventListener("dragstart", start); card.removeEventListener("dragend", end); card.removeEventListener("dragover", over); card.removeEventListener("drop", drop); };
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [calendar, load, onError, onMessage, schoolId]);

  const selectedSchool = data.schools.find((school) => school.id === schoolId);
  const monthDates = useMemo(() => calendarMonthDates(month), [month]);
  const weekStarts = useMemo(() => calendarWeekStarts(month), [month]);
  const schedulesById = useMemo(() => new Map((calendar?.bellSchedules ?? []).map((schedule) => [schedule.id, schedule])), [calendar]);
  const weeklyById = useMemo(() => new Map((calendar?.weeklySchedules ?? []).map((schedule) => [schedule.id, schedule])), [calendar]);
  const assignmentsByWeek = useMemo(() => new Map((calendar?.weekAssignments ?? []).map((assignment) => [assignment.weekStartsOn, assignment])), [calendar]);
  const overridesByDate = useMemo(() => new Map((calendar?.dateOverrides ?? []).map((override) => [override.occursOn, override])), [calendar]);

  function calendarDay(date: string) {
    const override = overridesByDate.get(date);
    if (override) {
      return override.mode === "NO_SCHOOL"
        ? { title: "No school", note: override.note, source: "override" }
        : { title: schedulesById.get(override.bellScheduleId ?? "")?.name ?? "Bell schedule", note: override.note, source: "override" };
    }
    const assignment = assignmentsByWeek.get(calendarWeekStart(date));
    const weekly = assignment ? weeklyById.get(assignment.weeklyScheduleId) : null;
    const day = weekly?.days.find((item) => item.weekday === calendarWeekday(date));
    if (!weekly || !day) return { title: "Unassigned", note: null, source: "empty" };
    if (day.isNoSchool) return { title: "No school", note: weekly.name, source: "weekly" };
    if (!day.bellScheduleId) return { title: "No bell schedule", note: weekly.name, source: "weekly" };
    return { title: schedulesById.get(day.bellScheduleId)?.name ?? "Bell schedule", note: weekly.name, source: "weekly" };
  }

  async function createPeriod(event: FormEvent) {
    event.preventDefault();
    setSaving("period-create");
    try {
      await api(`/api/admin/schools/${schoolId}/calendar/periods`, { method: "POST", body: JSON.stringify({ name: newPeriodName, sortOrder: (calendar?.periods.length ?? 0) + 1 }) });
      setNewPeriodName(""); await load(); onMessage("Bell period added.");
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Bell period could not be added"); }
    finally { setSaving(""); }
  }

  async function savePeriod(event: FormEvent) {
    event.preventDefault();
    if (!periodDraft) return;
    setSaving("period-save");
    try {
      await api(`/api/admin/schools/${schoolId}/calendar/periods/${periodDraft.id}`, { method: "PATCH", body: JSON.stringify(periodDraft) });
      setPeriodDraft(null); await load(); onMessage("Bell period saved.");
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Bell period could not be saved"); }
    finally { setSaving(""); }
  }

  function newBellSchedule(): BellScheduleDraft {
    const firstPeriod = calendar?.periods.find((period) => period.active) ?? null;
    return {
      name: "", description: "", active: true,
      slots: [{ periodId: firstPeriod?.id ?? null, label: firstPeriod?.name ?? "", slotType: firstPeriod ? "PERIOD" : "OTHER", startsAt: "08:00", endsAt: "08:50" }],
    };
  }

  function editBellSchedule(schedule: SchoolBellSchedule) {
    setBellDraft({ id: schedule.id, name: schedule.name, description: schedule.description ?? "", active: schedule.active, slots: schedule.slots.map((slot) => ({ periodId: slot.periodId, label: slot.label, slotType: slot.slotType, startsAt: slot.startsAt, endsAt: slot.endsAt })) });
  }

  function changeBellSlot(index: number, change: Partial<BellScheduleDraft["slots"][number]>) {
    setBellDraft((current) => current ? { ...current, slots: current.slots.map((slot, slotIndex) => slotIndex === index ? { ...slot, ...change } : slot) } : current);
  }

  async function saveBellSchedule(event: FormEvent) {
    event.preventDefault();
    if (!bellDraft) return;
    setSaving("bell-save");
    try {
      const endpoint = bellDraft.id
        ? `/api/admin/schools/${schoolId}/calendar/bell-schedules/${bellDraft.id}`
        : `/api/admin/schools/${schoolId}/calendar/bell-schedules`;
      await api(endpoint, { method: bellDraft.id ? "PATCH" : "POST", body: JSON.stringify(bellDraft) });
      setBellDraft(null); await load(); onMessage("Bell schedule saved.");
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Bell schedule could not be saved"); }
    finally { setSaving(""); }
  }

  function editWeeklySchedule(schedule: WeeklyBellSchedule) {
    const days = blankWeeklyDays().map((fallback) => {
      const day = schedule.days.find((item) => item.weekday === fallback.weekday);
      return day ? { ...day } : fallback;
    });
    setWeeklyDraft({ id: schedule.id, name: schedule.name, description: schedule.description ?? "", active: schedule.active, days });
  }

  function changeWeeklyDay(weekday: number, value: string) {
    setWeeklyDraft((current) => current ? {
      ...current,
      days: current.days.map((day) => day.weekday !== weekday ? day : value === "__NO_SCHOOL__"
        ? { ...day, bellScheduleId: null, isNoSchool: true }
        : { ...day, bellScheduleId: value || null, isNoSchool: false }),
    } : current);
  }

  async function saveWeeklySchedule(event: FormEvent) {
    event.preventDefault();
    if (!weeklyDraft) return;
    setSaving("weekly-save");
    try {
      const endpoint = weeklyDraft.id
        ? `/api/admin/schools/${schoolId}/calendar/weekly-schedules/${weeklyDraft.id}`
        : `/api/admin/schools/${schoolId}/calendar/weekly-schedules`;
      await api(endpoint, { method: weeklyDraft.id ? "PATCH" : "POST", body: JSON.stringify(weeklyDraft) });
      setWeeklyDraft(null); await load(); onMessage("Weekly schedule saved.");
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Weekly schedule could not be saved"); }
    finally { setSaving(""); }
  }

  async function saveWeek(weekStart: string, weeklyScheduleId: string) {
    setSaving(`week:${weekStart}`);
    try {
      await api(`/api/admin/schools/${schoolId}/calendar/weeks/${weekStart}`, { method: "PUT", body: JSON.stringify({ weeklyScheduleId: weeklyScheduleId || null }) });
      await load(); onMessage(weeklyScheduleId ? "Weekly calendar assignment saved." : "Weekly calendar assignment cleared.");
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Weekly calendar assignment could not be saved"); }
    finally { setSaving(""); }
  }

  async function saveOverride(event: FormEvent) {
    event.preventDefault();
    if (!overrideDraft) return;
    setSaving("override-save");
    try {
      await api(`/api/admin/schools/${schoolId}/calendar/days/${overrideDraft.date}`, { method: "PUT", body: JSON.stringify(overrideDraft) });
      setOverrideDraft(null); await load(); onMessage("Calendar day saved.");
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Calendar day could not be saved"); }
    finally { setSaving(""); }
  }

  if (!schoolId) return <section className="panel"><MiniEmpty text="Your administrator role does not currently include a school." /></section>;
  if (loading && !calendar) return <section className="panel"><MiniEmpty text="Loading school calendar…" /></section>;
  if (!calendar) return null;

  return (
    <div className="school-calendar-admin">
      <section className="panel calendar-intro">
        <div className="panel-heading">
          <div><p className="eyebrow ink">School scheduling</p><h2>School calendar & bell schedules</h2><p className="form-note">Build named daily schedules, combine them into weekly patterns, then assign those patterns by week with one-day overrides. Teachers may opt individual classes into schedule-aware restroom rules; all other pass behavior stays unchanged.</p></div>
          <div className="calendar-school-selectors">
            {scope.level === "GLOBAL" && <label className="calendar-school-select"><span>District</span><select value={districtId} onChange={(event) => { const nextDistrictId = event.target.value; setDistrictId(nextDistrictId); setSchoolId(data.schools.find((school) => school.district_id === nextDistrictId)?.id ?? ""); }}>{availableDistricts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label>}
            {scope.level !== "SCHOOL" && <label className="calendar-school-select"><span>School</span><select value={schoolId} onChange={(event) => setSchoolId(event.target.value)} disabled={!districtSchools.length}>{districtSchools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select></label>}
          </div>
        </div>
        {selectedSchool && <span className="status-pill">{selectedSchool.district_name} · {selectedSchool.name}</span>}
      </section>

      <div className="calendar-workspace">
        <section className="panel calendar-month-panel">
          <div className="panel-heading calendar-month-heading">
            <div><p className="eyebrow ink">Calendar assignment</p><h2>{calendarMonthLabel(month)}</h2></div>
            <div className="calendar-nav"><button type="button" className="button quiet small" onClick={() => setMonth((value) => changeCalendarMonth(value, -1))}>‹</button><input aria-label="Calendar month" type="month" value={month} onChange={(event) => setMonth(event.target.value)} /><button type="button" className="button quiet small" onClick={() => setMonth((value) => changeCalendarMonth(value, 1))}>›</button></div>
          </div>
          <p className="form-note">Select a date to set a test/minimum-day schedule, a holiday, or return it to the weekly pattern. A date override always wins over the assigned week.</p>
          <div className="school-calendar-grid" role="grid" aria-label={`${calendarMonthLabel(month)} school calendar`}>
            {calendarWeekdayNames.map((name) => <div className="calendar-weekday" key={name}>{name.slice(0, 3)}</div>)}
            {monthDates.map((date) => {
              const detail = calendarDay(date);
              const isCurrentMonth = date.startsWith(month);
              return <button type="button" key={date} className={cx("calendar-day", !isCurrentMonth && "outside-month", detail.source === "override" && "override", detail.title === "No school" && "no-school")} onClick={() => {
                const override = overridesByDate.get(date);
                setOverrideDraft(override
                  ? { date, mode: override.mode, bellScheduleId: override.bellScheduleId, note: override.note ?? "" }
                  : { date, mode: "DEFAULT", bellScheduleId: null, note: "" });
              }}>
                <strong>{Number(date.slice(-2))}</strong><span>{detail.title}</span>{detail.note && <small>{detail.note}</small>}
              </button>;
            })}
          </div>
          <div className="calendar-key"><span><i className="calendar-key-weekly" />Weekly pattern</span><span><i className="calendar-key-override" />Date override</span><span><i className="calendar-key-empty" />Unassigned</span></div>
        </section>

        <section className="panel calendar-week-assignments">
          <div className="panel-heading"><div><p className="eyebrow ink">Build the year</p><h2>Weekly assignments</h2></div></div>
          <p className="form-note">Assign a named weekly pattern to each Monday. Use date overrides above for exceptions.</p>
          <div className="table-wrap"><table><thead><tr><th>Week of</th><th>Weekly pattern</th></tr></thead><tbody>
            {weekStarts.map((weekStart) => <tr key={weekStart}><td className="mono">{weekStart}</td><td><select aria-label={`Weekly schedule for ${weekStart}`} value={assignmentsByWeek.get(weekStart)?.weeklyScheduleId ?? ""} disabled={saving === `week:${weekStart}`} onChange={(event) => void saveWeek(weekStart, event.target.value)}><option value="">Unassigned</option>{calendar.weeklySchedules.map((schedule) => <option key={schedule.id} value={schedule.id}>{schedule.name}{!schedule.active ? " (inactive)" : ""}</option>)}</select></td></tr>)}
          </tbody></table></div>
          {!calendar.weeklySchedules.length && <MiniEmpty text="Create a weekly pattern below before assigning calendar weeks." />}
        </section>
      </div>

      <div className="calendar-template-grid">
        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow ink">Shared building blocks</p><h2>Bell periods</h2></div></div>
          <p className="form-note">Set the school-wide period names once, then choose which ones occur in each named bell schedule. Inactive periods remain on old schedules but are not offered for new rows.</p>
          <form className="inline-form" onSubmit={createPeriod}><input value={newPeriodName} onChange={(event) => setNewPeriodName(event.target.value)} placeholder="e.g. Period 1" required /><button className="button primary small" disabled={saving === "period-create"}>{saving === "period-create" ? "Adding…" : "Add period"}</button></form>
          <div className="calendar-template-list calendar-period-list">{calendar.periods.map((period) => <article key={period.id}><div><strong>{period.name}</strong><small>Display order {period.sortOrder || "—"} · {period.active ? "Available" : "Inactive"}</small></div><button type="button" className="text-button" onClick={() => setPeriodDraft({ ...period })}>Edit</button></article>)}{!calendar.periods.length && <MiniEmpty text="Add the periods used by this school." />}</div>
        </section>

        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow ink">Named daily templates</p><h2>Bell schedules</h2></div><button type="button" className="button quiet small" onClick={() => setBellDraft(newBellSchedule())}>New schedule</button></div>
          <p className="form-note">Examples: Regular day, Minimum day, Testing day. Editing a template updates its calendar references; create a new template when preserving a prior version matters.</p>
          <div className="calendar-template-list">{calendar.bellSchedules.map((schedule) => <article key={schedule.id}><div><strong>{schedule.name}</strong><small>{schedule.description?.trim() || "No description"}{!schedule.active ? " · Inactive" : ""}</small></div><button type="button" className="text-button" onClick={() => editBellSchedule(schedule)}>Edit</button></article>)}{!calendar.bellSchedules.length && <MiniEmpty text="Create a daily bell schedule after adding periods." />}</div>
        </section>

        <section className="panel calendar-weekly-templates">
          <div className="panel-heading"><div><p className="eyebrow ink">Named weekly templates</p><h2>Weekly schedules</h2></div><button type="button" className="button quiet small" onClick={() => setWeeklyDraft({ name: "", description: "", active: true, days: blankWeeklyDays() })}>New weekly pattern</button></div>
          <p className="form-note">Examples: Standard week or Finals week. Each day can use one bell schedule, be a no-school day, or stay unassigned.</p>
          <div className="calendar-template-list">{calendar.weeklySchedules.map((schedule) => <article key={schedule.id}><div><strong>{schedule.name}</strong><small>{schedule.days.filter((day) => day.bellScheduleId || day.isNoSchool).length} configured day{schedule.days.filter((day) => day.bellScheduleId || day.isNoSchool).length === 1 ? "" : "s"}{!schedule.active ? " · Inactive" : ""}</small></div><button type="button" className="text-button" onClick={() => editWeeklySchedule(schedule)}>Edit</button></article>)}{!calendar.weeklySchedules.length && <MiniEmpty text="Create a weekly schedule after creating daily bell schedules." />}</div>
        </section>
      </div>

      {periodDraft && <div className="dialog-backdrop" role="presentation" onMouseDown={() => setPeriodDraft(null)}><form className="dialog" onSubmit={savePeriod} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Bell period</p><h2>Edit period</h2></div><button type="button" className="icon-button" onClick={() => setPeriodDraft(null)}>×</button></div><label>Period name<input value={periodDraft.name} onChange={(event) => setPeriodDraft({ ...periodDraft, name: event.target.value })} required /></label><label>Display order<input type="number" min={0} max={999} value={periodDraft.sortOrder} onChange={(event) => setPeriodDraft({ ...periodDraft, sortOrder: Number(event.target.value) })} /></label><label className="check-row"><input type="checkbox" checked={periodDraft.active} onChange={(event) => setPeriodDraft({ ...periodDraft, active: event.target.checked })} />Available for new bell schedules</label><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setPeriodDraft(null)}>Cancel</button><button className="button primary" disabled={saving === "period-save"}>{saving === "period-save" ? "Saving…" : "Save period"}</button></div></form></div>}

      {bellDraft && <div className="dialog-backdrop" role="presentation" onMouseDown={() => setBellDraft(null)}><form className="dialog calendar-editor-dialog" onSubmit={saveBellSchedule} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Daily template</p><h2>{bellDraft.id ? "Edit bell schedule" : "New bell schedule"}</h2></div><button type="button" className="icon-button" onClick={() => setBellDraft(null)}>×</button></div><label>Name<input value={bellDraft.name} onChange={(event) => setBellDraft({ ...bellDraft, name: event.target.value })} placeholder="e.g. Minimum day" required /></label><label>Description<input value={bellDraft.description} onChange={(event) => setBellDraft({ ...bellDraft, description: event.target.value })} placeholder="Optional planning note" /></label><label className="check-row"><input type="checkbox" checked={bellDraft.active} onChange={(event) => setBellDraft({ ...bellDraft, active: event.target.checked })} />Available for new calendar assignments</label><div className="bell-slot-editor"><div className="bell-slot-header"><span>Type</span><span>Period / label</span><span>Starts</span><span>Ends</span><span /></div>{bellDraft.slots.map((slot, index) => <div className="bell-slot-row" key={`${index}-${slot.startsAt}`}><select value={slot.slotType} onChange={(event) => { const slotType = event.target.value as BellSlotType; const period = calendar.periods.find((item) => item.active) ?? null; changeBellSlot(index, slotType === "PERIOD" ? { slotType, periodId: slot.periodId ?? period?.id ?? null, label: period?.name ?? slot.label } : { slotType, periodId: null }); }}><option value="PERIOD">Period</option><option value="BREAK">Break</option><option value="LUNCH">Lunch</option><option value="OTHER">Other</option></select>{slot.slotType === "PERIOD" ? <select value={slot.periodId ?? ""} onChange={(event) => { const period = calendar.periods.find((item) => item.id === event.target.value); changeBellSlot(index, { periodId: event.target.value || null, label: period?.name ?? "" }); }}><option value="" disabled>Select period</option>{calendar.periods.map((period) => <option key={period.id} value={period.id}>{period.name}{!period.active ? " (inactive)" : ""}</option>)}</select> : <input value={slot.label} onChange={(event) => changeBellSlot(index, { label: event.target.value })} placeholder="e.g. Lunch" required />}<BellScheduleTimeInput value={slot.startsAt} label={`Start time for ${slot.label || `time block ${index + 1}`}`} listId={`bell-start-${index}`} onChange={(startsAt) => changeBellSlot(index, { startsAt })} /><BellScheduleTimeInput value={slot.endsAt} label={`End time for ${slot.label || `time block ${index + 1}`}`} listId={`bell-end-${index}`} onChange={(endsAt) => changeBellSlot(index, { endsAt })} /><button type="button" className="icon-button" aria-label="Remove time block" disabled={bellDraft.slots.length === 1} onClick={() => setBellDraft({ ...bellDraft, slots: bellDraft.slots.filter((_, slotIndex) => slotIndex !== index) })}>×</button></div>)}</div><button type="button" className="button quiet small" onClick={() => { const firstPeriod = calendar.periods.find((period) => period.active); setBellDraft({ ...bellDraft, slots: [...bellDraft.slots, { periodId: firstPeriod?.id ?? null, label: firstPeriod?.name ?? "", slotType: firstPeriod ? "PERIOD" : "OTHER", startsAt: "09:00", endsAt: "09:50" }] }); }}>Add time block</button><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setBellDraft(null)}>Cancel</button><button className="button primary" disabled={saving === "bell-save"}>{saving === "bell-save" ? "Saving…" : "Save bell schedule"}</button></div></form></div>}

      {weeklyDraft && <div className="dialog-backdrop" role="presentation" onMouseDown={() => setWeeklyDraft(null)}><form className="dialog calendar-editor-dialog" onSubmit={saveWeeklySchedule} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Weekly template</p><h2>{weeklyDraft.id ? "Edit weekly schedule" : "New weekly schedule"}</h2></div><button type="button" className="icon-button" onClick={() => setWeeklyDraft(null)}>×</button></div><label>Name<input value={weeklyDraft.name} onChange={(event) => setWeeklyDraft({ ...weeklyDraft, name: event.target.value })} placeholder="e.g. Standard week" required /></label><label>Description<input value={weeklyDraft.description} onChange={(event) => setWeeklyDraft({ ...weeklyDraft, description: event.target.value })} placeholder="Optional planning note" /></label><label className="check-row"><input type="checkbox" checked={weeklyDraft.active} onChange={(event) => setWeeklyDraft({ ...weeklyDraft, active: event.target.checked })} />Available for new calendar assignments</label><div className="weekly-day-editor">{weeklyDraft.days.map((day) => <label key={day.weekday}>{calendarWeekdayNames[day.weekday - 1]}<select value={day.isNoSchool ? "__NO_SCHOOL__" : day.bellScheduleId ?? ""} onChange={(event) => changeWeeklyDay(day.weekday, event.target.value)}><option value="">No bell schedule</option><option value="__NO_SCHOOL__">No school</option>{calendar.bellSchedules.map((schedule) => <option key={schedule.id} value={schedule.id}>{schedule.name}{!schedule.active ? " (inactive)" : ""}</option>)}</select></label>)}</div><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setWeeklyDraft(null)}>Cancel</button><button className="button primary" disabled={saving === "weekly-save"}>{saving === "weekly-save" ? "Saving…" : "Save weekly schedule"}</button></div></form></div>}

      {overrideDraft && <div className="dialog-backdrop" role="presentation" onMouseDown={() => setOverrideDraft(null)}><form className="dialog" onSubmit={saveOverride} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Date override</p><h2>{overrideDraft.date}</h2></div><button type="button" className="icon-button" onClick={() => setOverrideDraft(null)}>×</button></div><label>Use<select value={overrideDraft.mode} onChange={(event) => setOverrideDraft({ ...overrideDraft, mode: event.target.value as typeof overrideDraft.mode, bellScheduleId: event.target.value === "BELL_SCHEDULE" ? overrideDraft.bellScheduleId : null })}><option value="DEFAULT">Weekly pattern</option><option value="BELL_SCHEDULE">Specific bell schedule</option><option value="NO_SCHOOL">No school</option></select></label>{overrideDraft.mode === "BELL_SCHEDULE" && <label>Bell schedule<select value={overrideDraft.bellScheduleId ?? ""} onChange={(event) => setOverrideDraft({ ...overrideDraft, bellScheduleId: event.target.value || null })} required><option value="" disabled>Select schedule</option>{calendar.bellSchedules.map((schedule) => <option key={schedule.id} value={schedule.id}>{schedule.name}{!schedule.active ? " (inactive)" : ""}</option>)}</select></label>}<label>Note<input value={overrideDraft.note} onChange={(event) => setOverrideDraft({ ...overrideDraft, note: event.target.value })} placeholder="e.g. State testing" /></label><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setOverrideDraft(null)}>Cancel</button><button className="button primary" disabled={saving === "override-save"}>{saving === "override-save" ? "Saving…" : "Save date"}</button></div></form></div>}
    </div>
  );
}

function SchoolAdmin({
  data,
  currentUser,
  refresh,
  onError,
}: {
  data: AdminData;
  currentUser: CurrentUser;
  refresh: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const global = currentUser.roles.some((role) => role.role === "GLOBAL_ADMIN");
  const scope = administrationScope(currentUser, data);
  const [districtName, setDistrictName] = useState("");
  const [districtCode, setDistrictCode] = useState("");
  const [districtTimezone, setDistrictTimezone] = useState(DEFAULT_TIME_ZONE);
  const [districtTimezones, setDistrictTimezones] = useState<Record<string, string>>({});
  const [expandedDistricts, setExpandedDistricts] = useState<Record<string, boolean>>({});
  const [schoolName, setSchoolName] = useState("");
  const [schoolCode, setSchoolCode] = useState("");
  const [districtDialogOpen, setDistrictDialogOpen] = useState(false);
  const [schoolDialogDistrictId, setSchoolDialogDistrictId] = useState<string | null>(null);
  const organizationTotals = data.schools.reduce((total, school) => ({
    classes: total.classes + Number(school.active_class_count ?? 0),
    teachers: total.teachers + Number(school.teacher_count ?? 0),
    students: total.students + Number(school.student_count ?? 0),
    passes: total.passes + Number(school.pass_count ?? 0),
    active: total.active + Number(school.active_pass_count ?? 0),
  }), { classes: 0, teachers: 0, students: 0, passes: 0, active: 0 });

  async function createDistrict(event: FormEvent) {
    event.preventDefault();
    try {
      await api("/api/admin/districts", { method: "POST", body: JSON.stringify({ name: districtName, code: districtCode, timezone: districtTimezone }) });
      setDistrictName(""); setDistrictCode(""); setDistrictTimezone(DEFAULT_TIME_ZONE); setDistrictDialogOpen(false); await refresh();
    } catch (caught) { onError(caught instanceof Error ? caught.message : "District could not be created"); }
  }
  async function createSchool(event: FormEvent) {
    event.preventDefault();
    if (!schoolDialogDistrictId) return;
    try {
      await api("/api/admin/schools", { method: "POST", body: JSON.stringify({ districtId: schoolDialogDistrictId, name: schoolName, code: schoolCode }) });
      setSchoolName(""); setSchoolCode(""); setSchoolDialogDistrictId(null); await refresh();
    } catch (caught) { onError(caught instanceof Error ? caught.message : "School could not be created"); }
  }
  async function saveDistrictTimezone(districtId: string, timezone: string) {
    try {
      await api(`/api/admin/districts/${districtId}`, {
        method: "PATCH",
        body: JSON.stringify({ timezone }),
      });
      await refresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "District timezone could not be saved");
    }
  }
  function canEditDistrict(districtId: string) {
    return global || currentUser.roles.some(
      (role) => role.role === "DISTRICT_ADMIN" && role.scope_id === districtId,
    );
  }
  return (
    <div className="admin-layout school-admin-layout">
      <datalist id="district-timezones">
        {TIME_ZONE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </datalist>
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">{data.schools.length} schools</p><h2>Organization</h2></div>{global && <button type="button" className="button quiet small" onClick={() => setDistrictDialogOpen(true)}>+ Add district</button>}</div>
        <div className="organization-total-metrics" aria-label="Organization totals">
          <Metric value={String(organizationTotals.classes)} label="Active classes" />
          <Metric value={String(organizationTotals.teachers)} label="Teachers" />
          <Metric value={String(organizationTotals.students)} label="Students" />
          <Metric value={String(organizationTotals.active)} label="Passes open now" />
          <Metric value={String(organizationTotals.passes)} label="Recorded passes" />
        </div>
        <div className="organization-list">
          {data.districts.map((district) => {
            const expanded = Boolean(expandedDistricts[district.id]);
            return <article key={district.id}>
              <button type="button" className="district-toggle" aria-expanded={expanded} onClick={() => setExpandedDistricts((current) => ({ ...current, [district.id]: !expanded }))}>
                <span><strong>{district.name}</strong><small>{district.code}</small></span>
                <span className="district-toggle-summary">{district.school_count ?? 0} {district.school_count === 1 ? "school" : "schools"}</span>
                <span className="district-toggle-icon" aria-hidden="true">{expanded ? "−" : "+"}</span>
              </button>
              {expanded && <>
                <div className="organization-metrics" aria-label={`${district.name} totals`}>
                  <span><strong>{district.school_count ?? 0}</strong> schools</span>
                  <span><strong>{district.active_class_count ?? 0}</strong> classes</span>
                  <span><strong>{district.teacher_count ?? 0}</strong> teachers</span>
                  <span><strong>{district.student_count ?? 0}</strong> students</span>
                  <span><strong>{district.active_pass_count ?? 0}</strong> open now</span>
                  <span><strong>{district.pass_count ?? 0}</strong> passes</span>
                </div>
                <div className="district-timezone">
                  <label>Timezone<input list="district-timezones" value={districtTimezones[district.id] ?? district.timezone} onChange={(event) => setDistrictTimezones((current) => ({ ...current, [district.id]: event.target.value }))} disabled={!canEditDistrict(district.id)} /></label>
                  {canEditDistrict(district.id) && <button className="button quiet small" onClick={() => void saveDistrictTimezone(district.id, districtTimezones[district.id] ?? district.timezone)}>Save timezone</button>}
                </div>
                <div>{data.schools.filter((school) => school.district_id === district.id).map((school) => (
                  <p key={school.id}>
                    <span className="status-dot" />
                    <span><strong>{school.name}</strong><small>{school.code}</small></span>
                    <span className="school-metrics"><span>{school.active_class_count ?? 0} classes</span><span>{school.teacher_count ?? 0} teachers</span><span>{school.student_count ?? 0} students</span><span>{school.active_pass_count ?? 0} open</span><span>{school.pass_count ?? 0} passes</span></span>
                  </p>
                ))}</div>
                {scope.level !== "SCHOOL" && canEditDistrict(district.id) && <div className="organization-add-school"><button type="button" className="button quiet small" onClick={() => setSchoolDialogDistrictId(district.id)}>+ Add school</button></div>}
              </>}
            </article>;
          })}
          {!data.districts.length && <MiniEmpty text="Create the first district to begin." />}
        </div>
      </section>
      <DistrictTermAdmin data={data} currentUser={currentUser} refresh={refresh} onError={onError} />
      <TermAdmin data={data} currentUser={currentUser} refresh={refresh} onError={onError} />
      {districtDialogOpen && <div className="dialog-backdrop" role="presentation" onMouseDown={() => setDistrictDialogOpen(false)}><form className="dialog" onSubmit={createDistrict} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Global structure</p><h2>Add a district</h2></div><button type="button" className="icon-button" onClick={() => setDistrictDialogOpen(false)} aria-label="Close add district">×</button></div><label>District name<input value={districtName} onChange={(event) => setDistrictName(event.target.value)} required autoFocus /></label><label>District code<input value={districtCode} onChange={(event) => setDistrictCode(event.target.value)} required /></label><label>Timezone<input list="district-timezones" value={districtTimezone} onChange={(event) => setDistrictTimezone(event.target.value)} placeholder="America/Los_Angeles" required /></label><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setDistrictDialogOpen(false)}>Cancel</button><button className="button primary">Create district</button></div></form></div>}
      {schoolDialogDistrictId && <div className="dialog-backdrop" role="presentation" onMouseDown={() => setSchoolDialogDistrictId(null)}><form className="dialog" onSubmit={createSchool} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">{data.districts.find((district) => district.id === schoolDialogDistrictId)?.name ?? "District"}</p><h2>Add a school</h2></div><button type="button" className="icon-button" onClick={() => setSchoolDialogDistrictId(null)} aria-label="Close add school">×</button></div><label>School name<input value={schoolName} onChange={(event) => setSchoolName(event.target.value)} required autoFocus /></label><label>School code<input value={schoolCode} onChange={(event) => setSchoolCode(event.target.value)} required /></label><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setSchoolDialogDistrictId(null)}>Cancel</button><button className="button primary">Create school</button></div></form></div>}
    </div>
  );
}

function DistrictTermAdmin({ data, currentUser, refresh, onError }: { data: AdminData; currentUser: CurrentUser; refresh: () => Promise<void>; onError: (message: string) => void }) {
  const scope = administrationScope(currentUser, data);
  const [districtId, setDistrictId] = useState(data.districts[0]?.id ?? "");
  const [name, setName] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [editing, setEditing] = useState<AdminData["districtTerms"][number] | null>(null);
  useEffect(() => {
    if (scope.level !== "GLOBAL") setDistrictId(scope.districtId);
    else if (!districtId && data.districts[0]) setDistrictId(data.districts[0].id);
  }, [data.districts, districtId, scope]);
  if (scope.level === "SCHOOL") return null;
  async function create(event: FormEvent) {
    event.preventDefault();
    try { await api("/api/admin/district-terms", { method: "POST", body: JSON.stringify({ districtId, name, startsOn, endsOn }) }); setName(""); setStartsOn(""); setEndsOn(""); await refresh(); }
    catch (caught) { onError(caught instanceof Error ? caught.message : "District term could not be created"); }
  }

  async function save(event: FormEvent) { event.preventDefault(); if (!editing) return; try { await api(`/api/admin/district-terms/${editing.id}`, { method: "PATCH", body: JSON.stringify({ name: editing.name, startsOn: editing.starts_on, endsOn: editing.ends_on, active: Boolean(editing.active) }) }); setEditing(null); await refresh(); } catch (caught) { onError(caught instanceof Error ? caught.message : "District term could not be saved"); } }
  if (scope.level === "DISTRICT") return (
    <section className="panel term-admin">
      <div className="panel-heading"><div><p className="eyebrow ink">District calendars</p><h2>District academic terms</h2></div></div>
      <p className="form-note">These terms apply to every school in your district.</p>
      <form className="form-grid term-create" onSubmit={create}>
        <label>Term name<input value={name} onChange={(event) => setName(event.target.value)} required /></label>
        <label>Starts<input type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} required /></label>
        <label>Ends<input type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} required /></label>
        <button className="button quiet">Add district term</button>
      </form>
      <div className="table-wrap"><table><thead><tr><th>Term</th><th>Dates</th><th>Status</th><th /></tr></thead><tbody>{data.districtTerms.filter((term) => term.district_id === districtId).map((term) => <tr key={term.id}><td><strong>{term.name}</strong></td><td>{term.starts_on} to {term.ends_on}</td><td>{term.active ? "Active" : "Inactive"}</td><td><button className="text-button" onClick={() => setEditing({ ...term })}>Edit</button></td></tr>)}</tbody></table></div>
      {editing && <div className="dialog-backdrop" onMouseDown={() => setEditing(null)}><form className="dialog" onSubmit={save} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><h2>Edit district term</h2><button type="button" className="icon-button" onClick={() => setEditing(null)}>×</button></div><label>Name<input value={editing.name} onChange={(event) => setEditing({ ...editing, name: event.target.value })} required /></label><label>Starts<input type="date" value={editing.starts_on ?? ""} onChange={(event) => setEditing({ ...editing, starts_on: event.target.value })} required /></label><label>Ends<input type="date" value={editing.ends_on ?? ""} onChange={(event) => setEditing({ ...editing, ends_on: event.target.value })} required /></label><label className="check-row"><input type="checkbox" checked={Boolean(editing.active)} onChange={(event) => setEditing({ ...editing, active: Number(event.target.checked) })} />Active</label><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setEditing(null)}>Cancel</button><button className="button primary">Save term</button></div></form></div>}
    </section>
  );
  return <><section className="panel term-admin"><div className="panel-heading"><div><p className="eyebrow ink">District calendars</p><h2>District academic terms</h2></div></div><p className="form-note">These terms apply to every school in the district. School-specific terms below remain independent.</p><form className="form-grid term-create" onSubmit={create}><label>District<select value={districtId} onChange={(event) => setDistrictId(event.target.value)} required>{data.districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label><label>Term name<input value={name} onChange={(event) => setName(event.target.value)} required /></label><label>Starts<input type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} required /></label><label>Ends<input type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} required /></label><button className="button quiet">Add district term</button></form><div className="table-wrap"><table><thead><tr><th>Term</th><th>Dates</th><th>Status</th><th /></tr></thead><tbody>{data.districtTerms.filter((term) => term.district_id === districtId).map((term) => <tr key={term.id}><td><strong>{term.name}</strong></td><td>{term.starts_on} to {term.ends_on}</td><td>{term.active ? "Active" : "Inactive"}</td><td><button className="text-button" onClick={() => setEditing({ ...term })}>Edit</button></td></tr>)}</tbody></table></div></section>{editing && <div className="dialog-backdrop" onMouseDown={() => setEditing(null)}><form className="dialog" onSubmit={save} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><h2>Edit district term</h2><button type="button" className="icon-button" onClick={() => setEditing(null)}>×</button></div><label>Name<input value={editing.name} onChange={(event) => setEditing({ ...editing, name: event.target.value })} required /></label><label>Starts<input type="date" value={editing.starts_on ?? ""} onChange={(event) => setEditing({ ...editing, starts_on: event.target.value })} required /></label><label>Ends<input type="date" value={editing.ends_on ?? ""} onChange={(event) => setEditing({ ...editing, ends_on: event.target.value })} required /></label><label className="check-row"><input type="checkbox" checked={Boolean(editing.active)} onChange={(event) => setEditing({ ...editing, active: Number(event.target.checked) })} />Active</label><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setEditing(null)}>Cancel</button><button className="button primary">Save term</button></div></form></div>}</>;
}

interface AdminStudent {
  id: string;
  school_id: string;
  student_number: string;
  first_name: string;
  last_name: string;
  preferred_first_name: string | null;
  preferred_last_name: string | null;
  enrollment_count: number;
  pass_count: number;
}

interface StudentMergePreview {
  source: AdminStudent;
  canonical: AdminStudent;
  sourcePassCount: number;
  canonicalPassCount: number;
  sourceActiveEnrollmentCount: number;
  overlappingEnrollmentCount: number;
  unresolvedPassCount: number;
}

interface AdminStudentHistoryItem {
  id: string;
  student_number: string;
  first_name: string;
  last_name: string;
  preferred_first_name: string | null;
  preferred_last_name: string | null;
  teacher_name: string;
  class_name: string | null;
  period_label: string | null;
  reason_code: string;
  reason_label: string;
  status: string;
  requested_at: string;
  approved_at: string | null;
  returned_at: string | null;
  due_at: string | null;
  timed_out: number;
  is_emergency: number;
  duration_seconds: number;
}

interface AdminStudentHistoryResponse {
  history: AdminStudentHistoryItem[];
  page: number;
  pageSize: number;
  total: number;
  timezone: string;
}

interface PassHistoryResponse {
  classes: AdminClassRoster[];
  history: Array<AdminStudentHistoryItem & { timezone: string }>;
  page: number;
  pageSize: number;
  total: number;
}

interface AdminClassRoster {
  id: string;
  school_id: string;
  term_id: string | null;
  name: string;
  period_label: string;
  active: number;
  teacher_id: string;
  teacher_name: string;
}

interface AdminRosterEntry extends Student {
  class_id: string;
  school_id: string;
  school_name: string;
  active_class_count: number;
  active_teacher_count: number;
}

interface AdminStudentProfile {
  student: AdminStudent;
  enrollments: Array<{
    class_id: string;
    class_name: string;
    period_label: string;
    class_active: number;
    teacher_id: string;
    teacher_name: string;
  }>;
  summary: {
    passCount: number;
    restroomPassCount: number;
    tardyCount: number;
    emergencyPassCount: number;
    lastPassAt: string | null;
  };
  history: Array<{
    id: string;
    reason_code: string;
    reason_label: string;
    status: string;
    requested_at: string;
    approved_at: string | null;
    returned_at: string | null;
    due_at: string | null;
    timed_out: number;
    is_emergency: number;
    teacher_name: string;
    class_name: string | null;
    period_label: string | null;
    duration_seconds: number;
  }>;
}

interface SeparationGroupRecord {
  id: string; school_id: string; reference_name: string; category_scope: "ALL" | "UNSTRUCTURED" | "RESTROOM_CAPACITY" | "CUSTOM";
  active: number; starts_on: string | null; ends_on: string | null;
  members: Array<Pick<Student, "id" | "student_number" | "first_name" | "last_name" | "preferred_first_name" | "preferred_last_name">>;
  categories: Array<{ id: string; label: string; code: string }>;
}

function StudentSeparationGroupsAdmin({ data, currentUser, onError, onMessage }: {
  data: AdminData; currentUser: CurrentUser; onError: (message: string) => void; onMessage: (message: string) => void;
}) {
  const scope = administrationScope(currentUser, data);
  const [districtId, setDistrictId] = useState(scope.level === "GLOBAL" ? data.districts[0]?.id ?? "" : scope.districtId);
  const [schoolId, setSchoolId] = useState(scope.level === "SCHOOL" ? scope.schoolId : "");
  const [groups, setGroups] = useState<SeparationGroupRecord[]>([]);
  const [students, setStudents] = useState<SeparationGroupRecord["members"]>([]);
  const [categories, setCategories] = useState<Array<{ id: string; label: string; code: string }>>([]);
  const [editing, setEditing] = useState<SeparationGroupRecord | null>(null);
  const [studentQuery, setStudentQuery] = useState("");
  const [groupStudentNumber, setGroupStudentNumber] = useState("");
  const schools = data.schools.filter((school) => school.district_id === districtId && (scope.level !== "SCHOOL" || school.id === scope.schoolId));
  useEffect(() => { if (scope.level === "SCHOOL") setSchoolId(scope.schoolId); else if (!schools.some((school) => school.id === schoolId)) setSchoolId(schools[0]?.id ?? ""); }, [schoolId, schools, scope]);
  async function load() {
    if (!schoolId) return;
    try { const result = await api<{ groups: SeparationGroupRecord[]; activeStudents: SeparationGroupRecord["members"]; availableCategories: Array<{ id: string; label: string; code: string }> }>(`/api/admin/student-separation-groups?schoolId=${encodeURIComponent(schoolId)}`); setGroups(result.groups); setStudents(result.activeStudents); setCategories(result.availableCategories); }
    catch (caught) { onError(caught instanceof Error ? caught.message : "Student safety groups could not be loaded"); }
  }
  useEffect(() => { void load(); }, [schoolId]);
  const draft = editing ?? { id: "", school_id: schoolId, reference_name: "", category_scope: "ALL" as const, active: 1, starts_on: null, ends_on: null, members: [], categories: [] };
  const matchingStudents = useMemo(() => {
    const query = studentQuery.trim().toLocaleLowerCase();
    if (!query) return [];
    return students.filter((student) => `${student.first_name} ${student.last_name} ${student.student_number} ${student.preferred_first_name ?? ""} ${student.preferred_last_name ?? ""}`.toLocaleLowerCase().includes(query)).slice(0, 20);
  }, [studentQuery, students]);
  const visibleGroups = useMemo(() => {
    const studentNumber = groupStudentNumber.trim();
    return studentNumber ? groups.filter((group) => group.members.some((member) => member.student_number.includes(studentNumber))) : groups;
  }, [groupStudentNumber, groups]);
  function toggle(id: string, list: Array<{ id: string }>, set: (next: SeparationGroupRecord) => void, field: "members" | "categories") {
    const included = list.some((item) => item.id === id);
    const source = field === "members" ? students : categories;
    set({ ...draft, [field]: included ? list.filter((item) => item.id !== id) : [...list, source.find((item) => item.id === id)!] });
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      const existingGroup = Boolean(editing?.id);
      await api(existingGroup ? `/api/admin/student-separation-groups/${editing!.id}` : "/api/admin/student-separation-groups", { method: existingGroup ? "PUT" : "POST", body: JSON.stringify({ schoolId, referenceName: draft.reference_name, categoryScope: draft.category_scope, active: Boolean(draft.active), startsOn: draft.starts_on || null, endsOn: draft.ends_on || null, studentIds: draft.members.map((member) => member.id), categoryIds: draft.categories.map((category) => category.id) }) });
      setEditing(null); await load(); onMessage("Student separation group saved.");
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Student separation group could not be saved"); }
  }
  async function dissolve(group: SeparationGroupRecord) {
    if (!window.confirm(`Dissolve “${group.reference_name}”? This removes its members and restrictions.`)) return;
    try { await api(`/api/admin/student-separation-groups/${group.id}`, { method: "DELETE" }); await load(); onMessage("Student separation group dissolved."); }
    catch (caught) { onError(caught instanceof Error ? caught.message : "Student separation group could not be dissolved"); }
  }
  if (Boolean(editing)) return <div className="dialog-backdrop" onMouseDown={() => setEditing(null)}><form className="dialog" onSubmit={save} onMouseDown={(event) => event.stopPropagation()}>
    <div className="panel-heading"><div><p className="eyebrow ink">Student safety</p><h2>{draft.id ? "Edit group" : "New group"}</h2></div><button type="button" className="icon-button" onClick={() => setEditing(null)}>×</button></div>
    <label>Neutral reference<input value={draft.reference_name} maxLength={100} onChange={(event) => setEditing({ ...draft, reference_name: event.target.value })} required /></label>
    <label>Pass scope<select value={draft.category_scope} onChange={(event) => setEditing({ ...draft, category_scope: event.target.value as SeparationGroupRecord["category_scope"], categories: event.target.value === "CUSTOM" ? draft.categories : [] })}><option value="ALL">All pass categories</option><option value="UNSTRUCTURED">Unstructured destinations only</option><option value="RESTROOM_CAPACITY">Restroom / water capacity passes only</option><option value="CUSTOM">Custom selected categories</option></select></label>
    {draft.category_scope === "CUSTOM" && <fieldset><legend>Categories</legend>{categories.map((category) => <label className="check-row" key={category.id}><input type="checkbox" checked={draft.categories.some((item) => item.id === category.id)} onChange={() => toggle(category.id, draft.categories, setEditing, "categories")} />{category.label}</label>)}</fieldset>}
    <div className="form-grid"><label>Starts (optional)<input type="date" value={draft.starts_on ?? ""} onChange={(event) => setEditing({ ...draft, starts_on: event.target.value || null })} /></label><label>Ends (optional)<input type="date" value={draft.ends_on ?? ""} onChange={(event) => setEditing({ ...draft, ends_on: event.target.value || null })} /></label></div>
    <label className="check-row"><input type="checkbox" checked={Boolean(draft.active)} onChange={(event) => setEditing({ ...draft, active: Number(event.target.checked) })} />Active</label>
    <fieldset><legend>Student members (at least two)</legend><label>Search active students at this school<input value={studentQuery} onChange={(event) => setStudentQuery(event.target.value)} placeholder="Name, preferred name, or student ID" autoFocus /></label>{draft.members.length > 0 && <div className="selected-members">{draft.members.map((student) => <button type="button" className="button quiet small" key={student.id} onClick={() => toggle(student.id, draft.members, setEditing, "members")}>{student.last_name}, {student.first_name} ×</button>)}</div>}<div className="checkbox-list">{studentQuery.trim() ? matchingStudents.map((student) => <label className="check-row" key={student.id}><input type="checkbox" checked={draft.members.some((item) => item.id === student.id)} onChange={() => toggle(student.id, draft.members, setEditing, "members")} />{student.last_name}, {student.first_name} · {student.student_number}</label>) : <p className="form-note">Start typing to find and add students.</p>}{studentQuery.trim() && !matchingStudents.length && <p className="form-note">No active students match that search.</p>}</div></fieldset>
    <div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setEditing(null)}>Cancel</button><button className="button primary">Save group</button></div>
  </form></div>;
  return <section className="panel"><div className="panel-heading"><div><p className="eyebrow ink">Restricted student safety</p><h2>Student separation groups</h2><p className="form-note">Use short neutral references only. Group details are not shown to teachers.</p></div></div>
    <div className="form-grid">{scope.level === "GLOBAL" && <label>District<select value={districtId} onChange={(event) => setDistrictId(event.target.value)}>{data.districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label>}<label>School<select value={schoolId} onChange={(event) => setSchoolId(event.target.value)}>{schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select></label></div>
    <label className="separation-group-search">Find groups by student ID<input value={groupStudentNumber} onChange={(event) => setGroupStudentNumber(event.target.value)} inputMode="numeric" placeholder="Student number" /></label>
    <div className="table-wrap"><table><thead><tr><th>Reference</th><th>Scope</th><th>Members</th><th>Status</th><th /></tr></thead><tbody>{visibleGroups.map((group) => <tr key={group.id}><td><strong>{group.reference_name}</strong><small>{group.members.map((member) => `${member.last_name}, ${member.first_name} · ${member.student_number}`).join("  |  ")}</small></td><td>{group.category_scope === "ALL" ? "All pass categories" : group.category_scope === "UNSTRUCTURED" ? "Unstructured destinations" : group.category_scope === "RESTROOM_CAPACITY" ? "Restroom / water capacity passes" : group.categories.map((item) => item.label).join(", ")}</td><td>{group.members.length}</td><td>{group.active ? "Active" : "Inactive"}</td><td><button className="text-button" onClick={() => { setStudentQuery(""); setEditing(group); }}>Edit</button><button className="text-button danger-text" onClick={() => void dissolve(group)}>Dissolve</button></td></tr>)}</tbody></table></div>
    {!editing ? <button className="button primary" onClick={() => setEditing({ ...draft, school_id: schoolId })} disabled={!schoolId}>Add separation group</button> : <div className="dialog-backdrop" onMouseDown={() => setEditing(null)}><form className="dialog" onSubmit={save} onMouseDown={(event) => event.stopPropagation()}><div className="panel-heading"><div><p className="eyebrow ink">Student safety</p><h2>{editing.id ? "Edit group" : "New group"}</h2></div><button type="button" className="icon-button" onClick={() => setEditing(null)}>×</button></div><label>Neutral reference<input value={draft.reference_name} maxLength={100} onChange={(event) => setEditing({ ...draft, reference_name: event.target.value })} required /></label><label>Pass scope<select value={draft.category_scope} onChange={(event) => setEditing({ ...draft, category_scope: event.target.value as SeparationGroupRecord["category_scope"], categories: event.target.value === "CUSTOM" ? draft.categories : [] })}><option value="ALL">All pass categories</option><option value="UNSTRUCTURED">Unstructured destinations only</option><option value="CUSTOM">Custom selected categories</option></select></label>{draft.category_scope === "CUSTOM" && <fieldset><legend>Categories</legend>{categories.map((category) => <label className="check-row" key={category.id}><input type="checkbox" checked={draft.categories.some((item) => item.id === category.id)} onChange={() => toggle(category.id, draft.categories, setEditing, "categories")} />{category.label}</label>)}</fieldset>}<div className="form-grid"><label>Starts (optional)<input type="date" value={draft.starts_on ?? ""} onChange={(event) => setEditing({ ...draft, starts_on: event.target.value || null })} /></label><label>Ends (optional)<input type="date" value={draft.ends_on ?? ""} onChange={(event) => setEditing({ ...draft, ends_on: event.target.value || null })} /></label></div><label className="check-row"><input type="checkbox" checked={Boolean(draft.active)} onChange={(event) => setEditing({ ...draft, active: Number(event.target.checked) })} />Active</label><fieldset><legend>Active student members (at least two)</legend><div className="checkbox-list">{students.map((student) => <label className="check-row" key={student.id}><input type="checkbox" checked={draft.members.some((item) => item.id === student.id)} onChange={() => toggle(student.id, draft.members, setEditing, "members")} />{student.last_name}, {student.first_name} · {student.student_number}</label>)}</div></fieldset><div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setEditing(null)}>Cancel</button><button className="button primary">Save group</button></div></form></div>}</section>;
}

function StudentRecordsAdmin({
  data,
  currentUser,
  onError,
  onMessage,
  passHistoryTeacherId,
}: {
  data: AdminData;
  currentUser: CurrentUser;
  onError: (message: string) => void;
  onMessage: (message: string) => void;
  passHistoryTeacherId: string;
}) {
  const [tab, setTab] = useState<"rosters" | "history" | "merge" | "safety">("rosters");
  useEffect(() => {
    if (passHistoryTeacherId) setTab("history");
  }, [passHistoryTeacherId]);
  return (
    <div className="student-records-admin">
      <nav className="tabs" aria-label="Student records">
        <button className={tab === "rosters" ? "active" : ""} onClick={() => setTab("rosters")}>Student Info</button>
        <button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>Pass history</button>
        <button className={tab === "merge" ? "active" : ""} onClick={() => setTab("merge")}>Duplicate merge</button>
        <button className={tab === "safety" ? "active" : ""} onClick={() => setTab("safety")}>Student safety</button>
      </nav>
      {tab === "rosters" ? <AdminClassRosters data={data} currentUser={currentUser} onError={onError} />
        : tab === "history" ? <PassHistoryAdmin data={data} currentUser={currentUser} onError={onError} initialTeacherId={passHistoryTeacherId} />
          : tab === "merge" ? <StudentMergeAdmin data={data} currentUser={currentUser} onError={onError} onMessage={onMessage} />
            : <StudentSeparationGroupsAdmin data={data} currentUser={currentUser} onError={onError} onMessage={onMessage} />}
    </div>
  );
}

function StudentProfileAdmin({ data, currentUser, onError }: { data: AdminData; currentUser: CurrentUser; onError: (message: string) => void }) {
  const [districtId, setDistrictId] = useState(data.schools[0]?.district_id ?? "");
  const [schoolId, setSchoolId] = useState(data.schools[0]?.id ?? "");
  const [query, setQuery] = useState("");
  const [students, setStudents] = useState<AdminStudent[]>([]);
  const [profile, setProfile] = useState<AdminStudentProfile | null>(null);
  const [searching, setSearching] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const scope = administrationScope(currentUser, data);
  const districts = useMemo(() => data.districts.filter((district) => (scope.level === "GLOBAL" || district.id === scope.districtId) && data.schools.some((school) => school.district_id === district.id)), [data.districts, data.schools, scope]);
  const schools = useMemo(() => data.schools.filter((school) => school.district_id === districtId && (scope.level !== "SCHOOL" || school.id === scope.schoolId)), [data.schools, districtId, scope]);

  useEffect(() => { if (scope.level !== "GLOBAL") setDistrictId(scope.districtId); else if (!districts.some((district) => district.id === districtId)) setDistrictId(districts[0]?.id ?? ""); }, [districtId, districts, scope]);
  useEffect(() => { if (scope.level === "SCHOOL") setSchoolId(scope.schoolId); else if (!schools.some((school) => school.id === schoolId)) setSchoolId(schools[0]?.id ?? ""); }, [schoolId, schools, scope]);

  async function search(event: FormEvent) {
    event.preventDefault();
    if (query.trim().length < 2) return onError("Enter at least two characters to search student records.");
    setSearching(true);
    try {
      const result = await api<{ students: AdminStudent[] }>(`/api/admin/students?schoolId=${encodeURIComponent(schoolId)}&query=${encodeURIComponent(query.trim())}`);
      setStudents(result.students);
      setProfile(null);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Student search failed");
    } finally {
      setSearching(false);
    }
  }

  async function openProfile(student: AdminStudent) {
    setLoadingProfile(true);
    try {
      setProfile(await api<AdminStudentProfile>(`/api/admin/students/${student.id}/profile`));
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Student profile could not be loaded");
    } finally {
      setLoadingProfile(false);
    }
  }

  return <div className="student-profile-admin">
    <section className="panel">
      <div className="panel-heading"><div><p className="eyebrow ink">Student records</p><h2>Find a student</h2><p className="form-note">Profiles are limited to the schools you administer and record profile access in the audit log.</p></div></div>
      <form className="form-grid student-search-form" onSubmit={search}>
        <AdminLocationSelectors data={data} user={currentUser} districtId={districtId} schoolId={schoolId} onDistrictChange={(nextDistrictId) => { setDistrictId(nextDistrictId); setSchoolId(data.schools.find((school) => school.district_id === nextDistrictId)?.id ?? ""); setStudents([]); setProfile(null); }} onSchoolChange={(nextSchoolId) => { setSchoolId(nextSchoolId); setStudents([]); setProfile(null); }} />
        <label>Find by name or student ID<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="e.g. Avery or 10492" /></label>
        <button className="button primary" disabled={searching || !schoolId}>{searching ? "Searching…" : "Search students"}</button>
      </form>
      {!!students.length && <div className="table-wrap"><table><thead><tr><th>Student</th><th>ID</th><th>Active periods</th><th>Pass records</th><th /></tr></thead><tbody>{students.map((student) => <tr key={student.id}><td><strong>{student.last_name}, {student.first_name}</strong><small>{student.preferred_first_name || student.preferred_last_name ? `Preferred: ${studentDisplayName(student)}` : "No preferred name"}</small></td><td className="mono">{student.student_number}</td><td>{student.enrollment_count}</td><td>{student.pass_count}</td><td><button className="text-button" onClick={() => void openProfile(student)}>View profile</button></td></tr>)}</tbody></table></div>}
      {!students.length && !searching && <MiniEmpty text="Search by at least two characters to find active student records." />}
    </section>
    {loadingProfile && <section className="panel"><MiniEmpty text="Loading student profile…" /></section>}
    {profile && !loadingProfile && <section className="panel student-profile-card">
      <div className="panel-heading"><div><p className="eyebrow ink">Student profile</p><h2>{profile.student.first_name} {profile.student.last_name}</h2><p className="form-note">Student ID {profile.student.student_number}{profile.student.preferred_first_name || profile.student.preferred_last_name ? ` · Preferred: ${studentDisplayName(profile.student)}` : ""}</p></div></div>
      <div className="organization-metrics" aria-label="Student pass summary"><span><strong>{profile.summary.passCount}</strong> passes</span><span><strong>{profile.summary.restroomPassCount}</strong> restroom passes</span><span><strong>{profile.summary.tardyCount}</strong> tardies</span><span><strong>{profile.summary.emergencyPassCount}</strong> emergencies</span><span><strong>{profile.enrollments.length}</strong> active periods</span></div>
      <h3>Registered classes</h3>
      <div className="table-wrap"><table><thead><tr><th>Teacher</th><th>Period</th><th>Class</th><th>Status</th></tr></thead><tbody>{profile.enrollments.map((enrollment) => <tr key={enrollment.class_id}><td>{enrollment.teacher_name}</td><td>{enrollment.period_label}</td><td>{enrollment.class_name}</td><td>{enrollment.class_active ? "Active" : "Archived"}</td></tr>)}</tbody></table>{!profile.enrollments.length && <MiniEmpty text="This student has no active enrollments." />}</div>
      <h3>Recent pass activity</h3>
      <div className="table-wrap"><table><thead><tr><th>Date</th><th>Time</th><th>Teacher</th><th>Class</th><th>Pass type</th><th>Status</th><th className="center-column">Duration</th></tr></thead><tbody>{profile.history.map((item) => <tr key={item.id}><td>{formatDateOnlyAtTimeZone(item.requested_at, data.districts.find((district) => district.id === districtId)?.timezone ?? DEFAULT_TIME_ZONE)}</td><td>{formatTimestampTimeAtTimeZone(item.requested_at, data.districts.find((district) => district.id === districtId)?.timezone ?? DEFAULT_TIME_ZONE)}</td><td>{item.teacher_name}</td><td>{item.period_label ? `${item.period_label} · ${item.class_name ?? ""}` : item.class_name ?? "—"}</td><td>{item.reason_label ?? reasonLabels[item.reason_code] ?? item.reason_code}</td><td><span className={`status-chip ${historyStatusClass(item.status, item.timed_out)}`}>{historyStatusLabel(item.status, item.timed_out)}</span></td><td className="mono center-column">{["CANCELLED", "PERIOD_ENDED"].includes(item.status) ? "—" : reportDuration(item.duration_seconds, Boolean(item.approved_at))}</td></tr>)}</tbody></table>{!profile.history.length && <MiniEmpty text="No current pass records are available for this student." />}</div>
    </section>}
  </div>;
}

function AdminClassRosters({ data, currentUser, onError }: { data: AdminData; currentUser: CurrentUser; onError: (message: string) => void }) {
  const [districtId, setDistrictId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [termId, setTermId] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [classes, setClasses] = useState<AdminClassRoster[]>([]);
  const [classId, setClassId] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");
  const [studentQuery, setStudentQuery] = useState("");
  const [roster, setRoster] = useState<AdminRosterEntry[]>([]);
  const [editing, setEditing] = useState<AdminRosterEntry | null>(null);
  const [profile, setProfile] = useState<AdminStudentProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const scope = administrationScope(currentUser, data);
  const selectedDistrictId = scope.level === "GLOBAL" ? districtId : scope.districtId;
  const selectedSchoolId = scope.level === "SCHOOL" ? scope.schoolId : schoolId;
  const districts = useMemo(() => data.districts.filter((district) => (scope.level === "GLOBAL" || district.id === scope.districtId) && data.schools.some((school) => school.district_id === district.id)), [data.districts, data.schools, scope]);
  const schools = useMemo(() => data.schools.filter((school) => {
    if (scope.level === "SCHOOL") return school.id === scope.schoolId;
    if (scope.level === "DISTRICT") return school.district_id === scope.districtId;
    return !districtId || school.district_id === districtId;
  }), [data.schools, districtId, scope]);
  const teachers = useMemo(() => {
    const schoolIds = new Set(schools.map((school) => school.id));
    return data.users.filter((user) => data.grants.some((grant) => grant.user_id === user.id && grant.role === "TEACHER" && grant.scope_type === "SCHOOL" && schoolIds.has(grant.scope_id))).sort((left, right) => left.display_name.localeCompare(right.display_name));
  }, [data.grants, data.users, schools]);
  const terms = useMemo(() => data.terms.filter((term) => term.active && schools.some((school) => school.id === term.school_id)).sort((left, right) => `${data.schools.find((school) => school.id === left.school_id)?.name ?? ""} ${left.name}`.localeCompare(`${data.schools.find((school) => school.id === right.school_id)?.name ?? ""} ${right.name}`)), [data.schools, data.terms, schools]);
  const availableClasses = useMemo(() => classes.filter((item) => !teacherId || item.teacher_id === teacherId), [classes, teacherId]);
  const availablePeriods = useMemo(() => [...new Set(classes.map((item) => item.period_label))].sort((left, right) => left.localeCompare(right, undefined, { numeric: true })), [classes]);

  useEffect(() => { if (scope.level !== "GLOBAL") setDistrictId(scope.districtId); else if (districtId && !districts.some((district) => district.id === districtId)) setDistrictId(""); }, [districtId, districts, scope]);
  useEffect(() => { if (scope.level === "SCHOOL") setSchoolId(scope.schoolId); else if (schoolId && !schools.some((school) => school.id === schoolId)) setSchoolId(""); }, [schoolId, schools, scope]);
  useEffect(() => { if (termId && !terms.some((term) => term.id === termId)) setTermId(""); }, [termId, terms]);
  useEffect(() => { if (teacherId && !teachers.some((teacher) => teacher.id === teacherId)) setTeacherId(""); }, [teacherId, teachers]);
  useEffect(() => { if (classId && !availableClasses.some((item) => item.id === classId)) setClassId(""); }, [availableClasses, classId]);
  useEffect(() => { if (periodLabel && !availablePeriods.includes(periodLabel)) setPeriodLabel(""); }, [availablePeriods, periodLabel]);
  const loadRoster = useCallback(async () => {
    if (!data.schools.length || (scope.level !== "GLOBAL" && !selectedDistrictId)) return;
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (selectedDistrictId) query.set("districtId", selectedDistrictId);
      if (selectedSchoolId) query.set("schoolId", selectedSchoolId);
      if (termId) query.set("termId", termId);
      if (teacherId) query.set("teacherId", teacherId);
      if (classId) query.set("classId", classId);
      if (periodLabel) query.set("periodLabel", periodLabel);
      if (studentQuery.trim()) query.set("query", studentQuery.trim());
      const result = await api<{ classes: AdminClassRoster[]; roster: AdminRosterEntry[] }>(`/api/admin/class-roster?${query.toString()}`);
      setClasses(result.classes); setRoster(result.roster);
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Class rosters could not be loaded"); }
    finally { setLoading(false); }
  }, [classId, data.schools.length, onError, periodLabel, scope.level, selectedDistrictId, selectedSchoolId, studentQuery, teacherId, termId]);
  useEffect(() => { void loadRoster(); }, [loadRoster]);
  async function openProfile(student: AdminRosterEntry) {
    try {
      setProfile(await api<AdminStudentProfile>(`/api/admin/students/${student.id}/profile`));
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Student profile could not be loaded");
    }
  }

  const profileTimeZone = profile ? data.districts.find((district) => district.id === data.schools.find((school) => school.id === profile.student.school_id)?.district_id)?.timezone ?? DEFAULT_TIME_ZONE : DEFAULT_TIME_ZONE;

  return <div className="admin-class-rosters"><section className="panel">
    <div className="panel-heading"><div><p className="eyebrow ink">Student directory</p><h2>Class roster management</h2><p className="form-note">Each student appears once, even when registered in several matching periods. Open their profile to see every registration.</p></div><label className="roster-student-search">Search students<input value={studentQuery} onChange={(event) => setStudentQuery(event.target.value)} placeholder="Name or student ID" /></label></div>
    <div className="form-grid admin-roster-filters">
      {scope.level === "GLOBAL" && <label>District<select value={districtId} onChange={(event) => { setDistrictId(event.target.value); setSchoolId(""); setTermId(""); setTeacherId(""); setClassId(""); setPeriodLabel(""); }}><option value="">All districts</option>{districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label>}
      {scope.level !== "SCHOOL" && <label>School<select value={schoolId} onChange={(event) => { setSchoolId(event.target.value); setTermId(""); setTeacherId(""); setClassId(""); setPeriodLabel(""); }} disabled={!schools.length}><option value="">All schools</option>{schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select></label>}
      <label>Term<select value={termId} onChange={(event) => { setTermId(event.target.value); setTeacherId(""); setClassId(""); setPeriodLabel(""); }} disabled={!terms.length}><option value="">All active terms</option>{terms.map((term) => <option key={term.id} value={term.id}>{selectedSchoolId ? term.name : `${data.schools.find((school) => school.id === term.school_id)?.name ?? "School"} - ${term.name}`}</option>)}</select></label>
      <label>Teacher<select value={teacherId} onChange={(event) => { setTeacherId(event.target.value); setClassId(""); setPeriodLabel(""); }} disabled={!teachers.length}><option value="">All teachers</option>{teachers.map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.display_name}</option>)}</select></label>
      <label>Class period{teacherId ? <select value={classId} onChange={(event) => setClassId(event.target.value)} disabled={!availableClasses.length}><option value="">All class periods</option>{availableClasses.map((item) => <option key={item.id} value={item.id}>{item.period_label} - {item.name}{item.active ? "" : " (archived)"}</option>)}</select> : <select value={periodLabel} onChange={(event) => setPeriodLabel(event.target.value)} disabled={!availablePeriods.length}><option value="">All class periods</option>{availablePeriods.map((period) => <option key={period} value={period}>{period}</option>)}</select>}</label>
    </div>
    <div className="table-wrap"><table><thead><tr><th>Student</th><th>Preferred</th><th>ID</th><th>School</th><th /></tr></thead><tbody>{roster.map((student) => <tr key={student.id}><td><strong>{student.last_name}, {student.first_name}</strong></td><td>{student.preferred_first_name || student.preferred_last_name ? studentDisplayName(student) : "-"}</td><td className="mono">{student.student_number}</td><td>{student.school_name}</td><td><div className="roster-actions"><button className="button quiet small" onClick={() => void openProfile(student)}>Profile</button><button className="button quiet small" onClick={() => setEditing(student)}>Edit record</button></div></td></tr>)}</tbody></table>{!loading && !roster.length && <MiniEmpty text="No active students match these roster filters." />}</div>
  </section>{editing && <StudentEditorDialog key={`${editing.class_id}-${editing.id}`} classId={editing.class_id} student={editing} onClose={() => setEditing(null)} onUpdated={loadRoster} onError={onError} />}{profile && <AdminStudentProfileDialog profile={profile} timeZone={profileTimeZone} onClose={() => setProfile(null)} />}</div>;
}

function AdminStudentProfileDialog({ profile, timeZone, onClose }: { profile: AdminStudentProfile; timeZone: string; onClose: () => void }) {
  return <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
    <div className="dialog wide-dialog student-profile-dialog" role="dialog" aria-modal="true" aria-label="Student profile" onMouseDown={(event) => event.stopPropagation()}>
      <div className="panel-heading"><div><p className="eyebrow ink">Student profile</p><h2>{profile.student.first_name} {profile.student.last_name}</h2><p className="form-note">Student ID {profile.student.student_number}{profile.student.preferred_first_name || profile.student.preferred_last_name ? ` - Preferred: ${studentDisplayName(profile.student)}` : ""}</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Close student profile">Close</button></div>
      <div className="organization-metrics" aria-label="Student pass summary"><span><strong>{profile.summary.passCount}</strong> passes</span><span><strong>{profile.summary.restroomPassCount}</strong> restroom passes</span><span><strong>{profile.summary.tardyCount}</strong> tardies</span><span><strong>{profile.summary.emergencyPassCount}</strong> emergencies</span><span><strong>{profile.enrollments.length}</strong> active periods</span></div>
      <h3>Registered classes</h3>
      <div className="table-wrap"><table><thead><tr><th>Teacher</th><th>Period</th><th>Class</th><th>Status</th></tr></thead><tbody>{profile.enrollments.map((enrollment) => <tr key={enrollment.class_id}><td>{enrollment.teacher_name}</td><td>{enrollment.period_label}</td><td>{enrollment.class_name}</td><td>{enrollment.class_active ? "Active" : "Archived"}</td></tr>)}</tbody></table>{!profile.enrollments.length && <MiniEmpty text="This student has no active enrollments." />}</div>
      <h3>Recent pass activity</h3>
      <div className="table-wrap"><table><thead><tr><th>Date</th><th>Time</th><th>Teacher</th><th>Class</th><th>Pass type</th><th>Status</th></tr></thead><tbody>{profile.history.map((item) => <tr key={item.id}><td>{formatDateOnlyAtTimeZone(item.requested_at, timeZone)}</td><td>{formatTimestampTimeAtTimeZone(item.requested_at, timeZone)}</td><td>{item.teacher_name}</td><td>{item.period_label ? `${item.period_label} - ${item.class_name ?? ""}` : item.class_name ?? "-"}</td><td>{item.reason_label ?? reasonLabels[item.reason_code] ?? item.reason_code}</td><td><span className={`status-chip ${historyStatusClass(item.status, item.timed_out)}`}>{historyStatusLabel(item.status, item.timed_out)}</span></td></tr>)}</tbody></table>{!profile.history.length && <MiniEmpty text="No current pass records are available for this student." />}</div>
    </div>
  </div>;
}

function StudentHistoryAdmin({ data, currentUser, onError }: { data: AdminData; currentUser: CurrentUser; onError: (message: string) => void }) {
  const [districtId, setDistrictId] = useState(data.districts[0]?.id ?? "");
  const [schoolId, setSchoolId] = useState(data.schools[0]?.id ?? "");
  const [termId, setTermId] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [teachers, setTeachers] = useState<Array<{ id: string; display_name: string }>>([]);
  const [report, setReport] = useState<AdminStudentHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const scope = administrationScope(currentUser, data);

  const districts = useMemo(
    () => data.districts.filter((district) => (scope.level === "GLOBAL" || district.id === scope.districtId) && data.schools.some((school) => school.district_id === district.id)),
    [data.districts, data.schools, scope],
  );
  const districtSchools = useMemo(() => data.schools.filter((school) => school.district_id === districtId && (scope.level !== "SCHOOL" || school.id === scope.schoolId)), [data.schools, districtId, scope]);
  const schoolTerms = useMemo(() => data.terms.filter((term) => term.school_id === schoolId), [data.terms, schoolId]);

  useEffect(() => {
    if (scope.level !== "GLOBAL") setDistrictId(scope.districtId);
    else if (!districtId || !districts.some((district) => district.id === districtId)) setDistrictId(districts[0]?.id ?? "");
  }, [districtId, districts, scope]);
  useEffect(() => {
    if (scope.level === "SCHOOL") setSchoolId(scope.schoolId);
    else if (!districtSchools.some((school) => school.id === schoolId)) setSchoolId(districtSchools[0]?.id ?? "");
  }, [districtSchools, schoolId, scope]);
  useEffect(() => {
    if (!schoolTerms.some((term) => term.id === termId)) {
      setTermId(schoolTerms.find((term) => Boolean(term.active))?.id ?? schoolTerms[0]?.id ?? "");
    }
  }, [schoolTerms, termId]);
  useEffect(() => {
    setTeacherId("");
    setTeachers([]);
    if (!schoolId || !termId || !schoolTerms.some((term) => term.id === termId)) return;
    let current = true;
    void api<{ teachers: Array<{ id: string; display_name: string }> }>(`/api/admin/student-history/filters?schoolId=${encodeURIComponent(schoolId)}&termId=${encodeURIComponent(termId)}`)
      .then((result) => { if (current) setTeachers(result.teachers); })
      .catch((caught) => { if (current) onError(caught instanceof Error ? caught.message : "Teachers could not be loaded"); });
    return () => { current = false; };
  }, [onError, schoolId, schoolTerms, termId]);
  useEffect(() => { setPage(1); }, [districtId, schoolId, termId, teacherId]);

  const load = useCallback(async () => {
    if (!districtId || !schoolId || !termId || !schoolTerms.some((term) => term.id === termId)) return;
    setLoading(true);
    try {
      const query = new URLSearchParams({ districtId, schoolId, termId, page: String(page), pageSize: "50" });
      if (teacherId) query.set("teacherId", teacherId);
      setReport(await api<AdminStudentHistoryResponse>(`/api/admin/student-history?${query.toString()}`));
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Student history could not be loaded");
    } finally {
      setLoading(false);
    }
  }, [districtId, onError, page, schoolId, schoolTerms, teacherId, termId]);
  useEffect(() => { void load(); }, [load]);

  const pageCount = Math.max(1, Math.ceil((report?.total ?? 0) / (report?.pageSize ?? 50)));
  return (
    <div className="student-history-admin">
      {false && <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">Student-first reporting</p><h2>Student history</h2><p className="form-note">Semester pass records are limited to the districts and schools you are authorized to administer.</p></div></div>
        <div className="form-grid student-history-filters">
          <AdminLocationSelectors data={data} user={currentUser} districtId={districtId} schoolId={schoolId} onDistrictChange={(nextDistrictId) => { setDistrictId(nextDistrictId); setSchoolId(data.schools.find((school) => school.district_id === nextDistrictId)?.id ?? ""); }} onSchoolChange={setSchoolId} />
          <label>Term<select value={termId} onChange={(event) => setTermId(event.target.value)} required disabled={!schoolTerms.length}><option value="" disabled>{schoolTerms.length ? "Select term" : "No terms available"}</option>{schoolTerms.map((term) => <option key={term.id} value={term.id}>{term.name}{term.active ? "" : " (inactive)"}</option>)}</select></label>
          <label>Teacher<select value={teacherId} onChange={(event) => setTeacherId(event.target.value)} disabled={!termId}><option value="">All teachers</option>{teachers.map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.display_name}</option>)}</select></label>
        </div>
      </section>}
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">Individual pass records</p><h2>Semester history</h2><p className="form-note">{report ? `${report.total} ${report.total === 1 ? "record" : "records"}` : "Loading records…"}</p></div></div>
        <div className="table-wrap"><table><thead><tr><th>Student</th><th>Teacher</th><th>Class</th><th>Pass type</th><th className="center-column">Emergency</th><th>Status</th><th>Date</th><th>Time</th><th className="center-column">Duration</th></tr></thead><tbody>
          {(report?.history ?? []).map((item) => <tr key={item.id}>
            <td><strong>{item.first_name} {item.last_name}</strong><small>{item.student_number}{item.preferred_first_name || item.preferred_last_name ? ` · Preferred: ${studentDisplayName(item)}` : ""}</small></td>
            <td>{item.teacher_name}</td><td>{item.period_label ? `${item.period_label} · ${item.class_name ?? ""}` : item.class_name ?? "—"}</td>
            <td>{item.reason_label ?? reasonLabels[item.reason_code] ?? item.reason_code}</td>
            <td className="center-column">{item.is_emergency ? <span className="status-chip emergency">Emergency</span> : "—"}</td>
            <td><span className={`status-chip ${historyStatusClass(item.status, item.timed_out)}`}>{historyStatusLabel(item.status, item.timed_out)}</span></td>
            <td>{formatDateOnlyAtTimeZone(item.requested_at, report?.timezone ?? DEFAULT_TIME_ZONE)}</td>
            <td>{formatTimestampTimeAtTimeZone(item.requested_at, report?.timezone ?? DEFAULT_TIME_ZONE)}</td>
            <td className="mono center-column">{["CANCELLED", "PERIOD_ENDED"].includes(item.status) ? "—" : reportDuration(item.duration_seconds, Boolean(item.approved_at))}</td>
          </tr>)}
        </tbody></table>{!loading && !(report?.history.length) && <MiniEmpty text="No pass records found for this semester and teacher selection." />}</div>
        {report && report.total > 0 && <div className="pagination-controls"><span>Page {report.page} of {pageCount}</span><button className="button quiet small" disabled={loading || report.page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button><button className="button quiet small" disabled={loading || report.page >= pageCount} onClick={() => setPage((current) => current + 1)}>Next</button></div>}
      </section>
    </div>
  );
}

function PassHistoryAdmin({ data, currentUser, onError, initialTeacherId }: { data: AdminData; currentUser: CurrentUser; onError: (message: string) => void; initialTeacherId: string }) {
  const [districtId, setDistrictId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [termId, setTermId] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [classId, setClassId] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");
  const [search, setSearch] = useState("");
  const [classes, setClasses] = useState<AdminClassRoster[]>([]);
  const [report, setReport] = useState<PassHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const scope = administrationScope(currentUser, data);
  const selectedDistrictId = scope.level === "GLOBAL" ? districtId : scope.districtId;
  const selectedSchoolId = scope.level === "SCHOOL" ? scope.schoolId : schoolId;
  const districts = useMemo(() => data.districts.filter((district) => (scope.level === "GLOBAL" || district.id === scope.districtId) && data.schools.some((school) => school.district_id === district.id)), [data.districts, data.schools, scope]);
  const schools = useMemo(() => data.schools.filter((school) => {
    if (scope.level === "SCHOOL") return school.id === scope.schoolId;
    if (scope.level === "DISTRICT") return school.district_id === scope.districtId;
    return !districtId || school.district_id === districtId;
  }), [data.schools, districtId, scope]);
  const terms = useMemo(() => data.terms.filter((term) => schools.some((school) => school.id === term.school_id)).sort((left, right) => `${data.schools.find((school) => school.id === left.school_id)?.name ?? ""} ${left.name}`.localeCompare(`${data.schools.find((school) => school.id === right.school_id)?.name ?? ""} ${right.name}`)), [data.schools, data.terms, schools]);
  const teachers = useMemo(() => classes.filter((item, index, entries) => entries.findIndex((candidate) => candidate.teacher_id === item.teacher_id) === index).map((item) => ({ id: item.teacher_id, display_name: item.teacher_name })), [classes]);
  const availableClasses = useMemo(() => classes.filter((item) => !teacherId || item.teacher_id === teacherId), [classes, teacherId]);
  const availablePeriods = useMemo(() => [...new Set(classes.map((item) => item.period_label))].sort((left, right) => left.localeCompare(right, undefined, { numeric: true })), [classes]);

  useEffect(() => { if (scope.level !== "GLOBAL") setDistrictId(scope.districtId); else if (districtId && !districts.some((district) => district.id === districtId)) setDistrictId(""); }, [districtId, districts, scope]);
  useEffect(() => { if (scope.level === "SCHOOL") setSchoolId(scope.schoolId); else if (schoolId && !schools.some((school) => school.id === schoolId)) setSchoolId(""); }, [schoolId, schools, scope]);
  useEffect(() => { if (termId && !terms.some((term) => term.id === termId)) setTermId(""); }, [termId, terms]);
  useEffect(() => {
    if (initialTeacherId) {
      setTeacherId(initialTeacherId);
      setClassId("");
      setPeriodLabel("");
    }
  }, [initialTeacherId]);
  useEffect(() => { if (teacherId && teachers.length && !teachers.some((teacher) => teacher.id === teacherId)) setTeacherId(""); }, [teacherId, teachers]);
  useEffect(() => { if (classId && !availableClasses.some((item) => item.id === classId)) setClassId(""); }, [availableClasses, classId]);
  useEffect(() => { if (periodLabel && !availablePeriods.includes(periodLabel)) setPeriodLabel(""); }, [availablePeriods, periodLabel]);
  useEffect(() => { setPage(1); }, [districtId, schoolId, termId, teacherId, classId, periodLabel, search]);

  const load = useCallback(async () => {
    if (!data.schools.length || (scope.level !== "GLOBAL" && !selectedDistrictId)) return;
    setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), pageSize: "50" });
      if (selectedDistrictId) query.set("districtId", selectedDistrictId);
      if (selectedSchoolId) query.set("schoolId", selectedSchoolId);
      if (termId) query.set("termId", termId);
      if (teacherId) query.set("teacherId", teacherId);
      if (classId) query.set("classId", classId);
      if (periodLabel) query.set("periodLabel", periodLabel);
      if (search.trim()) query.set("query", search.trim());
      const response = await api<PassHistoryResponse>(`/api/admin/pass-history?${query.toString()}`);
      setClasses(response.classes);
      setReport(response);
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Pass history could not be loaded"); }
    finally { setLoading(false); }
  }, [classId, data.schools.length, onError, page, periodLabel, scope.level, search, selectedDistrictId, selectedSchoolId, teacherId, termId]);
  useEffect(() => { void load(); }, [load]);

  const pageCount = Math.max(1, Math.ceil((report?.total ?? 0) / (report?.pageSize ?? 50)));
  return <div className="pass-history-admin"><section className="panel">
    <div className="panel-heading"><div><p className="eyebrow ink">Individual pass records</p><h2>Pass history</h2><p className="form-note">{report ? `${report.total} ${report.total === 1 ? "record" : "records"}` : "Loading records…"}</p></div><label className="roster-student-search">Search passes<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Student name or ID" /></label></div>
    <div className="form-grid pass-history-filters">
      {scope.level === "GLOBAL" && <label>District<select value={districtId} onChange={(event) => { setDistrictId(event.target.value); setSchoolId(""); setTermId(""); setTeacherId(""); setClassId(""); setPeriodLabel(""); }}><option value="">All districts</option>{districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}</select></label>}
      {scope.level !== "SCHOOL" && <label>School<select value={schoolId} onChange={(event) => { setSchoolId(event.target.value); setTermId(""); setTeacherId(""); setClassId(""); setPeriodLabel(""); }} disabled={!schools.length}><option value="">All schools</option>{schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select></label>}
      <label>Term<select value={termId} onChange={(event) => { setTermId(event.target.value); setTeacherId(""); setClassId(""); setPeriodLabel(""); }} disabled={!terms.length}><option value="">All terms</option>{terms.map((term) => <option key={term.id} value={term.id}>{selectedSchoolId ? term.name : `${data.schools.find((school) => school.id === term.school_id)?.name ?? "School"} - ${term.name}`}</option>)}</select></label>
      <label>Teacher<select value={teacherId} onChange={(event) => { setTeacherId(event.target.value); setClassId(""); setPeriodLabel(""); }} disabled={!teachers.length}><option value="">All teachers</option>{teachers.map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.display_name}</option>)}</select></label>
      <label>Class period{teacherId ? <select value={classId} onChange={(event) => setClassId(event.target.value)} disabled={!availableClasses.length}><option value="">All class periods</option>{availableClasses.map((item) => <option key={item.id} value={item.id}>{item.period_label} - {item.name}{item.active ? "" : " (archived)"}</option>)}</select> : <select value={periodLabel} onChange={(event) => setPeriodLabel(event.target.value)} disabled={!availablePeriods.length}><option value="">All class periods</option>{availablePeriods.map((period) => <option key={period} value={period}>{period}</option>)}</select>}</label>
    </div>
    <div className="table-wrap"><table><thead><tr><th>Student</th><th>Teacher</th><th>Class</th><th>Pass type</th><th className="center-column">Emergency</th><th>Status</th><th>Date</th><th>Time</th><th className="center-column">Duration</th></tr></thead><tbody>{(report?.history ?? []).map((item) => <tr key={item.id}><td><strong>{item.first_name} {item.last_name}</strong><small>{item.student_number}{item.preferred_first_name || item.preferred_last_name ? ` - Preferred: ${studentDisplayName(item)}` : ""}</small></td><td>{item.teacher_name}</td><td>{item.period_label ? `${item.period_label} - ${item.class_name ?? ""}` : item.class_name ?? "-"}</td><td>{item.reason_label ?? reasonLabels[item.reason_code] ?? item.reason_code}</td><td className="center-column">{item.is_emergency ? <span className="status-chip emergency">Emergency</span> : "-"}</td><td><span className={`status-chip ${historyStatusClass(item.status, item.timed_out)}`}>{historyStatusLabel(item.status, item.timed_out)}</span></td><td>{formatDateOnlyAtTimeZone(item.requested_at, item.timezone ?? DEFAULT_TIME_ZONE)}</td><td>{formatTimestampTimeAtTimeZone(item.requested_at, item.timezone ?? DEFAULT_TIME_ZONE)}</td><td className="mono center-column">{["CANCELLED", "PERIOD_ENDED"].includes(item.status) ? "-" : reportDuration(item.duration_seconds, Boolean(item.approved_at))}</td></tr>)}</tbody></table>{!loading && !(report?.history.length) && <MiniEmpty text="No pass records match these filters." />}</div>
    {report && report.total > 0 && <div className="pagination-controls"><span>Page {report.page} of {pageCount}</span><button className="button quiet small" disabled={loading || report.page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button><button className="button quiet small" disabled={loading || report.page >= pageCount} onClick={() => setPage((current) => current + 1)}>Next</button></div>}
  </section></div>;
}

function StudentMergeAdmin({
  data,
  currentUser,
  onError,
  onMessage,
}: {
  data: AdminData;
  currentUser: CurrentUser;
  onError: (message: string) => void;
  onMessage: (message: string) => void;
}) {
  const [schoolId, setSchoolId] = useState(data.schools[0]?.id ?? "");
  const [districtId, setDistrictId] = useState(data.schools[0]?.district_id ?? "");
  const [query, setQuery] = useState("");
  const [students, setStudents] = useState<AdminStudent[]>([]);
  const [source, setSource] = useState<AdminStudent | null>(null);
  const [canonical, setCanonical] = useState<AdminStudent | null>(null);
  const [preview, setPreview] = useState<StudentMergePreview | null>(null);
  const [searching, setSearching] = useState(false);
  const [merging, setMerging] = useState(false);
  const scope = administrationScope(currentUser, data);

  const districts = useMemo(() => data.districts.filter((district) => (scope.level === "GLOBAL" || district.id === scope.districtId) && data.schools.some((school) => school.district_id === district.id)), [data.districts, data.schools, scope]);
  const districtSchools = useMemo(() => data.schools.filter((school) => school.district_id === districtId && (scope.level !== "SCHOOL" || school.id === scope.schoolId)), [data.schools, districtId, scope]);
  useEffect(() => { if (scope.level !== "GLOBAL") setDistrictId(scope.districtId); else if (!districtId || !districts.some((district) => district.id === districtId)) setDistrictId(districts[0]?.id ?? ""); }, [districtId, districts, scope]);
  useEffect(() => { if (scope.level === "SCHOOL") setSchoolId(scope.schoolId); else if (!districtSchools.some((school) => school.id === schoolId)) setSchoolId(districtSchools[0]?.id ?? ""); }, [districtSchools, schoolId, scope]);
  useEffect(() => {
    if (!source || !canonical) {
      setPreview(null);
      return;
    }
    let current = true;
    void (async () => {
      try {
        const result = await api<StudentMergePreview>(`/api/admin/students/${source.id}/merge-preview?canonicalStudentId=${encodeURIComponent(canonical.id)}`);
        if (current) setPreview(result);
      } catch (caught) {
        if (!current) return;
        setPreview(null);
        onError(caught instanceof Error ? caught.message : "Merge preview could not be loaded");
      }
    })();
    return () => { current = false; };
  }, [canonical, onError, source]);

  async function search(event: FormEvent) {
    event.preventDefault();
    if (query.trim().length < 2) return onError("Enter at least two characters to search student records.");
    setSearching(true);
    try {
      const result = await api<{ students: AdminStudent[] }>(`/api/admin/students?schoolId=${encodeURIComponent(schoolId)}&query=${encodeURIComponent(query.trim())}`);
      setStudents(result.students);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Student search failed");
    } finally {
      setSearching(false);
    }
  }

  function chooseSource(student: AdminStudent) {
    setSource(student);
    if (canonical?.id === student.id) setCanonical(null);
  }

  function chooseCanonical(student: AdminStudent) {
    setCanonical(student);
    if (source?.id === student.id) setSource(null);
  }

  async function merge() {
    if (!source || !canonical || !preview) return;
    if (preview.unresolvedPassCount > 0) return onError("Resolve all active, waiting, ready, and pending passes before merging.");
    if (!window.confirm(`Merge duplicate ${source.first_name} ${source.last_name} (${source.student_number}) into canonical ${canonical.first_name} ${canonical.last_name} (${canonical.student_number})? This keeps all pass history, retires only the duplicate record, and cannot be undone from this screen.`)) return;
    setMerging(true);
    try {
      const result = await api<{ movedPasses: number; movedEnrollments: number; combinedEnrollments: number }>(`/api/admin/students/${source.id}/merge`, {
        method: "POST",
        body: JSON.stringify({ canonicalStudentId: canonical.id }),
      });
      setStudents((current) => current.filter((student) => student.id !== source.id));
      setSource(null);
      setCanonical(null);
      setPreview(null);
      onMessage(`Student records merged: ${result.movedPasses} pass records and ${result.movedEnrollments} enrollments moved; ${result.combinedEnrollments} duplicate enrollments combined.`);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Student records could not be merged");
    } finally {
      setMerging(false);
    }
  }

  return (
    <div className="student-merge-admin">
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">Data quality</p><h2>Merge duplicate students</h2></div></div>
        <p className="form-note">Search for the two school records. Select the mistaken duplicate, then the one canonical record to retain. Records from different schools cannot be merged.</p>
        <form className="form-grid student-search-form" onSubmit={search}>
          <AdminLocationSelectors data={data} user={currentUser} districtId={districtId} schoolId={schoolId} onDistrictChange={(nextDistrictId) => { setDistrictId(nextDistrictId); setSchoolId(data.schools.find((school) => school.district_id === nextDistrictId)?.id ?? ""); setStudents([]); setSource(null); setCanonical(null); }} onSchoolChange={(nextSchoolId) => { setSchoolId(nextSchoolId); setStudents([]); setSource(null); setCanonical(null); }} />
          <label>Find by name or student ID<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="e.g. Billy or 11111" /></label>
          <button className="button primary" disabled={searching || !schoolId}>{searching ? "Searchingâ€¦" : "Search students"}</button>
        </form>
      </section>
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow ink">Search results</p><h2>Choose records</h2></div></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Student</th><th>ID</th><th>Active periods</th><th>Pass records</th><th /></tr></thead>
            <tbody>
              {students.map((student) => (
                <tr key={student.id} className={cx(source?.id === student.id && "merge-source-row", canonical?.id === student.id && "merge-canonical-row")}>
                  <td><strong>{student.last_name}, {student.first_name}</strong><small>{student.preferred_first_name || student.preferred_last_name ? `Preferred: ${studentDisplayName(student)}` : "No preferred name"}</small></td>
                  <td className="mono">{student.student_number}</td>
                  <td>{student.enrollment_count}</td>
                  <td>{student.pass_count}</td>
                  <td><button className="text-button danger-text" onClick={() => chooseSource(student)}>{source?.id === student.id ? "Duplicate selected" : "Mark duplicate"}</button><button className="text-button" onClick={() => chooseCanonical(student)}>{canonical?.id === student.id ? "Canonical selected" : "Keep as canonical"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!students.length && <MiniEmpty text="Search by at least two characters to find active student records." />}
        </div>
      </section>
      {(source || canonical) && (
        <section className="panel merge-preview">
          <div className="panel-heading"><div><p className="eyebrow ink">Merge review</p><h2>Confirm the canonical record</h2></div></div>
          <div className="merge-selection">
            <article className="merge-source-row"><span>Duplicate to retire</span>{source ? <><strong>{source.first_name} {source.last_name}</strong><small>{source.student_number}{source.preferred_first_name || source.preferred_last_name ? ` · Preferred: ${studentDisplayName(source)}` : ""}</small></> : <p>Select the mistaken record above.</p>}</article>
            <article className="merge-canonical-row"><span>Canonical record to keep</span>{canonical ? <><strong>{canonical.first_name} {canonical.last_name}</strong><small>{canonical.student_number}{canonical.preferred_first_name || canonical.preferred_last_name ? ` · Preferred: ${studentDisplayName(canonical)}` : ""}</small></> : <p>Select the correct record above.</p>}</article>
          </div>
          {preview && (
            <>
              {preview.unresolvedPassCount > 0 ? <div className="alert error">{preview.unresolvedPassCount} unresolved pass{preview.unresolvedPassCount === 1 ? " is" : "es are"} attached to these records. Return, cancel, or resolve them before merging.</div> : <div className="student-profile-impact"><strong>Ready to merge</strong><p>{preview.sourcePassCount} duplicate pass records will become part of the canonical student’s {preview.canonicalPassCount} existing records. {preview.sourceActiveEnrollmentCount} active duplicate enrollment{preview.sourceActiveEnrollmentCount === 1 ? "" : "s"} will be preserved; {preview.overlappingEnrollmentCount} shared enrollment{preview.overlappingEnrollmentCount === 1 ? "" : "s"} will be combined. Count-based alert clear flags will recalculate after the merge.</p></div>}
              <div className="dialog-actions"><button className="button primary" disabled={merging || preview.unresolvedPassCount > 0} onClick={() => void merge()}>{merging ? "Mergingâ€¦" : "Merge into canonical student"}</button></div>
            </>
          )}
          {!preview && source && canonical && <MiniEmpty text="Loading merge impactâ€¦" />}
        </section>
      )}
    </div>
  );
}

function TermAdmin({
  data,
  currentUser,
  refresh,
  onError,
}: {
  data: AdminData;
  currentUser: CurrentUser;
  refresh: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [schoolId, setSchoolId] = useState(data.schools[0]?.id ?? "");
  const [districtId, setDistrictId] = useState(data.schools[0]?.district_id ?? "");
  const [name, setName] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [editing, setEditing] = useState<AdminData["terms"][number] | null>(null);
  const scope = administrationScope(currentUser, data);

  const availableDistricts = useMemo(
    () => data.districts
      .filter((district) => data.schools.some((school) => school.district_id === district.id))
      .sort((left, right) => left.code.localeCompare(right.code)),
    [data.districts, data.schools],
  );
  const districtSchools = useMemo(
    () => data.schools
      .filter((school) => school.district_id === districtId)
      .sort((left, right) => left.name.localeCompare(right.name)),
    [data.schools, districtId],
  );

  useEffect(() => {
    if (scope.level !== "GLOBAL") {
      setDistrictId(scope.districtId);
    } else if (!districtId || !availableDistricts.some((district) => district.id === districtId)) {
      setDistrictId(availableDistricts[0]?.id ?? "");
    }
  }, [availableDistricts, districtId, scope]);

  useEffect(() => {
    if (scope.level === "SCHOOL") {
      setSchoolId(scope.schoolId);
    } else if (!districtSchools.some((school) => school.id === schoolId)) {
      setSchoolId(districtSchools[0]?.id ?? "");
    }
  }, [districtSchools, schoolId, scope]);

  async function create(event: FormEvent) {
    event.preventDefault();
    try {
      await api("/api/admin/terms", { method: "POST", body: JSON.stringify({ schoolId, name, startsOn, endsOn }) });
      setName(""); setStartsOn(""); setEndsOn(""); await refresh();
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Academic term could not be created"); }
  }

  async function save(term: AdminData["terms"][number]) {
    try {
      await api(`/api/admin/terms/${term.id}`, { method: "PATCH", body: JSON.stringify({ name: term.name, startsOn: term.starts_on, endsOn: term.ends_on, active: Boolean(term.active) }) });
      setEditing(null); await refresh();
    } catch (caught) { onError(caught instanceof Error ? caught.message : "Academic term could not be saved"); }
  }

  return (
    <section className="panel term-admin">
      <div className="panel-heading"><div><p className="eyebrow ink">School exceptions</p><h2>School-specific academic terms</h2></div></div>
      <p className="form-note">Use these only when a school does not follow its district’s academic-term dates.</p>
      <form className="form-grid term-create" onSubmit={create}>
        <AdminLocationSelectors data={data} user={currentUser} districtId={districtId} schoolId={schoolId} onDistrictChange={(nextDistrictId) => { setDistrictId(nextDistrictId); setSchoolId(data.schools.find((school) => school.district_id === nextDistrictId)?.id ?? ""); }} onSchoolChange={setSchoolId} />
        <label>Term name<input value={name} onChange={(event) => setName(event.target.value)} placeholder="Fall 2026" required /></label>
        <label>Starts<input type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} required /></label>
        <label>Ends<input type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} required /></label>
        <button className="button quiet">Add term</button>
      </form>
      <div className="table-wrap">
        <table>
          <thead><tr><th>School</th><th>Term</th><th>Dates</th><th>Status</th><th /></tr></thead>
          <tbody>
            {data.terms.filter((term) => !term.district_term_id).map((term) => (
              <tr key={term.id}>
                <td>{data.schools.find((school) => school.id === term.school_id)?.name ?? "—"}</td>
                <td><strong>{term.name}</strong></td>
                <td>{term.starts_on} to {term.ends_on}</td>
                <td>{term.active ? "Active" : "Inactive"}</td>
                <td><button className="text-button" onClick={() => setEditing({ ...term })}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={() => setEditing(null)}>
          <form className="dialog" onSubmit={(event) => { event.preventDefault(); void save(editing); }} onMouseDown={(event) => event.stopPropagation()}>
            <div className="panel-heading"><div><p className="eyebrow ink">Academic term</p><h2>Edit term</h2></div><button type="button" className="icon-button" onClick={() => setEditing(null)}>×</button></div>
            <label>Name<input value={editing.name} onChange={(event) => setEditing({ ...editing, name: event.target.value })} required /></label>
            <div className="field-row"><label>Starts<input type="date" value={editing.starts_on ?? ""} onChange={(event) => setEditing({ ...editing, starts_on: event.target.value })} required /></label><label>Ends<input type="date" value={editing.ends_on ?? ""} onChange={(event) => setEditing({ ...editing, ends_on: event.target.value })} required /></label></div>
            <label className="check-row"><input type="checkbox" checked={Boolean(editing.active)} onChange={(event) => setEditing({ ...editing, active: Number(event.target.checked) })} />Available in semester filters</label>
            <div className="dialog-actions"><button type="button" className="button quiet" onClick={() => setEditing(null)}>Cancel</button><button className="button primary">Save term</button></div>
          </form>
        </div>
      )}
    </section>
  );
}

interface DeviceState {
  device: { id: string; name: string; type: "KIOSK" | "DISPLAY"; scalePercent: number | null };
  teacher: { heading: string; displayName: string; title: string | null };
  scheduleHeading: {
    heading: string;
    phase: "PERIOD" | "BREAK" | "BEFORE_SCHOOL" | "AFTER_SCHOOL" | "NO_SCHOOL";
    schoolPeriodLabel: string | null;
    teacherClassName: string | null;
  } | null;
  class: {
    id: string | null;
    name: string;
    period_label: string;
    max_restroom_out: number;
    pass_limit_minutes: number;
    warning_minutes: number;
    timer_colors_enabled: number;
    timer_normal_color: string;
    timer_warning_color: string;
    timer_alert_color: string;
    timeout_minutes: number;
    timezone: string;
  };
  passes: Pass[];
  categories: PassCategory[];
  maskOtherDestinations: boolean;
  rosterNamePickerEnabled: boolean;
  otherDestinationLabel: string;
  otherDestinationSymbol: string;
  serverNow: string;
}

function DeviceScreen({ type }: { type: "KIOSK" | "DISPLAY" }) {
  const storageKey = `hallway:${type.toLowerCase()}:token`;
  const launchStorageKey = `hallway:${type.toLowerCase()}:authenticated-launch`;
  let fragmentLaunchToken = "";
  try {
    fragmentLaunchToken = new URLSearchParams(window.location.hash.slice(1)).get("launch") ?? "";
    if (fragmentLaunchToken) {
      sessionStorage.setItem(launchStorageKey, fragmentLaunchToken);
      window.history.replaceState(null, "", window.location.pathname);
    }
  } catch {
    // Storage and history are conveniences; the fragment still supplies the
    // temporary credential when browser privacy settings disable them.
  }
  const authenticatedLaunchToken = fragmentLaunchToken || (() => { try { return sessionStorage.getItem(launchStorageKey) ?? ""; } catch { return ""; } })();
  const [temporaryLaunch, setTemporaryLaunch] = useState(Boolean(authenticatedLaunchToken));
  const [token, setToken] = useState(() => authenticatedLaunchToken || (localStorage.getItem(storageKey) ?? ""));
  const [state, setState] = useState<DeviceState | null>(null);
  const [stateReceivedAt, setStateReceivedAt] = useState(0);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const response = await deviceApi<DeviceState>(token, "/api/device/state");
      setState(response);
      setStateReceivedAt(Date.now());
      setError("");
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        if (temporaryLaunch) {
          try { sessionStorage.removeItem(launchStorageKey); } catch { /* no stored launch credential */ }
        } else {
          localStorage.removeItem(storageKey);
        }
        setToken("");
      } else {
        setError(caught instanceof Error ? caught.message : "Unable to update device");
      }
    }
  }, [launchStorageKey, storageKey, temporaryLaunch, token]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5_000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (!token) {
    return (
      <PairDevice
        type={type}
        onPaired={(newToken) => {
          try { sessionStorage.removeItem(launchStorageKey); } catch { /* no stored launch credential */ }
          setTemporaryLaunch(false);
          localStorage.setItem(storageKey, newToken);
          setToken(newToken);
        }}
      />
    );
  }
  if (!state) return <LoadingScreen />;
  return type === "KIOSK" ? (
    <Kiosk state={state} stateReceivedAt={stateReceivedAt} token={token} refresh={load} error={error} />
  ) : (
    <DisplayBoard state={state} stateReceivedAt={stateReceivedAt} error={error} />
  );
}

function PairDevice({
  type,
  onPaired,
}: {
  type: "KIOSK" | "DISPLAY";
  onPaired: (token: string) => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState(type === "KIOSK" ? "Classroom kiosk" : "Classroom display");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function pair(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const response = await api<{ token: string; deviceType: string }>("/api/device/pair", { method: "POST", body: JSON.stringify({ code, name }) });
      if (response.deviceType !== type) throw new Error(`That code is for a ${response.deviceType.toLowerCase()}, not this ${type.toLowerCase()}.`);
      onPaired(response.token);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Pairing failed"); }
    finally { setBusy(false); }
  }
  return (
    <main className="pair-screen">
      <Link className="brand-lockup light" to="/login"><span className="brand-mark">V</span><span>Virtual Hall Pass</span></Link>
      <form className="pair-card" onSubmit={pair}>
        <span className="pair-icon">{type === "KIOSK" ? "▣" : "◫"}</span>
        <p className="eyebrow ink">Pair {type.toLowerCase()}</p>
        <h1>Connect this device</h1>
        <p className="muted">Ask the teacher to create a one-time code from Classroom → Devices.</p>
        {error && <div className="alert error">{error}</div>}
        <label>Six-digit code<input className="code-input" inputMode="numeric" pattern="[0-9 ]{6,7}" maxLength={7} placeholder="123 456" value={code} onChange={(event) => setCode(event.target.value.replace(/[^\d ]/g, ""))} autoFocus required /></label>
        <label>Device name<input value={name} onChange={(event) => setName(event.target.value)} required /></label>
        <button className="button primary wide" disabled={busy}>{busy ? "Connecting…" : `Pair ${type.toLowerCase()}`}</button>
      </form>
    </main>
  );
}

interface KioskLimitConfirmation {
  reasonCode: string;
  categoryLabel: string;
  passCount: number;
  passLimit: number;
}

interface KioskScheduleConfirmation {
  reasonCode: string;
  slotLabel: string;
  remainingMinutes: number;
}

// The compact device layouts were designed around a 7-inch, 1024 × 600
// landscape touchscreen. Scale the complete interface from that reference
// size so a larger kiosk or its paired display keeps the same proportions.
const DEVICE_LAYOUT_WIDTH = 1024;
const DEVICE_LAYOUT_HEIGHT = 600;

function useDeviceViewportScale(customScalePercent: number | null): CSSProperties {
  const measure = () => {
    const automaticScale = Math.max(1, Math.min(window.innerWidth / DEVICE_LAYOUT_WIDTH, window.innerHeight / DEVICE_LAYOUT_HEIGHT));
    const scale = customScalePercent === null ? automaticScale : customScalePercent / 100;
    return {
      "--device-scale": scale,
      "--device-layout-width": `${window.innerWidth / scale}px`,
      "--device-layout-height": `${window.innerHeight / scale}px`,
    } as CSSProperties;
  };
  const [style, setStyle] = useState<CSSProperties>(measure);

  useEffect(() => {
    const update = () => setStyle(measure());
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [customScalePercent]);

  return style;
}

function kioskLimitConfirmation(error: unknown): KioskLimitConfirmation | null {
  if (!(error instanceof ApiError) || error.status !== 409 || !error.body || typeof error.body !== "object") {
    return null;
  }
  const body = error.body as {
    code?: unknown;
    confirmation?: { categoryLabel?: unknown; passCount?: unknown; passLimit?: unknown };
  };
  if (body.code !== "PASS_LIMIT_CONFIRMATION" || !body.confirmation) return null;
  const passCount = Number(body.confirmation.passCount);
  const passLimit = Number(body.confirmation.passLimit);
  const categoryLabel = typeof body.confirmation.categoryLabel === "string"
    ? body.confirmation.categoryLabel
    : null;
  if (!categoryLabel || !Number.isFinite(passCount) || !Number.isFinite(passLimit)) return null;
  return { reasonCode: "", categoryLabel, passCount, passLimit };
}

function kioskScheduleConfirmation(error: unknown): KioskScheduleConfirmation | null {
  if (!(error instanceof ApiError) || error.status !== 409 || !error.body || typeof error.body !== "object") return null;
  const body = error.body as {
    code?: unknown;
    confirmation?: { slotLabel?: unknown; remainingMinutes?: unknown };
  };
  if (body.code !== "SCHEDULE_END_CONFIRMATION" || !body.confirmation) return null;
  const slotLabel = typeof body.confirmation.slotLabel === "string" ? body.confirmation.slotLabel : null;
  const remainingMinutes = Number(body.confirmation.remainingMinutes);
  if (!slotLabel || !Number.isFinite(remainingMinutes)) return null;
  return { reasonCode: "", slotLabel, remainingMinutes };
}

function Kiosk({
  state,
  stateReceivedAt,
  token,
  refresh,
  error,
}: {
  state: DeviceState;
  stateReceivedAt: number;
  token: string;
  refresh: () => Promise<void>;
  error: string;
}) {
  const deviceScaleStyle = useDeviceViewportScale(state.device.scalePercent);
  const [studentNumber, setStudentNumber] = useState("");
  const [message, setMessage] = useState("");
  const [messageVersion, setMessageVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [roster, setRoster] = useState<Array<{ token: string; first_name: string; last_name: string }>>([]);
  const [rosterPeriods, setRosterPeriods] = useState<Array<{ id: string; periodLabel: string; name: string }>>([]);
  const [rosterPeriodId, setRosterPeriodId] = useState("");
  const [rosterLoading, setRosterLoading] = useState(false);
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const [selectedStudentToken, setSelectedStudentToken] = useState("");
  const [limitConfirmation, setLimitConfirmation] = useState<KioskLimitConfirmation | null>(null);
  const [scheduleConfirmation, setScheduleConfirmation] = useState<KioskScheduleConfirmation | null>(null);
  const [scheduleEmergencyApproved, setScheduleEmergencyApproved] = useState(false);
  const studentInputRef = useRef<HTMLInputElement>(null);
  const now = useClock();
  const active = state.passes.filter((pass) => pass.status === "ACTIVE");
  const queue = state.passes.filter((pass) => ["WAITLISTED", "READY"].includes(pass.status));
  const capacityChips = kioskCapacityChips(state.passes, state.class.max_restroom_out);
  const queuePositions = new Map<string, number>();
  const periodQueueCounts = new Map<string, number>();
  for (const pass of queue) {
    const periodKey = pass.class_period_id ?? "UNASSIGNED";
    const position = (periodQueueCounts.get(periodKey) ?? 0) + 1;
    periodQueueCounts.set(periodKey, position);
    queuePositions.set(pass.id, position);
  }
  const primaryCategories = state.categories.filter((category) => category.kiosk_group === "PRIMARY");
  const otherCategories = state.categories.filter((category) => category.kiosk_group === "MORE");

  const showMessage = useCallback((nextMessage: string) => {
    setMessage(nextMessage);
    // A repeated message still gets a fresh full display interval.
    setMessageVersion((version) => version + 1);
  }, []);

  const focusStudentInput = useCallback(() => {
    // Wait until the clicked/touched control has completed its own action.
    // The modal guard prevents focus from being moved behind an open dialog.
    window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        if (document.querySelector(".kiosk-other-overlay, .kiosk-roster-overlay, .kiosk-confirmation-backdrop")) return;
        const input = studentInputRef.current;
        if (input && document.activeElement !== input) input.focus({ preventScroll: true });
      });
    }, 0);
  }, []);

  useEffect(() => {
    focusStudentInput();
  }, [focusStudentInput]);

  useEffect(() => {
    // A request briefly disables the field so a rapid second scan cannot be
    // erased by completion of the first request. Focus returns as soon as it
    // is safe to accept the next barcode.
    if (!busy) focusStudentInput();
  }, [busy, focusStudentInput]);

  useEffect(() => {
    if (!message) return;
    const timeout = window.setTimeout(() => setMessage(""), 8_000);
    return () => window.clearTimeout(timeout);
  }, [message, messageVersion]);

  useEffect(() => {
    // Barcode scanners behave like keyboards. Keeping this field focused
    // after touch, click, and scroll interactions means the next barcode can
    // be scanned immediately without a staff member tapping the field again.
    const restoreFocus = () => focusStudentInput();
    document.addEventListener("click", restoreFocus);
    document.addEventListener("pointerup", restoreFocus);
    window.addEventListener("scroll", restoreFocus, true);
    return () => {
      document.removeEventListener("click", restoreFocus);
      document.removeEventListener("pointerup", restoreFocus);
      window.removeEventListener("scroll", restoreFocus, true);
    };
  }, [focusStudentInput]);

  async function createPass(reasonCode: string, confirmOverLimit = false, confirmScheduleEmergency = false) {
    if (!studentNumber.trim() && !selectedStudentToken) return showMessage("Enter or scan a student ID first.");
    setBusy(true);
    try {
      const result = await deviceApi<{ status: string; position: number | null; separationBlocked?: boolean; category: { code: string; label: string }; student: { firstName: string; lastInitial: string } }>(
        token,
        "/api/device/passes",
        { method: "POST", body: JSON.stringify({ studentNumber, studentToken: selectedStudentToken || undefined, reasonCode, confirmOverLimit, confirmScheduleEmergency: confirmScheduleEmergency || scheduleEmergencyApproved }) },
      );
      showMessage(
        result.status === "WAITLISTED"
          ? result.separationBlocked
            ? "This pass is unavailable right now. You can wait for availability or request an emergency pass from the waitlist."
            : `${result.student.firstName} is #${result.position} on the restroom waitlist.`
          : result.status === "TARDY_LOGGED"
            ? `Tardy recorded for ${result.student.firstName}.`
            : result.status === "PENDING_APPROVAL"
              ? `${result.student.firstName}'s ${result.category.label.toLowerCase()} request is waiting for teacher approval.`
              : `${result.student.firstName} is cleared for ${result.category.label.toLowerCase()}.`,
      );
      setStudentNumber("");
      setSelectedStudentName("");
      setSelectedStudentToken("");
      setLimitConfirmation(null);
      setScheduleConfirmation(null);
      setScheduleEmergencyApproved(false);
      await refresh();
    } catch (caught) {
      const confirmation = kioskLimitConfirmation(caught);
      const schedulePrompt = kioskScheduleConfirmation(caught);
      if (schedulePrompt) {
        setScheduleConfirmation({ ...schedulePrompt, reasonCode });
        setLimitConfirmation(null);
        setMessage("");
      } else if (confirmation) {
        setLimitConfirmation({ ...confirmation, reasonCode });
        setScheduleConfirmation(null);
        setMessage("");
      } else {
        showMessage(caught instanceof Error ? caught.message : "Pass request failed");
      }
    }
    finally { setBusy(false); }
  }

  async function loadRoster(classPeriodId = "") {
    setRosterLoading(true);
    try {
      const suffix = classPeriodId ? `?classPeriodId=${encodeURIComponent(classPeriodId)}` : "";
      const result = await deviceApi<{ students: typeof roster; periods: typeof rosterPeriods }>(token, `/api/device/roster${suffix}`);
      setRoster(result.students); setRosterPeriods(result.periods);
    }
    catch (caught) { showMessage(caught instanceof Error ? caught.message : "Roster could not be loaded"); setShowRoster(false); }
    finally { setRosterLoading(false); }
  }

  async function openRoster() {
    setShowRoster(true); setRosterPeriodId("");
    await loadRoster();
  }

  async function action(passId: string, actionName: "return" | "leave" | "cancel" | "emergency") {
    try {
      await deviceApi(token, `/api/device/passes/${passId}/${actionName}`, { method: "POST" });
      if (actionName === "emergency") {
        showMessage("Emergency pass request sent to the teacher.");
      }
      await refresh();
    } catch (caught) { showMessage(caught instanceof Error ? caught.message : "Action failed"); }
  }

  return (
    <main className="device-app kiosk-app device-scaled" style={deviceScaleStyle}>
      <header className="device-header">
        <div className="brand-lockup">
          <span className="brand-mark">V</span>
          <span className="kiosk-brand-copy"><strong>Virtual Hall Pass</strong><small>{state.device.name}</small></span>
        </div>
        <div><strong>{state.teacher.heading}</strong><span>{state.scheduleHeading?.heading ?? "All day"}</span></div>
        <div className="capacity-chips" aria-label="Restroom availability by period">
          {capacityChips.length ? capacityChips.map((chip) => (
            <div className="capacity-chip" key={chip.periodId}>
              <strong>{chip.periodLabel}:</strong> {chip.available} {chip.available === 1 ? "pass" : "passes"} available
            </div>
          )) : <div className="capacity-chip available">Restroom is available</div>}
        </div>
      </header>
      {error && <div className="alert error">{error}</div>}
      <section className="kiosk-request">
        <div className="kiosk-intro"><p className="eyebrow ink">Student pass</p><p>Enter your ID<br />Choose destination</p></div>
        <div className="student-id-field">
          <input ref={studentInputRef} className="student-id-input" inputMode={selectedStudentName ? "text" : "numeric"} placeholder="Student ID" value={selectedStudentName || studentNumber} onChange={(event) => { setSelectedStudentName(""); setSelectedStudentToken(""); setStudentNumber(event.target.value); }} disabled={busy} autoFocus />
          {studentNumber || selectedStudentToken ? <button type="button" className="student-id-clear" aria-label="Clear selected student" disabled={busy} onClick={() => { setStudentNumber(""); setSelectedStudentName(""); setSelectedStudentToken(""); focusStudentInput(); }}>×</button> : state.rosterNamePickerEnabled && <button type="button" className="student-id-clear student-id-picker" aria-label="Find your name" title="Find your name" disabled={busy} onClick={() => void openRoster()}>👤</button>}
        </div>
        <div className="reason-grid">
          {primaryCategories.map((category) => (
            <button
              key={category.id}
              disabled={busy}
              className={cx(
                category.code === "WATER" && "water",
                category.capacity_group === "RESTROOM" && category.code !== "WATER" && "featured",
                Boolean(category.is_tardy) && "tardy",
              )}
              style={{ backgroundColor: category.button_color, color: category.text_color }}
              onClick={() => void createPass(category.code)}
            ><span className={reasonIcon(category.code, category.symbol) === "✚" ? "nurse-icon" : undefined}>{reasonIcon(category.code, category.symbol) ?? "•"}</span>{category.label}</button>
          ))}
          <button disabled={busy} className="other-toggle" aria-expanded={showOther} onClick={() => setShowOther(true)}><span>＋</span>Other</button>
        </div>
        {message && <div className="kiosk-message">{message}</div>}
      </section>
      {showOther && (
        <div className="kiosk-other-overlay" role="presentation" onClick={() => setShowOther(false)}>
          <section className="kiosk-other-menu" role="dialog" aria-modal="true" aria-labelledby="other-destinations-heading" onClick={(event) => event.stopPropagation()}>
            <header><h2 id="other-destinations-heading">Other destinations</h2><button type="button" className="button quiet small" onClick={() => setShowOther(false)}>Close</button></header>
            <div className="reason-grid secondary-reasons">
              {otherCategories.map((category, index) => (
                <style key={`${category.id}-colors`}>{`.secondary-reasons > button:nth-of-type(${index + 1}) { background-color: ${category.button_color}; color: ${category.text_color}; }`}</style>
              ))}
              {otherCategories.map((category) => (
                <button key={category.id} disabled={busy} onClick={() => { setShowOther(false); void createPass(category.code); }}><span className={reasonIcon(category.code, category.symbol) === "✚" ? "nurse-icon" : undefined}>{reasonIcon(category.code, category.symbol) ?? "•"}</span>{category.label}</button>
              ))}
            </div>
          </section>
        </div>
      )}
      {showRoster && <div className="kiosk-roster-overlay" role="presentation" onClick={() => setShowRoster(false)}>
        <section className="kiosk-roster-menu" role="dialog" aria-modal="true" aria-labelledby="roster-heading" onClick={(event) => event.stopPropagation()}>
          <header><h2 id="roster-heading">Find your name</h2><button type="button" className="button quiet small" onClick={() => setShowRoster(false)}>Close</button></header>
          {rosterPeriods.length > 0 && <label className="kiosk-roster-period">Period
            <select value={rosterPeriodId} onChange={(event) => { const nextPeriodId = event.target.value; setRosterPeriodId(nextPeriodId); void loadRoster(nextPeriodId); }}>
              <option value="">All periods</option>
              {rosterPeriods.map((period) => <option key={period.id} value={period.id}>{period.periodLabel}{period.name ? ` — ${period.name}` : ""}</option>)}
            </select>
          </label>}
          {rosterLoading ? <MiniEmpty text="Loading roster…" /> : <div className="kiosk-roster-grid">{roster.map((student) => <button key={student.token} type="button" onClick={() => { setStudentNumber(""); setSelectedStudentToken(student.token); setSelectedStudentName(`${student.first_name} ${student.last_name.slice(0, 1)}.`); setShowRoster(false); }}>{student.first_name} {student.last_name.slice(0, 1)}.</button>)}</div>}
          {!rosterLoading && !roster.length && <MiniEmpty text="No students are available for this period." />}
        </section>
      </div>}
      {limitConfirmation && (
        <div className="dialog-backdrop kiosk-confirmation-backdrop" role="presentation">
          <section className="dialog kiosk-limit-confirmation" role="dialog" aria-modal="true" aria-labelledby="pass-limit-heading">
            <p className="eyebrow ink">Pass allowance reached</p>
            <h2 id="pass-limit-heading">Do you still need to go?</h2>
            <p>
              You have already used {limitConfirmation.passCount} {limitConfirmation.categoryLabel.toLowerCase()} pass{limitConfirmation.passCount === 1 ? "" : "es"}.
              Your teacher&apos;s limit is {limitConfirmation.passLimit}.
            </p>
            <p className="form-note">Continuing will still request a pass or add you to the waitlist.</p>
            <div className="dialog-actions">
              <button type="button" className="button quiet" disabled={busy} onClick={() => setLimitConfirmation(null)}>Go back</button>
              <button type="button" className="button primary" disabled={busy} onClick={() => void createPass(limitConfirmation.reasonCode, true)}>{busy ? "Submitting…" : "Yes, continue"}</button>
            </div>
          </section>
        </div>
      )}
      {scheduleConfirmation && (
        <div className="dialog-backdrop kiosk-confirmation-backdrop" role="presentation">
          <section className="dialog kiosk-limit-confirmation" role="dialog" aria-modal="true" aria-labelledby="schedule-end-heading">
            <p className="eyebrow ink">Bell is coming up</p>
            <h2 id="schedule-end-heading">Only {scheduleConfirmation.remainingMinutes} minute{scheduleConfirmation.remainingMinutes === 1 ? "" : "s"} remain in {scheduleConfirmation.slotLabel}.</h2>
            <p>It is close to the bell. If this is an emergency, you may still leave now.</p>
            <p className="form-note">Continuing is recorded as an emergency restroom pass and may bypass the waitlist.</p>
            <div className="dialog-actions">
              <button type="button" className="button quiet" disabled={busy} onClick={() => { setScheduleConfirmation(null); setScheduleEmergencyApproved(false); }}>Cancel request</button>
              <button type="button" className="button primary" disabled={busy} onClick={() => { setScheduleEmergencyApproved(true); void createPass(scheduleConfirmation.reasonCode, false, true); }}>{busy ? "Submitting…" : "Emergency pass"}</button>
            </div>
          </section>
        </div>
      )}
      <section className="kiosk-lists">
        <div className="device-panel">
          <header><div><span className="status-dot" /><h2>Currently out</h2></div><strong>{active.length}</strong></header>
          <div>
            {active.map((pass) => {
              const tone = restroomTimerTone(
                pass,
                now,
                pass.restroom_warning_minutes ?? state.class.warning_minutes,
                pass.restroom_alert_limit_minutes ?? state.class.pass_limit_minutes,
                stateReceivedAt,
              );
              const presentation = timerPresentation(tone, passTimerRules(pass, state.class));
              return (
                <article key={pass.id} className="kiosk-active-pass">
                  <div className="kiosk-active-student">
                    <strong className="kiosk-active-name">{pass.first_name} {pass.last_initial}.</strong>
                    <small className="kiosk-active-period">{pass.period_label ?? "Unassigned period"}</small>
                  </div>
                  <span className="kiosk-active-reason" title={`Reason code: ${pass.reason_code}`}>{state.maskOtherDestinations && state.categories.find((category) => category.code === pass.reason_code)?.kiosk_group === "MORE" ? `${state.otherDestinationSymbol} ${state.otherDestinationLabel}` : categoryLabel(pass.reason_code, state.categories, pass.reason_label)}</span>
                  <span
                    className={cx("device-timer", tone, presentation.className)}
                    style={presentation.style}
                    data-elapsed-start={pass.approved_at ?? ""}
                    title="Elapsed time out of the room"
                  >
                    {elapsed(pass.approved_at, now, pass.elapsed_seconds, stateReceivedAt)}
                  </span>
                  <button onClick={() => void action(pass.id, "return")}>I’m back</button>
                </article>
              );
            })}
            {!active.length && <MiniEmpty text="Everyone is in the room." />}
          </div>
        </div>
        <div className="device-panel">
          <header><div><span className="status-dot warning" /><h2>Pass waitlist</h2></div><strong>{queue.length}</strong></header>
          <div>
            {queue.map((pass) => (
              <article key={pass.id} className={pass.status === "READY" ? "ready" : ""}>
                <span className="queue-position">{queuePositions.get(pass.id)}</span>
                <div className="kiosk-wait-student"><strong>{pass.first_name} {pass.last_initial}.</strong><small className="kiosk-wait-period">{pass.period_label ?? "Unassigned period"}{pass.status !== "READY" && pass.schedule_hold_until && new Date(pass.schedule_hold_until).getTime() > now ? ` · Held until ${formatDateAtTimeZone(pass.schedule_hold_until, state.class.timezone)}` : ""}</small></div>
                <div className="queue-actions">
                  {pass.status === "READY"
                    ? <button className="go-button" onClick={() => void action(pass.id, "leave")}>Leave room</button>
                    : <button className="emergency-button" onClick={() => void action(pass.id, "emergency")}>{Boolean(pass.separation_waiting) ? "Request emergency pass" : "Emergency pass"}</button>}
                  <button className="cancel-button" onClick={() => void action(pass.id, "cancel")}>Cancel</button>
                </div>
              </article>
            ))}
            {!queue.length && <MiniEmpty text="No one is waiting." />}
          </div>
        </div>
      </section>
    </main>
  );
}

function reasonIcon(code: string, symbol?: string | null) {
  if (symbol?.trim()) return symbol;
  return {
    RESTROOM: "\u{1F6BD}",
    WATER: "\u{1F4A7}",
    RESTROOM_WATER: "◒",
    FRONT_OFFICE: "\u{1F3E2}",
    COUNSELOR: "\u{1F4AC}",
    NURSE: "✚",
    LIBRARY: "\u{1F4DA}",
    OTHER_CLASSROOM: "\u{1F3EB}",
    TARDY: "\u{23F1}\u{FE0F}",
  }[code];
}

function DisplayBoard({
  state,
  stateReceivedAt,
  error,
}: {
  state: DeviceState;
  stateReceivedAt: number;
  error: string;
}) {
  const deviceScaleStyle = useDeviceViewportScale(state.device.scalePercent);
  const now = useClock();
  const active = state.passes.filter((pass) => pass.status === "ACTIVE");
  const queue = state.passes.filter((pass) => ["WAITLISTED", "READY"].includes(pass.status));
  const capacityChips = kioskCapacityChips(state.passes, state.class.max_restroom_out);
  const queuePositions = new Map<string, number>();
  const periodQueueCounts = new Map<string, number>();
  for (const pass of queue) {
    const periodKey = pass.class_period_id ?? "UNASSIGNED";
    const position = (periodQueueCounts.get(periodKey) ?? 0) + 1;
    periodQueueCounts.set(periodKey, position);
    queuePositions.set(pass.id, position);
  }
  return (
    <main className="display-board device-scaled" style={deviceScaleStyle}>
      <header>
        <div className="brand-lockup light"><span className="brand-mark">V</span><span>Virtual Hall Pass</span></div>
        <div><p>{state.scheduleHeading?.heading ?? "All day"}</p><h1>{state.teacher.heading}</h1></div>
        <time>{formatTimeAtTimeZone(now, state.class.timezone)}</time>
      </header>
      {error && <div className="alert error">{error}</div>}
      <div className="display-columns">
        <section>
          <div className="display-title"><div><span className="status-dot" /><h2>Currently out</h2></div><strong>{active.length}</strong></div>
          <div className="display-list">
            {active.map((pass) => {
              const tone = restroomTimerTone(
                pass,
                now,
                pass.restroom_warning_minutes ?? state.class.warning_minutes,
                pass.restroom_alert_limit_minutes ?? state.class.pass_limit_minutes,
                stateReceivedAt,
              );
              const presentation = timerPresentation(tone, passTimerRules(pass, state.class));
              return (
                <article key={pass.id} className={cx("display-active-pass", tone === "limit" && "overdue")}>
                  <div className="display-card-student">
                    <strong className="display-card-name">{pass.first_name} {pass.last_initial}.</strong>
                    <small className="display-card-period">{pass.period_label ?? "Unassigned period"}</small>
                  </div>
                  <span className="display-card-reason" title={`Reason code: ${pass.reason_code}`}>{categoryLabel(pass.reason_code, state.categories, pass.reason_label)}</span>
                  <time className={cx(tone, presentation.className)} style={presentation.style} data-elapsed-start={pass.approved_at ?? ""} title="Elapsed time out of the room">
                    {elapsed(pass.approved_at, now, pass.elapsed_seconds, stateReceivedAt)}
                  </time>
                </article>
              );
            })}
            {!active.length && <MiniEmpty text="Everyone is in the room." />}
          </div>
        </section>
        <section>
          <div className="display-title"><div><span className="status-dot warning" /><h2>Restroom waitlist</h2></div><strong>{queue.length}</strong></div>
          <div className="display-list">
            {queue.map((pass) => (
              <article key={pass.id} className={cx("display-waiting-pass", pass.status === "READY" && "ready")}>
                <span className="queue-position">{queuePositions.get(pass.id)}</span>
                <div className="display-card-student"><strong>{pass.first_name} {pass.last_initial}.</strong><small className="display-card-period">{pass.period_label ?? "Unassigned period"}</small></div>
              </article>
            ))}
            {!queue.length && <MiniEmpty text="No one is waiting." />}
          </div>
        </section>
      </div>
      <footer>
        <div className="display-capacity-chips" aria-label="Restroom availability by period">
          {capacityChips.length ? capacityChips.map((chip) => (
            <div className="capacity-chip" key={chip.periodId}>
              <strong>{chip.periodLabel}:</strong> {chip.available} {chip.available === 1 ? "pass" : "passes"} available
            </div>
          )) : <div className="capacity-chip available">Restroom is available</div>}
        </div>
      </footer>
    </main>
  );
}

export default App;
